import {
  AnyID,
  Getter,
  Item,
  ItemGroup,
  ItemWithOptions,
  LinkedItem,
  Morphs,
  Partition,
  SceneObjects,
  Setter,
  Textures,
  Walls,
  Zone,
} from '@types'
import { calculateTiradorPosition } from '@utils/calculateTiradorPosition'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { validateHex } from '@utils/conversion/validateHex'
import { degrees, radians } from '@utils/generalMaths/geometry'
import { API } from '@utils/queryBuilder'
import isArea from '@utils/sceneObjects/assertions/isArea'
import isHiddenWall from '@utils/sceneObjects/assertions/isHiddenWall'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { computeItemGroupOBB } from '@utils/sceneObjects/computeItemGroupOBB'
import {
  getExistingTexturesFromScene,
  mapExistingTexturesToNewItemByDisplayName,
  syncZonesWithMappedTextures,
} from '@utils/sceneObjects/getExistingTexturesFromScene'
import getId from '@utils/sceneObjects/getId'
import { addItemOffset } from '@utils/sceneObjects/items/addItemOffset'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import {
  fetchUnidadByCatalog,
  isGolaEffective,
  isSelfReferencingGolaUnit,
  resolveDrawGolaInheritanceTarget,
  shouldHideTiradoresForParent,
  shouldInheritGolaModoOnDraw,
} from '@context/utils/linked/linkedUnitVariants'
import { syncLinkedItemZonesFromTextures } from '@context/utils/linked/syncLinkedItemZonesFromTextures'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { depthVisualOrderSorter } from '@utils/sceneObjects/linked/depthVisualOrderSorter'
import fetchLinkedChildrenInfo from '@utils/sceneObjects/linked/fetchLinkedChildrenInfo'
import {
  applyEncimeraThicknessMm,
  isEncimeraLinkedSonItem,
  isRbFreeDimensionRegleta,
  logFreeDimensionEncimeraFondo,
  logFreeDimensionFondo,
  logFreeDimensionPuertaMaterial,
  logFreeDimensionZocaloMaterial,
  matchesRbRegletaPreferredEncimera,
  patchEncimeraLinkedTextureIdOnItem,
  placeLinkedEncimeraY,
  RB_REGLETA_ENCIMERA_CODIGO,
  resolveRbRegletaDefaultLinkedChildren,
  syncFreeDimensionEncimeraAnchoToParent,
  syncFreeDimensionEncimeraFondoToParent,
  usesFreeDimensionScaling,
} from '@utils/sceneObjects/linked/freeDimensionLinkedCatalogs'
import { purgeSceneGlbForCatalog } from '@utils/sceneObjects/purgeSceneGlbForCatalog'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import {
  buildCatalogIDWithSuffix,
  getBaseCatalogID,
  mapUnitsWithSuffixes,
  updateGlbPathWithSuffix,
  updateReferenciaWithSuffix,
} from '@utils/sceneObjects/suffixUtils'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { radToDeg } from 'three/src/math/MathUtils'
import { getElementosEnlazadosUnidadAcabados } from '../proxies/getElementosEnlazados'
import { getEncimerasFrontales } from '../proxies/getEncimerasFrontales'
import getLinkedChildrenInfo from './getLinkedChildrenInfo'

export type ItemActions = ReturnType<typeof createItemActions>
export const dictionary = {
  MAL: 'highItems' as const,
  MBJ: 'lowItems' as const,
}

export const createItemActions = (set: Setter, get: Getter) => ({
  drawItem: (
    id: string,
    catalogID: string,
    icono: string,
    visible: boolean, // Solo para puertas es false
    matrix: Matrix4 | null,
    type = 'items' as SceneObjects['type'],
    realCatalogID?: string,
    shouldGoToBackHeight = false,
    skipLinkedSons = false, // Cuando es true, NO crea linkedSons automáticamente
  ): Promise<AnyID> => {
    return new Promise((resolve, reject) => {
      ;(async () => {
        try {
          const glbInScene = get().sceneGLB as Map<string | number, { glb: any; backGLBInfo: any }>
          let backGLBInfo, glb

          const store = get()
          const currentSourceID = store.currentObjectID
          // El increment se asignará después con renumberItemsHierarchically

          if (glbInScene.has(catalogID)) {
            const { backGLBInfo: back, glb: _glb } = glbInScene.get(catalogID) as any
            glb = _glb
            backGLBInfo = back
          } else {
            backGLBInfo = await getGLB(glbInScene, catalogID, realCatalogID)
            if (!backGLBInfo) {
              // No mostrar error para laterales que no existen, es un caso esperado
              const isLateral =
                typeof catalogID === 'string' && /^(LA|LB|LC|CC|CB)\d+/.test(catalogID)
              if (isLateral) {
                console.warn(
                  `⚠️ Lateral ${catalogID} no existe en el catálogo. Se omite su creación.`,
                )
              } else {
                console.error(`❌ No se pudo obtener GLB data para catalogID: ${catalogID}`)
              }
              reject(
                new Error(
                  `No se pudo cargar el elemento con catalogID: ${catalogID}. El elemento no existe en el catálogo.`,
                ),
              )
              return
            }
            const referenciaBlender = (backGLBInfo as any)?.referenciaBlender
            if (!referenciaBlender) {
              console.error(`❌ No se encontró referenciaBlender para catalogID: ${catalogID}`)
              reject(
                new Error(
                  `No se encontró la referencia del modelo 3D para catalogID: ${catalogID}`,
                ),
              )
              return
            }
            const gltfLoader = await setGLTFLoader()
            const _glb = await gltfLoader.loadAsync(referenciaBlender)
            glb = _glb
          }

          const extraInfo: { zonas: { idAcabadoUnidad: number; zona: string }[] } = { zonas: [] }
          if (!backGLBInfo.universal) {
            const concha = await API().api.acabadosGetUnidadAcabadosInfo(
              (backGLBInfo as any).idUnidades,
            )
            if (concha && Array.isArray(concha.data?.zonas)) {
              extraInfo.zonas = concha.data.zonas as { idAcabadoUnidad: number; zona: string }[]
            }
          }

          const item = newGetDataFromGLB({
            glbData: glb,
            catalogID,
            id: !backGLBInfo.universal ? getId('linkedItem') : id,
            type: !backGLBInfo.universal ? 'linkedItem' : 'items',
            icono,
            visible,
            backGLBInfo,
            paginaPdf: backGLBInfo.paginaPdf,
            matrix,
            extraInfo,
            shouldGoToBackHeight,
            canCollide: backGLBInfo.colision ?? !backGLBInfo.universal,
          }) as any
          // if (!item.universal) {
          //   item.depth = 0
          //   item.type = 'linkedItem'
          //   item.id = getId('linkedItem')
          //   item.childrenOrder = []
          // }

          if (!item.universal) {
            item.depth = 0
            // Respetar el tipo si se especifica 'linkedSon'
            item.type = type === 'linkedSon' ? 'linkedSon' : 'linkedItem'
            item.id = type === 'linkedSon' ? id : getId('linkedItem')
            item.childrenOrder = []
          }

          // El increment se asignará después con renumberItemsHierarchically
          item.increment = '0'

          const toolbarAcabadoBlancoPreference = store.toolbarAcabadoBlanco
          const sliderForcesPetroleo = toolbarAcabadoBlancoPreference === false
          const sliderForcesBlanco = toolbarAcabadoBlancoPreference === true

          if (!item.universal && item.textures && item.textures.length > 0) {
            const isVitrinaCatalog = catalogID.toUpperCase().startsWith('KABV')
            const hasVitrinaModel = item.textures.some(
              (texture: any) => texture.modelo === 'Vitrina',
            )

            const shouldInheritTextures =
              (!isVitrinaCatalog && !hasVitrinaModel) || (isVitrinaCatalog && hasVitrinaModel)

            if (shouldInheritTextures) {
              const existingTextures = getExistingTexturesFromScene(
                store.items,
                catalogID,
                currentSourceID,
              )

              if (existingTextures && existingTextures.length > 0) {
                const texturesWithoutArmazon = existingTextures.filter((texture) => {
                  const zoneDisplayName = String(texture.zoneDisplayName || '').toLowerCase()
                  return !zoneDisplayName.includes('armazon')
                })

                item.textures = mapExistingTexturesToNewItemByDisplayName(
                  texturesWithoutArmazon,
                  item.textures,
                )

                const syncResult = syncZonesWithMappedTextures(
                  item.zones,
                  item.textures,
                  store.items,
                  item.type,
                  item.description,
                )

                item.zones = syncResult.zones
              }
            }
          }

          if (!item.universal && isLinkedItem(item)) {
            if (sliderForcesBlanco) {
              item.ArmazonBlanco = true
            } else if (sliderForcesPetroleo) {
              item.ArmazonBlanco = false
            }

            const sourceItemForGola = currentSourceID ? store.items.get(currentSourceID) : null
            if (
              shouldInheritGolaModoOnDraw(
                item as LinkedItem,
                sourceItemForGola && isLinkedItem(sourceItemForGola)
                  ? (sourceItemForGola as LinkedItem)
                  : null,
                store.toolbarGolaActiva,
              )
            ) {
              ;(item as LinkedItem).golaActiva = true
            }
          }

          // Crear linkedSons solo si NO se solicita omitirlos
          if (
            !skipLinkedSons &&
            (backGLBInfo.elementosEnlazados || backGLBInfo.elementosEnlazadosAcabadosPorDefecto)
          ) {
            let data =
              backGLBInfo.elementosEnlazados ?? backGLBInfo.elementosEnlazadosAcabadosPorDefecto
            if (isRbFreeDimensionRegleta(backGLBInfo)) {
              data = resolveRbRegletaDefaultLinkedChildren(data)
              if (!data.some((el) => matchesRbRegletaPreferredEncimera(el))) {
                const apiEnc = (
                  backGLBInfo.elementosEnlazados ??
                  backGLBInfo.elementosEnlazadosAcabadosPorDefecto ??
                  []
                ).find((el: { descripcion?: string }) => /encimera/i.test(el.descripcion ?? ''))
                try {
                  const encRes = await API().api.unidadesGetUnidadPost({
                    codigo: RB_REGLETA_ENCIMERA_CODIGO,
                    date: '2023-05-12',
                    catalogoId: RB_REGLETA_ENCIMERA_CODIGO,
                  })
                  const unidad = encRes?.data
                  if (unidad) {
                    data = [
                      ...data.filter(
                        (el: { descripcion?: string }) => !/encimera/i.test(el.descripcion ?? ''),
                      ),
                      {
                        descripcion: unidad.descripcion ?? RB_REGLETA_ENCIMERA_CODIGO,
                        unidad,
                        posicionY: apiEnc?.posicionY ?? null,
                        posicionX: apiEnc?.posicionX ?? 0,
                        posicionZ: apiEnc?.posicionZ ?? 0,
                        nodoPadre: apiEnc?.nodoPadre ?? 0,
                        nodo: apiEnc?.nodo ?? 1,
                      },
                    ]
                  }
                } catch (e) {
                  console.warn(
                    '[RB regleta] No se pudo cargar encimera por defecto',
                    RB_REGLETA_ENCIMERA_CODIGO,
                    e,
                  )
                }
              }
            }
            const loader = await setGLTFLoader()
            const info = await fetchLinkedChildrenInfo(backGLBInfo.idUnidades)

            const orderedOptions: string[] = []
            const nestingLevelArray = [data]
            const optionsObject: Record<string, string> = {}
            const orderMap = new Map()

            const options = await API().api.acabadosGetElementosEnlazadosUnidadAcabados(
              backGLBInfo.idUnidades,
            )
            for (const [parentNode, childNodeObj] of Object.entries(options.data)) {
              for (const [childNode, info] of Object.entries(childNodeObj)) {
                info.forEach((el) => {
                  orderMap.set(el.descripcion, {
                    parentNode: Number(parentNode),
                    option: Number(childNode),
                    visualOrder: el.ordenVisual,
                  })
                })
              }
            }

            // Procesar cada nivel en paralelo, pero los niveles secuencialmente
            for (let i = 0; i < nestingLevelArray.length; i++) {
              // Recopilar todas las promesas para este nivel
              const levelPromises = nestingLevelArray[i].map(async (rawBackData: any) => {
                const linkedID = getId('linkedSon')
                const idElementosEnlazados = rawBackData.idElementosEnlazados
                const itemInfo = getLinkedChildrenInfo(rawBackData, info, idElementosEnlazados)
                const backData = rawBackData.unidad ?? rawBackData[0]

                if (rawBackData.childs?.length) {
                  if (!Array.isArray(nestingLevelArray[i + 1])) {
                    nestingLevelArray[i + 1] = []
                  }
                  nestingLevelArray[i + 1].push(...rawBackData.childs)
                }

                if (!glb || !catalogID) {
                  return null
                }

                // Crear promesas para carga de GLB y API en paralelo
                let glbPromise: Promise<any>
                if (glbInScene.has(backData?.codigo)) {
                  const cached = glbInScene.get(backData?.codigo)
                  if (cached) {
                    glbPromise = Promise.resolve(cached.glb)
                  } else {
                    glbPromise = loader.loadAsync(backData?.referenciaBlender)
                  }
                } else {
                  glbPromise = loader.loadAsync(backData?.referenciaBlender)
                }

                const extraInfoPromise = API().api.acabadosGetUnidadAcabadosInfo(
                  rawBackData.unidad.idUnidades,
                )

                // Ejecutar ambas promesas en paralelo
                const [sonGlbData, extraInfo] = await Promise.all([glbPromise, extraInfoPromise])

                return {
                  linkedID,
                  rawBackData,
                  idElementosEnlazados,
                  itemInfo,
                  backData,
                  depth: i + 1,
                  sonGlbData,
                  extraInfo,
                }
              })

              // Esperar a que todas las promesas de este nivel se completen en paralelo
              const levelResults = await Promise.allSettled(levelPromises)

              // Procesar los resultados de este nivel
              for (const result of levelResults) {
                if (result.status === 'rejected') {
                  console.error('Error procesando hijo:', result.reason)
                  continue
                }

                const childData = result.value
                if (!childData) continue

                const {
                  linkedID,
                  rawBackData,
                  idElementosEnlazados,
                  itemInfo,
                  backData,
                  depth,
                  sonGlbData,
                  extraInfo,
                } = childData

                const linkedSon = newGetDataFromGLB({
                  id: linkedID,
                  glbData: sonGlbData,
                  type: 'linkedSon',
                  catalogID: backData?.codigo,
                  visible: true,
                  backGLBInfo: backData,
                  depth: depth,
                  firstParentID: item.id,
                  itemInfo,
                  extraInfo: {
                    zonas:
                      extraInfo.data?.zonas?.map((z: any) => ({
                        idAcabadoUnidad: z.idAcabadoUnidad,
                        zona: z.zona ?? '',
                      })) ?? [],
                  },
                }) as any

                if (linkedSon.description === 'Tirador') {
                  linkedSon.idElementosEnlazados = idElementosEnlazados
                }

                // El increment se asignará después con renumberItemsHierarchically
                linkedSon.increment = '0'

                if (isEncimeraLinkedSonItem(linkedSon)) {
                  patchEncimeraLinkedTextureIdOnItem(linkedSon, extraInfo?.zonas)
                }

                if (linkedSon.textures && linkedSon.textures.length > 0) {
                  const childCatalogID = linkedSon.catalogID?.toString().toUpperCase() ?? ''
                  const isChildVitrinaCatalog = childCatalogID.startsWith('KABV')
                  const childHasVitrinaModel = linkedSon.textures.some(
                    (texture: any) => texture.modelo === 'Vitrina',
                  )

                  const shouldInheritTextures =
                    (!isChildVitrinaCatalog && !childHasVitrinaModel) ||
                    (isChildVitrinaCatalog && childHasVitrinaModel)

                  if (shouldInheritTextures) {
                    const existingTextures = getExistingTexturesFromScene(
                      store.items,
                      linkedSon.catalogID,
                      currentSourceID,
                    )

                    if (existingTextures) {
                      linkedSon.textures = mapExistingTexturesToNewItemByDisplayName(
                        existingTextures,
                        linkedSon.textures,
                      )

                      const syncResult = syncZonesWithMappedTextures(
                        linkedSon.zones,
                        linkedSon.textures,
                        store.items,
                        linkedSon.type,
                        linkedSon.description,
                      )
                      linkedSon.zones = syncResult.zones

                      if (isEncimeraLinkedSonItem(linkedSon)) {
                        applyEncimeraThicknessMm(linkedSon)
                        if (usesFreeDimensionScaling(item)) {
                          const apiY = rawBackData.posicionY
                          placeLinkedEncimeraY(linkedSon, item, {
                            backendPosicionY: typeof apiY === 'number' ? apiY : undefined,
                            backendPosicionZ:
                              typeof rawBackData.posicionZ === 'number' ? rawBackData.posicionZ : 0,
                          })
                          syncFreeDimensionEncimeraAnchoToParent(linkedSon, item)
                          syncFreeDimensionEncimeraFondoToParent(linkedSon, item)
                          logFreeDimensionEncimeraFondo('drawItem.inheritTextures', linkedSon, item)
                        }

                        if (
                          syncResult.inheritedTexture &&
                          linkedSon.textures &&
                          linkedSon.textures.length > 0
                        ) {
                          const encTex = linkedSon.textures.find((t: any) => t.zone === 'Encimera')
                          if (encTex) {
                            encTex.name = syncResult.inheritedTexture.name
                            encTex.url = syncResult.inheritedTexture.url
                            encTex.description = syncResult.inheritedTexture.description
                            encTex.acabado = syncResult.inheritedTexture.acabado

                            const event = new CustomEvent('textureInherited', {
                              detail: {
                                texture: syncResult.inheritedTexture,
                                elementId: linkedSon.id,
                              },
                            })

                            window.dispatchEvent(event)
                          }
                        }
                      }
                    }
                  }
                }

                if (linkedSon.description === 'Tirador') {
                  const elementosEnlazadosOriginales =
                    backGLBInfo.elementosEnlazados ??
                    backGLBInfo.elementosEnlazadosAcabadosPorDefecto ??
                    []
                  let original = null
                  if (rawBackData && rawBackData.idElementosEnlazados) {
                    original = elementosEnlazadosOriginales.find(
                      (el) => el.idElementosEnlazados === rawBackData.idElementosEnlazados,
                    )
                  } else if (rawBackData && rawBackData.descripcion) {
                    original = elementosEnlazadosOriginales.find(
                      (el) => el.descripcion === rawBackData.descripcion,
                    )
                  }
                  if (original) {
                    if (original.nodo !== undefined) {
                      linkedSon.nodo = original.nodo
                    }

                    const posicionesAlternativas = [
                      'biPosicionX',
                      'biPosicionY',
                      'biPosicionZ',
                      'bcPosicionX',
                      'bcPosicionY',
                      'bcPosicionZ',
                      'bdPosicionX',
                      'bdPosicionY',
                      'bdPosicionZ',
                      'aiPosicionX',
                      'aiPosicionY',
                      'aiPosicionZ',
                      'acPosicionX',
                      'acPosicionY',
                      'acPosicionZ',
                      'adPosicionX',
                      'adPosicionY',
                      'adPosicionZ',
                      'rotacionX',
                      'rotacionY',
                      'rotacionZ',
                      'biRotacionX',
                      'biRotacionY',
                      'biRotacionZ',
                      'bdRotacionX',
                      'bdRotacionY',
                      'bdRotacionZ',
                      'bcRotacionX',
                      'bcRotacionY',
                      'bcRotacionZ',
                      'b2idRotacionX',
                      'b2idRotacionY',
                      'b2idRotacionZ',
                      'aiRotacionX',
                      'aiRotacionY',
                      'aiRotacionZ',
                      'adRotacionX',
                      'adRotacionY',
                      'adRotacionZ',
                      'acRotacionX',
                      'acRotacionY',
                      'acRotacionZ',
                      'a2idRotacionX',
                      'a2idRotacionY',
                      'a2idRotacionZ',
                    ]
                    posicionesAlternativas.forEach((key) => {
                      if (original[key] !== undefined) {
                        linkedSon[key] = original[key]
                      }
                    })
                  }
                  linkedSon.handPosition = 'Centro'

                  let prefix = 'b'
                  if (item.tipoNormalizado === 'MAL' || item.tipoNormalizado === 'COL') prefix = 'a'

                  const posicionesCentro = [
                    `${prefix}cPosicionX`,
                    `${prefix}cPosicionY`,
                    `${prefix}cPosicionZ`,
                  ]

                  const algunaPosicionInvalida = posicionesCentro.some((posKey) => {
                    const valor = (linkedSon as any)[posKey]
                    return valor === null || valor === '000' || valor === 0
                  })

                  if (algunaPosicionInvalida) {
                    linkedSon.visible = false
                  } else {
                    linkedSon.visible = true
                  }

                  const sourceForGola = currentSourceID ? store.items.get(currentSourceID) : null
                  if (
                    isLinkedItem(item) &&
                    shouldHideTiradoresForParent(item as LinkedItem, {
                      toolbarGolaActiva: store.toolbarGolaActiva,
                      sourceItem:
                        sourceForGola && isLinkedItem(sourceForGola)
                          ? (sourceForGola as LinkedItem)
                          : null,
                    })
                  ) {
                    linkedSon.visible = false
                  }

                  const morphs = item.morphs || []
                  const morphAncho = morphs.find((m) => m.name === 'Ancho')
                  const morphAlto = morphs.find((m) => m.name === 'Alto')
                  const morphFondo = morphs.find((m) => m.name === 'Fondo')
                  const widthBase = morphAncho?._default ?? item.width
                  const widthActual = morphAncho?.default ?? item.width
                  const heightBase = morphAlto?._default ?? item.height
                  const heightActual = morphAlto?.default ?? item.height
                  const depthBase = morphFondo?._default ?? item.thickness
                  const depthActual = morphFondo?.default ?? item.thickness

                  let X = 0,
                    Y = 0,
                    Z = 0

                  if (linkedSon.handPosition === 'Izquierda') {
                    X = (linkedSon as unknown as Record<string, number>)[`${prefix}iPosicionX`] ?? 0
                    Y = (linkedSon as unknown as Record<string, number>)[`${prefix}iPosicionY`] ?? 0
                    Z = (linkedSon as unknown as Record<string, number>)[`${prefix}iPosicionZ`] ?? 0
                  } else if (linkedSon.handPosition === 'Centro') {
                    X = (linkedSon as unknown as Record<string, number>)[`${prefix}cPosicionX`] ?? 0
                    Y = (linkedSon as unknown as Record<string, number>)[`${prefix}cPosicionY`] ?? 0
                    Z = (linkedSon as unknown as Record<string, number>)[`${prefix}cPosicionZ`] ?? 0
                  } else if (linkedSon.handPosition === 'Derecha') {
                    X = (linkedSon as unknown as Record<string, number>)[`${prefix}dPosicionX`] ?? 0
                    Y = (linkedSon as unknown as Record<string, number>)[`${prefix}dPosicionY`] ?? 0
                    Z = (linkedSon as unknown as Record<string, number>)[`${prefix}dPosicionZ`] ?? 0
                  }

                  const noWidthChange = Math.abs(widthActual - widthBase) < 0.1
                  const noHeightChange = Math.abs(heightActual - heightBase) < 0.1
                  const noDepthChange = Math.abs(depthActual - depthBase) < 0.1
                  if (noWidthChange && noHeightChange && noDepthChange) {
                    linkedSon.x = X
                    linkedSon.y = Y
                    linkedSon.z = Z
                  } else {
                    let x = X
                    switch (linkedSon.handPosition as 'Izquierda' | 'Centro' | 'Derecha') {
                      case 'Centro':
                        if (!noWidthChange) {
                          x = X + (widthActual - widthBase) / 2
                        }
                        break
                      case 'Derecha':
                        if (!noWidthChange) {
                          x = X + (widthActual - widthBase)
                        }
                        break
                      // 'Izquierda' no necesita ajuste
                    }
                    const y = Y
                    let z = Z
                    if (!noDepthChange) {
                      z = Z - (depthBase - depthActual)
                    }
                    linkedSon.x = x
                    linkedSon.y = y
                    linkedSon.z = z
                  }
                }

                if (isEncimeraLinkedSonItem(linkedSon) && usesFreeDimensionScaling(item)) {
                  const apiY = rawBackData.posicionY
                  placeLinkedEncimeraY(linkedSon, item, {
                    backendPosicionY: typeof apiY === 'number' ? apiY : undefined,
                    backendPosicionZ:
                      typeof rawBackData.posicionZ === 'number' ? rawBackData.posicionZ : 0,
                  })
                  logFreeDimensionEncimeraFondo('drawItem.placeSon', linkedSon, item)
                  logFreeDimensionFondo('drawItem.parent', item, {
                    childCatalog: linkedSon.catalogID,
                    childThickness: linkedSon.thickness,
                    childWidth: linkedSon.width,
                  })
                }

                set((store) => {
                  orderedOptions.push(
                    `${linkedSon.parentOption}-${linkedSon.option}-${linkedSon.visualOrder}!${linkedID}`,
                  )
                  // El increment se asignará después con renumberItemsHierarchically
                  linkedSon.increment = '0'
                  optionsObject[linkedSon.option] = linkedID
                  store.items.set(linkedID, linkedSon)
                  store.sceneGLB.set(linkedSon.catalogID, {
                    glb: sonGlbData,
                    backGLBInfo: backData,
                  })
                })
              }
            }

            // Después de procesar todos los hijos, establecer relaciones padre-hijo
            const parentOptionsOrdered = orderedOptions.sort(depthVisualOrderSorter)
            const firstLayer = parentOptionsOrdered.filter((el) => el.startsWith('0-'))

            for (const [parentId, parentValue] of Object.entries(optionsObject)) {
              const findAllChildren = orderedOptions.filter((el) => el.startsWith(`${parentId}-`))
              set((store) => {
                const childSon = store.items.get(parentValue)
                if (!childSon) return
                childSon.childs = new Set(findAllChildren.map((el) => el.split('!')[1]))
              })
            }

            item.childrenOrder = parentOptionsOrdered
            item.childs = new Set(firstLayer.map((el) => el.split('!')[1]))
            item.orderMap = orderMap

            if (options.data && options.data['0'] && options.data['0']['Tirador']) {
              item.tiradorOptions = options.data['0']['Tirador'] as ItemWithOptions[]
            }

            if (options.data && options.data['0'] && options.data['0']['Fregadero']) {
              item.fregaderoOptions = options.data['0']['Fregadero'] as ItemWithOptions[]
            }

            if (options.data && options.data['0']) {
              const grifoKey = Object.keys(options.data['0']).find((key) =>
                key.toLowerCase().includes('grifo'),
              )
              if (grifoKey) {
                ;(item as any).grifoOptions = options.data['0'][grifoKey] as ItemWithOptions[]
              }
            }

            if (options.data && options.data['0']) {
              const placaKey = Object.keys(options.data['0']).find((key) =>
                key.toLowerCase().includes('placa'),
              )
              if (placaKey) {
                ;(item as any).placaOptions = options.data['0'][placaKey] as ItemWithOptions[]
              }
            }
          }

          if (item.type === 'linkedItem') {
            try {
              const zocaloTexture = item.textures.find((el) => el.zoneDisplayName === 'Zocalo')

              if (zocaloTexture && typeof zocaloTexture.linkedTextureID === 'number') {
                const api = API()
                const info = await api.api.acabadosGetAcabadoZonaModeloAcabadoInfo(
                  zocaloTexture.linkedTextureID,
                )
                const data = (info as AcabadoZonaModeloAcabadoInfo)?.data
                const acabados =
                  data && Array.isArray(data)
                    ? data.find((z) => (z?.zona || '').toLowerCase().includes('zocalo'))
                        ?.acabados ||
                      data[0]?.acabados ||
                      []
                    : []

                const idAcabado = acabados[0]?.idAcabadoUnidadModeloAcabado

                if (idAcabado) {
                  const texturasResp = await api.api.acabadosGetTexturas(idAcabado)
                  const texturas = Array.isArray(texturasResp)
                    ? texturasResp
                    : (texturasResp as { data?: ItemWithOptions[] })?.data

                  if (Array.isArray(texturas)) {
                    item.zocaloOptions = texturas as ItemWithOptions[]
                  }
                }
              }
            } catch (e) {
              console.warn('[itemActions] Error cargando opciones de zócalo:', e)
            }
          }

          const itemCenter = toVector3([
            item.morphs[0].default / 2,
            item.morphs[1].default / 2,
            item.morphs[2].default / 2,
          ])

          const centerPosition = new Vector3()
          const quaternion = new Quaternion()
          matrix?.decompose(centerPosition, quaternion, new Vector3())

          itemCenter.applyQuaternion(quaternion).add(centerPosition)

          const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
          const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

          // Verificar ANTES de agregar el item si ya existía (para evitar regenerar frontales al editar)
          const storeBeforeAdd = get()
          const itemAlreadyExisted = storeBeforeAdd.items.has(item.id)

          set((store) => {
            store.sceneGLB.set(catalogID, { glb, backGLBInfo })
            store.items.set(item.id, item)
            if (item.type === 'linkedItem') {
              logFreeDimensionPuertaMaterial(item) 
              logFreeDimensionZocaloMaterial(item)
            }
            store.layers[store.currentLayer].items.add(item.id)
             // linkedSon (incluyendo cutkits) no debe robar foco/seleccion del item editado.
            if (item.type !== 'linkedSon') {
              store.layers[store.currentLayer].selected.clear()
              visible && store.layers[store.currentLayer].selected.add(item.id)
              store.currentObjectID = item.id
            }
            store.sceneBoundingBoxes.set(item.id, {
              boundingBox: new OBB(
                toVector3([
                  -item.morphs[2].default / 2,
                  item.morphs[1].default / 2,
                  item.morphs[0].default / 2,
                ]),
                toVector3([
                  item.morphs[0].default / 2,
                  item.morphs[1].default / 2,
                  item.morphs[2].default / 2,
                ]),
                rotationMatrix,
              ),
              type: 'items',
            })
            if (backGLBInfo.colision ?? !backGLBInfo.universal) {
              store.collisionableItems.add(item.id)
            }
          })

          // Sync encimera height from inherited acabado when drawing next to a selected item.
          if (currentSourceID && item.type === 'linkedItem') {
            // Case 1: encimera is the item itself (linkedItem whose description is 'Encimera')
            const encimeraIsParent = isEncimeraLinkedSonItem(item as any)
            // Case 2: encimera is a linkedSon child of the new item
            const encimeraChild = encimeraIsParent
              ? null
              : (Array.from(get().items.values()).find(
                  (it) => isEncimeraLinkedSonItem(it) && (it as any).firstParentID === item.id,
                ) as any)
            const targetEncimera = encimeraIsParent ? item : encimeraChild
            if (targetEncimera) {
              get().updateEncimeraHeight((targetEncimera as any).id)
            }
          }

          if (catalogID === 'Frontal') {
            const existingFrontals = Array.from(get().items.values()).filter(
              (existingItem) =>
                (isItem(existingItem) || isLinkedItem(existingItem)) &&
                existingItem.catalogID === 'Frontal' &&
                existingItem.id !== item.id,
            )

            if (existingFrontals.length > 0) {
              try {
                const opcionesFrontales = await getEncimerasFrontales()

                set((store) => {
                  const newFrontal = store.items.get(item.id)
                  if (newFrontal && isLinkedItem(newFrontal)) {
                    newFrontal.OpcionesFrontales = opcionesFrontales
                  }

                  existingFrontals.forEach((frontal) => {
                    const frontalItem = store.items.get(frontal.id)
                    if (frontalItem && isLinkedItem(frontalItem)) {
                      frontalItem.OpcionesFrontales = opcionesFrontales
                    }
                  })
                })
              } catch (error) {
                console.error('❌ Error obteniendo opciones de frontales:', error)
              }
            }
          }

          const hasTiradores =
            item.childs &&
            Array.from(item.childs).some((childId) => {
              const childItem = get().items.get(childId)
              return (
                childItem && childItem.type === 'linkedSon' && childItem.typeOfSon === 'Tirador'
              )
            })

          if (item.type === 'linkedItem' && hasTiradores && !item.herenciaProcessed) {
            set((store) => {
              const currentItem = store.items.get(item.id)
              if (currentItem) {
                store.items.set(item.id, { ...currentItem, herenciaProcessed: true })
              }
            })

            await new Promise((resolve) => setTimeout(resolve, 100))

            const tiradoresDestino = Array.from(item.childs).filter((childId) => {
              const childItem = get().items.get(childId)
              return (
                childItem && childItem.type === 'linkedSon' && childItem.typeOfSon === 'Tirador'
              )
            })

            const isSinTiradorCatalogId = (catalogID?: string | null) =>
              String(catalogID ?? '')
                .trim()
                .toLowerCase()
                .includes('sin tirador')

            let tiradorOriginal = null
            let inheritedIdUnidadEnlazada: number | null = null

            for (const [itemId, storeItem] of get().items) {
              if (
                storeItem.type === 'linkedItem' &&
                storeItem.childs &&
                storeItem.childs.size > 0 &&
                storeItem.id !== item.id
              ) {
                for (const originalChildId of storeItem.childs) {
                  const originalChild = get().items.get(originalChildId)
                  if (
                    !originalChild ||
                    originalChild.type !== 'linkedSon' ||
                    originalChild.description !== 'Tirador' ||
                    isSinTiradorCatalogId(originalChild.catalogID)
                  ) {
                    continue
                  }

                  const sceneOption = item.tiradorOptions?.find(
                    (option: any) => option.descripcion === originalChild.catalogID,
                  )
                  if (sceneOption?.idUnidadEnlazada) {
                    tiradorOriginal = originalChild
                    inheritedIdUnidadEnlazada = sceneOption.idUnidadEnlazada
                    break
                  }
                }
                if (tiradorOriginal) break
              }
            }

            if (tiradorOriginal && inheritedIdUnidadEnlazada) {
              for (const childId of item.childs) {
                const childItem = get().items.get(childId)

                if (
                  childItem &&
                  childItem.type === 'linkedSon' &&
                  childItem.typeOfSon === 'Tirador'
                ) {
                  try {
                    const drawerNode = childItem.option ?? childItem.nodo
                    const matchingOption = item.tiradorOptions?.find(
                      (option: any) =>
                        option.nodo === drawerNode &&
                        option.idUnidadEnlazada === inheritedIdUnidadEnlazada,
                    )

                    if (matchingOption) {
                      const tiradorData = await getElementosEnlazadosUnidadAcabados(
                        item.idUnidades,
                        matchingOption.idElementosEnlazados,
                      )

                      set((store) => {
                        const updatedChild = store.items.get(childId)
                        if (updatedChild) {
                          const updatedTirador = {
                            ...updatedChild,

                            catalogID: tiradorData.unidad.codigo,
                            glbPath: tiradorData.unidad.referenciaBlender,
                            referenciaBlender: tiradorData.unidad.referenciaBlender,
                            code: tiradorData.unidad.codigo,
                            referencia: tiradorData.unidad.referencia,
                            long:
                              tiradorData.unidad.descripcionLarga ||
                              tiradorData.unidad.descripcion ||
                              tiradorData.unidad.codigo,
                            textures: updatedChild.textures,
                            zones: updatedChild.zones,

                            acPosicionX: tiradorData.acPosicionX,
                            acPosicionY: tiradorData.acPosicionY,
                            acPosicionZ: tiradorData.acPosicionZ,
                            adPosicionX: tiradorData.adPosicionX,
                            adPosicionY: tiradorData.adPosicionY,
                            adPosicionZ: tiradorData.adPosicionZ,
                            aiPosicionX: tiradorData.aiPosicionX,
                            aiPosicionY: tiradorData.aiPosicionY,
                            aiPosicionZ: tiradorData.aiPosicionZ,
                            bcPosicionX: tiradorData.bcPosicionX,
                            bcPosicionY: tiradorData.bcPosicionY,
                            bcPosicionZ: tiradorData.bcPosicionZ,
                            bdPosicionX: tiradorData.bdPosicionX,
                            bdPosicionY: tiradorData.bdPosicionY,
                            bdPosicionZ: tiradorData.bdPosicionZ,
                            biPosicionX: tiradorData.biPosicionX,
                            biPosicionY: tiradorData.biPosicionY,
                            biPosicionZ: tiradorData.biPosicionZ,
                            acRotacionX: tiradorData.acRotacionX,
                            acRotacionY: tiradorData.acRotacionY,
                            acRotacionZ: tiradorData.acRotacionZ,
                            adRotacionX: tiradorData.adRotacionX,
                            adRotacionY: tiradorData.adRotacionY,
                            adRotacionZ: tiradorData.adRotacionZ,
                            aiRotacionX: tiradorData.aiRotacionX,
                            aiRotacionY: tiradorData.aiRotacionY,
                            aiRotacionZ: tiradorData.aiRotacionZ,
                            bcRotacionX: tiradorData.bcRotacionX,
                            bcRotacionY: tiradorData.bcRotacionY,
                            bcRotacionZ: tiradorData.bcRotacionZ,
                            bdRotacionX: tiradorData.bdRotacionX,
                            bdRotacionY: tiradorData.bdRotacionY,
                            bdRotacionZ: tiradorData.bdRotacionZ,
                            biRotacionX: tiradorData.biRotacionX,
                            biRotacionY: tiradorData.biRotacionY,
                            biRotacionZ: tiradorData.biRotacionZ,
                          }

                          const parentItem = store.items.get(item.id)
                          if (parentItem) {
                            const nuevaPos = calculateTiradorPosition(updatedTirador, parentItem)
                            updatedTirador.x = nuevaPos.x
                            updatedTirador.y = nuevaPos.y
                            updatedTirador.z = nuevaPos.z

                            let rotationZ = 0
                            const prefix =
                              parentItem.tipoNormalizado === 'MAL' ||
                              parentItem.tipoNormalizado === 'COL'
                                ? 'a'
                                : 'b'

                            if (updatedTirador.handPosition === 'Centro') {
                              rotationZ = updatedTirador[`${prefix}cRotacionZ`] || 0
                            } else if (updatedTirador.handPosition === 'Izquierda') {
                              rotationZ = updatedTirador[`${prefix}iRotacionZ`] || 0
                            } else if (updatedTirador.handPosition === 'Derecha') {
                              rotationZ = updatedTirador[`${prefix}dRotacionZ`] || 0
                            }

                            updatedTirador.rotation = rotationZ * (Math.PI / 180)

                            // Recalcular visibilidad según posiciones crudas de la mano actual
                            const posKey =
                              updatedTirador.handPosition === 'Izquierda'
                                ? 'i'
                                : updatedTirador.handPosition === 'Centro'
                                  ? 'c'
                                  : 'd'
                            const rawX = (updatedTirador as any)[`${prefix}${posKey}PosicionX`]
                            const rawY = (updatedTirador as any)[`${prefix}${posKey}PosicionY`]
                            const rawZ = (updatedTirador as any)[`${prefix}${posKey}PosicionZ`]
                            const isNull = rawX == null && rawY == null && rawZ == null
                            const isZero = rawX === 0 && rawY === 0 && rawZ === 0
                            updatedTirador.visible = !(isNull || isZero)
                          }
                          store.items.set(childId, updatedTirador)
                        }
                      })

                      try {
                        const loader = await setGLTFLoader()
                        const glbRef = tiradorData.unidad.referenciaBlender

                        if (glbRef) {
                          const glb = await loader.loadAsync(glbRef)

                          const extraInfo = await API().api.acabadosGetUnidadAcabadosInfo(
                            tiradorData.unidad.idUnidades,
                          )

                          const backGLBInfo = {
                            idUnidades: tiradorData.unidad?.idUnidades ?? 0,
                            linkedID: tiradorData.unidad?.idUnidades ?? 0,
                            active: true,
                            codigo: tiradorData.unidad?.codigo ?? '',
                            referenciaBlender: tiradorData.unidad?.referenciaBlender ?? '',
                            elemento: tiradorData.unidad?.elemento,
                            descripcion: tiradorData.unidad?.descripcion,
                            descripcionLarga: tiradorData.unidad?.descripcionLarga,
                            ancho: tiradorData.unidad?.ancho,
                            fondo: tiradorData.unidad?.fondo,
                            alto: tiradorData.unidad?.alto,
                            mano: tiradorData.unidad?.mano,
                            altura: tiradorData.unidad?.altura,
                            alturaHaciaAbajo: tiradorData.unidad?.alturaHaciaAbajo,
                            colision: tiradorData.unidad?.colision,
                            cota: tiradorData.unidad?.cota,
                            tipoNormalizado: tiradorData.unidad?.tipoNormalizado ?? 'MBJ',
                            tipoUnidNorm: tiradorData.unidad?.tipoUnidNorm ?? '',
                            modificarAncho: tiradorData.unidad?.modificarAncho,
                            modificarFondo: tiradorData.unidad?.modificarFondo,
                            modificarAlto: tiradorData.unidad?.modificarAlto,
                            morphs: tiradorData.unidad?.morphs ?? [],
                            precio: tiradorData.unidad?.precio,
                            acabados: tiradorData.unidad?.acabados ?? [],
                            unidadesLuces: tiradorData.unidad?.unidadesLuces ?? [],
                            elementosEnlazados: tiradorData.unidad?.elementosEnlazados ?? [],
                            referenciaJpgpng: tiradorData.unidad?.referenciaJpgpng,
                            universal: tiradorData.unidad?.universal,
                          } as unknown as BackGLBInfo

                          const processedInfo = newGetDataFromGLB({
                            glbData: glb,
                            backGLBInfo,
                            id: childId,
                            type: 'linkedSon',
                            catalogID: tiradorData.unidad.codigo ?? '',
                            extraInfo: {
                              zonas: Array.isArray(extraInfo.data?.zonas)
                                ? extraInfo.data.zonas.map((z) => ({
                                    idAcabadoUnidad: z.idAcabadoUnidad,
                                    zona: z.zona ?? '',
                                  }))
                                : [],
                            },
                            itemInfo: {},
                          })

                          set((store) => {
                            const updatedChild = store.items.get(childId)
                            if (updatedChild) {
                              Object.assign(updatedChild, {
                                textures: processedInfo.textures,
                                zones: processedInfo.zones,
                              })

                              if (
                                tiradorData.unidad.codigo !== undefined &&
                                tiradorData.unidad.codigo !== null
                              ) {
                                store.sceneGLB.set(String(tiradorData.unidad.codigo), {
                                  glb,
                                  backGLBInfo: tiradorData.unidad,
                                })
                              }
                            }
                          })
                        }
                      } catch (glbError) {
                        console.error('[drawItem] Error cargando GLB:', glbError)
                      }
                    } else {
                      console.warn(
                        `[drawItem] No se encontró opción de tirador para idUnidadEnlazada: ${inheritedIdUnidadEnlazada} y nodo/cajón: ${drawerNode}`,
                      )
                    }
                  } catch (error) {
                    console.error(
                      `[drawItem] Error en llamada API para tirador nodo ${childItem.nodo}:`,
                      error,
                    )
                  }
                }
              }
            }
          }

          // Si el modo de frontales está activo y acabamos de dibujar una unidad junto a una pared,
          // regenerar los frontales para incluir esta nueva unidad. Evitamos recursión si el item es un propio 'Frontal'.
          try {
            const storeSnapshot = get()
            const autoFrontalsEnabled = storeSnapshot.frontalsAutoEnabled ?? true
            // NO regenerar frontales si:
            // 1. El item ya existía (es una edición, no una creación)
            // 2. Es un frontal
            // 3. Es un linkedSon (hijo de un item, como CORTEKITESP, Encimera, etc.)
            // 4. Es un CORTEKITESP específicamente
            const isLinkedSon = item.type === 'linkedSon'
            const isCorteKit = catalogID === 'CORTEKITESP'
            const shouldAutoDrawFrontals =
              autoFrontalsEnabled &&
              storeSnapshot.frontalActive &&
              catalogID !== 'Frontal' &&
              !itemAlreadyExisted && // Solo regenerar frontales si es un item NUEVO, no si se está editando
              !isLinkedSon && // No regenerar si es un hijo (linkedSon)
              !isCorteKit // No regenerar si es un kit de corte

            if (shouldAutoDrawFrontals) {
              // Guardar el ID de la unidad original para mantenerla seleccionada después de regenerar frontales
              const originalUnitId = item.id

              // Altura a usar: si ya existen frontales, reutilizar su altura; si no, usar 50 por defecto
              const existingFrontals = Array.from(storeSnapshot.items.values()).filter(
                (it) => (isItem(it) || isLinkedItem(it)) && (it as any).catalogID === 'Frontal',
              ) as any[]

              const heightFromExisting = existingFrontals.length
                ? Number(existingFrontals[0]?.height) || 50
                : 50

              // Dar tiempo suficiente para que el item quede fijado a la pared/agrupado
              // y esté completamente procesado antes de regenerar frontales
              // NO redibujar frontales si se está dibujando un lateral (LA, LB, LC, CC, CB)
              const catalogIDStr = catalogID?.toString() || ''
              const isLateral =
                catalogIDStr.startsWith('LA') ||
                catalogIDStr.startsWith('LB') ||
                catalogIDStr.startsWith('LC') ||
                catalogIDStr.startsWith('CC') ||
                catalogIDStr.startsWith('CB')

              if (!isLateral) {
                setTimeout(async () => {
                  try {
                    // Redibuja todos los frontales en base a los grupos contiguos actuales
                    // Usar force: false para respetar frontalsAutoEnabled, pero asegurar que frontalActive esté activo
                    const currentStore = get()
                    if (currentStore.frontalActive && (currentStore.frontalsAutoEnabled ?? true)) {
                      await get().drawFrontalsFromSnappedGroups(heightFromExisting)
                    }

                    // Esperar un poco más para que los frontales se procesen completamente
                    await new Promise((resolve) => setTimeout(resolve, 300))

                    // Aplicar acabado/material correcto según la encimera a los nuevos frontales
                    const storeAfter = get()
                    const encimera = Array.from(storeAfter.items.values()).find(
                      (it: any) =>
                        it?.type === 'linkedSon' &&
                        (it?.typeOfSon === 'Encimera' || it?.description === 'Encimera'),
                    ) as any

                    const encimeraTex = encimera?.textures?.find(
                      (t: any) => t.zoneDisplayName === 'Encimera',
                    ) as any
                    const encimeraTextureName: string | undefined = encimeraTex?.name

                    if (encimeraTextureName) {
                      const mapping = storeAfter.frontalesOptions as
                        | Record<string, string>
                        | undefined
                      const frontalCode = mapping ? mapping[encimeraTextureName] : undefined

                      if (frontalCode) {
                        const texturesOptions = (storeAfter.frontalesTexturesOptions as any[]) || []
                        const targetTex = texturesOptions.find(
                          (t: any) => t?.nombre === frontalCode || t?.descripcion === frontalCode,
                        )

                        if (targetTex) {
                          set((store) => {
                            const frontalEntries = Array.from(store.items.entries()).filter(
                              ([, v]: [string, any]) =>
                                v?.type === 'linkedItem' && v?.catalogID === 'Frontal',
                            ) as [string, any][]

                            frontalEntries.forEach(([fid, fitem]) => {
                              const texIdx = fitem?.textures?.findIndex(
                                (tx: any) =>
                                  tx.zoneDisplayName === 'Frontal' || tx.modelo === 'Frontal',
                              )
                              if (texIdx != null && texIdx >= 0) {
                                const prev = fitem.textures[texIdx]
                                const mini =
                                  targetTex.urlMiniatura || targetTex.miniatura || prev?.img
                                fitem.textures[texIdx] = {
                                  ...prev,
                                  name: targetTex.nombre ?? targetTex.descripcion ?? prev?.name,
                                  url: targetTex.url ?? prev?.url,
                                  description: targetTex.descripcion ?? prev?.description,
                                  acabado: prev?.acabado ?? 'Frontal',
                                  img: mini,
                                  id: targetTex.identificadorTextura ?? prev?.id,
                                  searchID: targetTex.idTexturas ?? prev?.searchID,
                                }
                                if (mini) {
                                  fitem.image = mini
                                }
                                // sincronizar zones.material
                                const zoneName = prev?.zoneName || fitem.textures[texIdx]?.zoneName
                                if (zoneName && fitem.zones && fitem.zones[zoneName]) {
                                  const matPrev = fitem.zones[zoneName].material || {}
                                  fitem.zones[zoneName].material = {
                                    ...matPrev,
                                    name:
                                      targetTex.nombre ?? targetTex.descripcion ?? matPrev?.name,
                                    description: targetTex.descripcion ?? matPrev?.description,
                                    url: targetTex.url ?? matPrev?.url,
                                    img: mini ?? matPrev?.img,
                                    acabado: matPrev?.acabado ?? 'Frontal',
                                    groupID: targetTex.idGruposDeTexturas ?? matPrev?.groupID,
                                    id: targetTex.identificadorTextura ?? matPrev?.id,
                                    searchID: targetTex.idTexturas ?? matPrev?.searchID,
                                  }
                                }
                              }
                            })
                          })
                        }
                      }
                    }

                    // Restaurar la selección de la unidad original en lugar del frontal
                    const storeAfterUpdate = get()
                    const originalUnit = storeAfterUpdate.items.get(originalUnitId)
                    if (originalUnit) {
                      // Asegurar que la unidad original esté seleccionada y no el frontal
                      set((store) => {
                        store.currentObjectID = originalUnitId

                        store.layers[store.currentLayer].selected.forEach((selectedId) => {
                          const selectedItem = store.items.get(selectedId)
                          if (selectedItem && (selectedItem as any).catalogID === 'Frontal') {
                            store.layers[store.currentLayer].selected.delete(selectedId)
                          }
                        })
                        // Asegurar que la unidad original esté seleccionada
                        store.layers[store.currentLayer].selected.add(originalUnitId)
                      })
                    }
                  } catch (e) {
                    console.warn('[drawItem] Auto-draw frontals error:', e)
                    // En caso de error, también restaurar la selección
                    const storeAfterError = get()
                    const originalUnit = storeAfterError.items.get(originalUnitId)
                    if (originalUnit) {
                      set((store) => {
                        store.currentObjectID = originalUnitId
                        store.layers[store.currentLayer].selected.add(originalUnitId)
                      })
                    }
                  }
                }, 600)
              }
            }
          } catch (e) {
            console.warn('[drawItem] post-draw frontal sync skipped:', e)
          }

          // Si el objeto origen tiene "GO" en su catalogID, cambiar la gola según el armazón
          // PERO NO hacerlo para laterales u otros items que no deberían tener Gola automáticamente
          if (currentSourceID && !item.universal && isLinkedItem(item)) {
            const itemDescription = String(item.description || '').toLowerCase()
            const itemCatalogID = String(item.catalogID || '').toUpperCase()

            // Detectar laterales por descripción o catalogID
            // KAB puede ser MAL (unidades altas) o lateral, pero si tiene " GO" es una unidad Gola válida
            // Solo detectar laterales reales: LA, LB, LC, CC, CB (sin " GO")
            const isLateral =
              (itemDescription.includes('lateral') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('LA') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('LB') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('LC') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('CC') && !itemCatalogID.includes(' GO')) ||
              (itemCatalogID.startsWith('CB') && !itemCatalogID.includes(' GO'))

            // NO aplicar Gola automáticamente a laterales u otros items específicos
            if (!isLateral) {
              const sourceItem = store.items.get(currentSourceID)
              const storeActions = get()
              const freshItem = get().items.get(item.id)

              if (sourceItem && isLinkedItem(sourceItem) && freshItem && isLinkedItem(freshItem)) {
                const sourceCatalogID = String(sourceItem.catalogID || '')
                const sourceGolaEffective = isGolaEffective(sourceItem as LinkedItem)
                const drawnGolaEffective = isGolaEffective(freshItem as LinkedItem)

                if (sourceGolaEffective && !drawnGolaEffective) {
                  if (
                    isSelfReferencingGolaUnit(freshItem as LinkedItem) &&
                    storeActions.applyLinkedItemGolaModo
                  ) {
                    await storeActions.applyLinkedItemGolaModo(item.id as any, true)
                  } else if (storeActions.updateLinkedItemGola) {
                    const toolbarAcabadoBlancoPreference = store.toolbarAcabadoBlanco
                    const isArmazonBlanco = toolbarAcabadoBlancoPreference === true
                    const isArmazonPetroleo = toolbarAcabadoBlancoPreference === false
                    const armazonBlanco = isArmazonBlanco
                      ? true
                      : isArmazonPetroleo
                        ? false
                        : (freshItem.ArmazonBlanco ?? true)

                    const target = resolveDrawGolaInheritanceTarget(freshItem, sourceCatalogID, {
                      armazonBlanco,
                      sourceGolaEffective,
                    })

                    if (target.strategy !== 'skip') {
                      try {
                        let newUnidadData: Record<string, unknown> | null = null

                        if (target.strategy === 'id') {
                          const response = await API().api.unidadesGetUnidadGet(
                            target.idUnidades,
                            String(target.idUnidades),
                            {
                              headers: {
                                'Accept-Language': 'ES',
                              },
                            },
                          )
                          newUnidadData = response?.data ?? null
                        } else {
                          newUnidadData = await fetchUnidadByCatalog(target.catalogId)
                        }

                        if (newUnidadData) {
                          await storeActions.updateLinkedItemGola(item.id as any, newUnidadData)

                          const storeAfterGola = get()
                          const itemAfterGola = storeAfterGola.items.get(item.id)
                          if (
                            itemAfterGola &&
                            isLinkedItem(itemAfterGola) &&
                            itemAfterGola.textures?.length
                          ) {
                            const existingTextures = getExistingTexturesFromScene(
                              storeAfterGola.items,
                              String(itemAfterGola.catalogID || ''),
                              currentSourceID,
                            )

                            if (existingTextures?.length) {
                              const texturesWithoutArmazon = existingTextures.filter((texture) => {
                                const zoneDisplayName = String(
                                  texture.zoneDisplayName || '',
                                ).toLowerCase()
                                return !zoneDisplayName.includes('armazon')
                              })

                              set((s) => {
                                const it = s.items.get(item.id)
                                if (!it || !isLinkedItem(it) || !it.textures?.length) return

                                const mapped = mapExistingTexturesToNewItemByDisplayName(
                                  texturesWithoutArmazon,
                                  it.textures,
                                )
                                const syncResult = syncZonesWithMappedTextures(
                                  it.zones,
                                  mapped,
                                  s.items,
                                  it.type,
                                  it.description,
                                )
                                it.textures = mapped
                                it.zones = syncResult.zones
                                syncLinkedItemZonesFromTextures(it, {
                                  itemId: item.id,
                                  source: 'drawItem:afterGola',
                                })
                              })
                            }
                          }
                        }
                      } catch (error) {
                        console.warn('[DrawItem][Gola] Error al heredar variante Gola:', error)
                      }
                    }
                  }
                }

                // Si golaActiva se heredó al crear, sincronizar tiradores (idempotente)
                if (
                  freshItem.golaActiva === true &&
                  isSelfReferencingGolaUnit(freshItem as LinkedItem) &&
                  storeActions.applyLinkedItemGolaModo
                ) {
                  await storeActions.applyLinkedItemGolaModo(item.id as any, true)
                }
              }
            }
          }

          if (
            item.type === 'linkedItem' &&
            item.childs &&
            item.childs.size > 0 &&
            usesFreeDimensionScaling(item)
          ) {
            const parentId = item.id
            setTimeout(() => {
              get().updateLinkedSonPositions(parentId, 'Alto')
            }, 0)
          }

          resolve(item.id)
        } catch (error) {
          reject(error)
        }
      })()
    })
  },
  addCutKits: () => {
    const store = get()
    const promises = []
    let modifiedCount = 0
    const originalCurrentLayer = store.currentLayer
    const originalCurrentObjectID = store.currentObjectID
    const originalSelected = new Set(store.layers[originalCurrentLayer].selected)

    // Mapa para rastrear los kits de corte existentes por mueble original
    const existingKitsByItem = new Map()

    // Primero identificar los kits de corte existentes basándose en firstParentID
    store.items.forEach((item, id) => {
      if (
        item.catalogID &&
        typeof item.catalogID === 'string' &&
        item.catalogID.startsWith('CORTEKIT') &&
        item.firstParentID
      ) {
        // Este es un kit de corte, añadirlo al mapa por su padre
        if (!existingKitsByItem.has(item.firstParentID)) {
          existingKitsByItem.set(item.firstParentID, [])
        }
        existingKitsByItem.get(item.firstParentID).push(id)
      }
    })

    // Recorrer todos los items para añadir o actualizar kits
    store.items.forEach((item, id) => {
      // Verificar si es un linkedItem con morphs
      if (item.type === 'linkedItem' && item.morphs && Array.isArray(item.morphs)) {
        const changedDimensions = []

        // Buscar morphs con valores default diferentes a _default
        item.morphs.forEach((morph) => {
          if (
            morph &&
            typeof morph.name === 'string' &&
            morph._default !== undefined &&
            morph.default !== undefined &&
            Math.abs(morph._default - morph.default) > 0.1
          ) {
            changedDimensions.push(morph.name)
          }
        })

        // Si se encontraron dimensiones modificadas
        if (changedDimensions.length > 0) {
          modifiedCount++

          // Determinar qué catalogID usar basado en las dimensiones modificadas
          let catalogID
          if (changedDimensions.length > 1) {
            catalogID = 'CORTEKITESP'
          } else {
            switch (changedDimensions[0]) {
              case 'Alto':
                catalogID = 'CORTEKITALTO'
                break
              case 'Ancho':
                catalogID = 'CORTEKITANCHO'
                break
              case 'Fondo':
                catalogID = 'CORTEKITFONDO'
                break
              default:
                catalogID = 'CORTEKITESP' // Por defecto si es otra dimensión
                break
            }
          }

          // SIEMPRE eliminar todos los kits existentes para este mueble antes de crear uno nuevo
          const existingKits = existingKitsByItem.get(id) || []
          if (existingKits.length > 0) {
            existingKits.forEach((kitId) => {
              // Eliminar el kit existente
              set((state) => {
                const kitItem = state.items.get(kitId)
                const kitCatalogID = kitItem?.catalogID
                const kitCodigo = kitItem ? (kitItem as { codigo?: string }).codigo : undefined
                // Remover del Set childs del padre si existe
                const parentItem = state.items.get(id as AnyID)
                if (parentItem && 'childs' in parentItem && parentItem.childs) {
                  parentItem.childs.delete(kitId)
                }
                state.items.delete(kitId)
                state.layers[state.currentLayer].items.delete(kitId)
                state.layers[state.currentLayer].selected.delete(kitId)
                state.sceneBoundingBoxes.delete(kitId)
                state.collisionableItems.delete(kitId)
                purgeSceneGlbForCatalog(state, kitCatalogID, kitCodigo)
              })
            })

            // Limpiar el mapa para este mueble
            existingKitsByItem.delete(id)
          }

          // En lugar de crear una matriz fija, usar la matriz del padre
          const kitMatrix = item.matrix.clone()
          const position = new Vector3()
          const quaternion = new Quaternion()
          const scale = new Vector3()
          kitMatrix.decompose(position, quaternion, scale)

          // Recomponer la matriz con la posición modificada
          const finalKitMatrix = new Matrix4().compose(position, quaternion, scale)

          // Usar drawItem para crear el linkedItemSon y cargar los datos del backend
          const promise = get()
            .drawItem(
              getId('linkedSon'), // Generar ID para linkedSon
              catalogID, // CatalogoID del kit
              '', // Icono
              false, // Visible (invisible ya que está en Y=100000)
              finalKitMatrix, // Matriz con posición (0, 100000, 0)
              'linkedSon', // Tipo: linkedSon
              undefined, // realCatalogID
              false, // shouldGoToBackHeight
              true, // skipLinkedSons
            )
            .then((newItemId) => {
              // Después de crear el item, asociarlo como hijo del item padre
              set((state) => {
                const kitItem = state.items.get(newItemId)

                if (kitItem) {
                  kitItem.firstParentID = id
                  kitItem.depth = 1
                  kitItem.snappedWall = ''
                }











                state.sceneBoundingBoxes.delete(newItemId)
                state.collisionableItems.delete(newItemId)

                // Importante: drawItem limpia selección y cambia currentObjectID.
                // Restaurar foco/selección para no interferir con el linkedItem editado.
                state.currentObjectID =
                  originalCurrentObjectID && state.items.has(originalCurrentObjectID)
                    ? originalCurrentObjectID
                    : (id as AnyID)
                state.layers[originalCurrentLayer].selected = new Set(originalSelected)
                if (state.layers[originalCurrentLayer].selected.size === 0 && state.items.has(id)) {
                  state.layers[originalCurrentLayer].selected.add(id as AnyID)
                }
              })

              return { id: newItemId, originalId: id, catalogID }
            })
            .catch((error) => {
              console.error(`Error añadiendo kit de corte ${catalogID} para ${id}:`, error)
              return null
            })

          promises.push(promise)
        } else {
          // Si no hay dimensiones modificadas, eliminar cualquier kit existente para este mueble
          const existingKits = existingKitsByItem.get(id) || []
          if (existingKits.length > 0) {
            existingKits.forEach((kitId) => {
              set((state) => {
                const kitItem = state.items.get(kitId)
                const kitCatalogID = kitItem?.catalogID
                const kitCodigo = kitItem ? (kitItem as { codigo?: string }).codigo : undefined
                // Remover del Set childs del padre si existe
                const parentItem = state.items.get(id as AnyID)
                if (parentItem && 'childs' in parentItem && parentItem.childs) {
                  parentItem.childs.delete(kitId)
                }
                state.items.delete(kitId)
                state.layers[state.currentLayer].items.delete(kitId)
                state.layers[state.currentLayer].selected.delete(kitId)
                state.sceneBoundingBoxes.delete(kitId)
                state.collisionableItems.delete(kitId)
                purgeSceneGlbForCatalog(state, kitCatalogID, kitCodigo)
              })
            })
            existingKitsByItem.delete(id)
          }
        }
      }
    })

    // Esperar a que todas las promesas se resuelvan
    return Promise.all(promises).then((results) => {
      const validResults = results.filter(Boolean)
      return validResults
    })
  },
  replaceItem: (
    selectedItemId: string,
    newCatalogID: string,
    icono: string = '',
    type: SceneObjects['type'] = 'items',
    realCatalogID?: string,
  ): Promise<AnyID> => {
    return new Promise((resolve, reject) => {
      try {
        // 1. Obtener el item seleccionado y guardar sus datos
        const selectedItem = get().items.get(selectedItemId)
        if (!selectedItem) {
          reject(new Error(`No se encontró el item con ID ${selectedItemId}`))
          return
        }

        // Guardar la matriz y otras propiedades importantes
        const matrix = selectedItem.matrix?.clone() || null
        const visible = selectedItem.visible
        const snappedWall = selectedItem.snappedWall

        // 2. Eliminar el item seleccionado y toda su jerarquía enlazada
        const itemsSnapshot = get().items
        const idsToDelete = new Set<AnyID>([selectedItemId as AnyID])
        const queue: AnyID[] = [selectedItemId as AnyID]

        while (queue.length > 0) {
          const currentId = queue.shift()
          if (!currentId) continue

          const currentItem = itemsSnapshot.get(currentId as string) as any
          if (!currentItem) continue

          // linkedItem -> linkedSons vía childs
          if (currentItem.childs && currentItem.childs.size > 0) {
            currentItem.childs.forEach((childId: AnyID) => {
              if (idsToDelete.has(childId)) return
              idsToDelete.add(childId)
              queue.push(childId)
            })
          }

          // linkedSons y descendientes por firstParentID
          itemsSnapshot.forEach((candidate: any, candidateId) => {
            if (!candidate || !candidate.firstParentID) return
            if (candidate.firstParentID !== currentId) return
            const normalizedCandidateId = candidateId as AnyID
            if (idsToDelete.has(normalizedCandidateId)) return
            idsToDelete.add(normalizedCandidateId)
            queue.push(normalizedCandidateId)
          })
        }

        set((store) => {
          idsToDelete.forEach((idToDelete) => {
            store.items.delete(idToDelete)
            store.sceneBoundingBoxes.delete(idToDelete)
            store.collisionableItems.delete(idToDelete)
          })

          // Limpiar referencias en TODAS las capas para que no reaparezcan.
          Object.values(store.layers).forEach((layer) => {
            idsToDelete.forEach((idToDelete) => {
              layer.items.delete(idToDelete)
              layer.selected.delete(idToDelete)
            })
          })
        })

        // 3. Dibujar el nuevo item en la misma posición
        get()
          .drawItem(
            getId('Items'), // Generar un nuevo ID único
            newCatalogID,
            icono,
            visible,
            matrix,
            type,
            realCatalogID,
            false,
          )
          .then((newItemId) => {
            // 4. Restaurar la relación con la pared si existía
            if (snappedWall) {
              get().editSnappedWall(newItemId, snappedWall)
            }

            // 5. Devolver el ID del nuevo item
            resolve(newItemId)
          })
          .catch((error) => {
            reject(error)
          })
      } catch (error) {
        reject(error)
      }
    })
  },
  setOBB: (id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const centerPosition = toVector3()
      const quaternion = toQuaternion()
      item.matrix.decompose(centerPosition, quaternion, toVector3())
      const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
      itemCenter.applyQuaternion(quaternion).add(centerPosition)
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
      store.sceneBoundingBoxes.set(id, {
        boundingBox: new OBB(
          itemCenter,
          new Vector3(item.width / 2, item.height / 2, item.thickness / 2),
          rotationMatrix,
        ),
        type: 'items',
      })
    })
  },
  setOBBScene: () => {
    set((store) => {
      store.items.forEach((item, id) => {
        if (!isItem(item) && !isLinkedItem(item)) return
        const centerPosition = toVector3()
        const quaternion = toQuaternion()
        item.matrix.decompose(centerPosition, quaternion, toVector3())
        const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
        itemCenter.applyQuaternion(quaternion).add(centerPosition)
        const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
        const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
        store.sceneBoundingBoxes.set(id, {
          boundingBox: new OBB(
            itemCenter,
            new Vector3(item.width / 2, item.height / 2, item.thickness / 2),
            rotationMatrix,
          ),
          type: 'items',
        })
      })

      // Calcular OBB para ItemGroups: AABB en espacio mundo desde las matrices de los hijos
      store.items.forEach((item, id) => {
        if (item.type !== 'itemGroup') return
        const group = item as ItemGroup
        if (!group.matrix || !group.children.size) return

        const obbResult = computeItemGroupOBB(group.children, store.items, group.matrix)
        if (!obbResult) return

        store.sceneBoundingBoxes.set(id, {
          boundingBox: new OBB(obbResult.center, obbResult.halfSize, obbResult.rotation),
          type: 'items',
        })
      })
    })
  },

  // Actualizar la OBB de un ItemGroup específico calculando directamente desde las matrices de los hijos
  setItemGroupOBB: (groupId: AnyID) => {
    set((store) => {
      const group = store.items.get(groupId) as ItemGroup | undefined
      if (!group || group.type !== 'itemGroup' || !group.matrix || !group.children.size) return

      const obbResult = computeItemGroupOBB(group.children, store.items, group.matrix)
      if (!obbResult) return

      store.sceneBoundingBoxes.set(groupId, {
        boundingBox: new OBB(obbResult.center, obbResult.halfSize, obbResult.rotation),
        type: 'items',
      })

      // Sincronizar width/height/thickness del grupo con las dimensiones de la OBB
      // para que cualquier lógica de colisión que lea esos campos obtenga valores correctos
      group.width = obbResult.halfSize.x * 2
      group.height = obbResult.halfSize.y * 2
      group.thickness = obbResult.halfSize.z * 2
    })
  },
  setSceneGLB: (catalogID: string, scene: any) => {
    set((store) => {
      store.sceneGLB.set(catalogID, { glb: scene, glbData: {} })
    })
  },
  setItemPasteId: (id: string) => {
    set((store) => {
      store.itemsCopyId = id
    })
  },
  editItem: <T extends Item>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    if (config) {
      set((store) => {
        const items = store.items
        if (id === 'selected') {
          const ids = store.layers[store.currentLayer].selected
          ids.forEach((id) => {
            const item = store.items.get(id) as Item
            if (!item) return
            for (const [property, value] of Object.entries(config)) {
              item[property] = value
            }
            items.set(id, item)
          })
        } else {
          const item = store.items.get(id) as Item
          if (!item) return

          const matrix = item.matrix as Matrix4
          const centerPosition = new Vector3()
          const quaternion = new Quaternion()
          matrix.decompose(centerPosition, quaternion, new Vector3())

          const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])

          itemCenter.applyQuaternion(quaternion).add(centerPosition)

          // Convertir la rotación del quaternion a una Matrix4 y extraer la Matrix3
          const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
          const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

          const mode = store.mode

          // Aplicar restricción de altura en modo 3D cuando se actualiza y o matrix
          let restrictedY: number | undefined = undefined
          if (
            (config.y !== undefined || config.matrix !== undefined) &&
            (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS')
          ) {
            // Obtener el Y actual: de config.y o de la matriz si viene en config.matrix
            let currentY = config.y as number | undefined
            if (currentY === undefined && config.matrix !== undefined) {
              const matrixPos = toVector3()
              ;(config.matrix as Matrix4).decompose(matrixPos, toQuaternion(), toVector3())
              currentY = matrixPos.y
            }
            if (currentY === undefined) {
              currentY = item.y
            }

            restrictedY = currentY

            // Restringir mínimo a 0 siempre
            restrictedY = Math.max(0, restrictedY)

            // Si el item tiene altura, aplicar restricción máxima
            if (item.height) {
              // Obtener altura máxima de la pared
              let maxWallHeight: number | null = null

              // Si el item está pegado a una pared, usar esa pared
              if (item.snappedWall) {
                const wall = store.items.get(item.snappedWall) as Walls
                if (isWall(wall)) {
                  maxWallHeight = wall.height
                }
              } else {
                // Si no está pegado, obtener la altura máxima de todas las paredes
                let maxHeight = 0
                store.items.forEach((sceneObject) => {
                  if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                    maxHeight = sceneObject.height
                  }
                })
                maxWallHeight = maxHeight > 0 ? maxHeight : null
              }

              // Restringir máximo considerando la altura del mueble
              if (maxWallHeight !== null) {
                const maxYAllowed = maxWallHeight - item.height
                restrictedY = Math.min(restrictedY, maxYAllowed)
              }
            }

            // Actualizar config.y y config.matrix con el valor restringido
            if (config.y !== undefined) {
              config.y = restrictedY as T[keyof T]
            }
            if (config.matrix !== undefined) {
              const matrix = config.matrix as Matrix4
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              matrix.decompose(matrixPos, matrixRot, matrixScale)
              const restrictedMatrix = new Matrix4()
                .makeRotationFromQuaternion(matrixRot)
                .setPosition(matrixPos.x, restrictedY, matrixPos.z)
              config.matrix = restrictedMatrix as T[keyof T]
            }
          }

          for (const [property, value] of Object.entries(config)) {
            item[property] = value
          }

          // Si se aplicó restricción pero no se actualizó y directamente, actualizarlo ahora
          if (restrictedY !== undefined && config.y === undefined) {
            item.y = restrictedY
            // También actualizar la matriz si existe
            if (item.matrix) {
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              item.matrix.decompose(matrixPos, matrixRot, matrixScale)
              item.matrix.setPosition(matrixPos.x, restrictedY, matrixPos.z)
            }
          }

          // CORRECCIÓN ABSOLUTA: asegurar que item.y NUNCA sea negativo en modo 3D o alzados
          // Esta es la última línea de defensa - se ejecuta SIEMPRE después de cualquier actualización
          if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
            if (item.y < 0) {
              item.y = 0
              // Actualizar la matriz también para mantener consistencia
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              item.matrix.decompose(matrixPos, matrixRot, matrixScale)
              item.matrix.setPosition(matrixPos.x, 0, matrixPos.z)
            }
          }

          // Recalcular itemCenter si se aplicó restricción
          if (restrictedY !== undefined) {
            const finalMatrix = item.matrix as Matrix4
            const finalCenterPosition = toVector3()
            const finalQuaternion = toQuaternion()
            finalMatrix.decompose(finalCenterPosition, finalQuaternion, toVector3())
            const finalItemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
            finalItemCenter.applyQuaternion(finalQuaternion).add(finalCenterPosition)
            itemCenter.copy(finalItemCenter)
          }

          items.set(id, item)
          store.sceneBoundingBoxes.set(id, {
            boundingBox: new OBB(
              itemCenter,
              toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
              rotationMatrix,
            ),
            type: 'items',
          })
        }
      })
    }
  },
  editItemPosition: (id: string, x: number, z: number, y?: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      if (!item.visible) item.visible = true
      item.matrix = item.matrix.clone().setPosition(x, item.y, z)
    })
  },
  editItemHeight: (height: number, id?: string) => {
    set((store) => {
      const mode = store.mode

      if (id) {
        const item = store.items.get(id) as Item
        if (!item) return

        // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
        if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
          height = Math.max(0, height)

          // Obtener altura máxima de la pared
          let maxWallHeight: number | null = null

          // Si el item está pegado a una pared, usar esa pared
          if (item.snappedWall && item.height) {
            const wall = store.items.get(item.snappedWall) as Walls
            if (isWall(wall)) {
              maxWallHeight = wall.height
            }
          } else if (item.height) {
            // Si no está pegado, obtener la altura máxima de todas las paredes
            let maxHeight = 0
            store.items.forEach((sceneObject) => {
              if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                maxHeight = sceneObject.height
              }
            })
            maxWallHeight = maxHeight > 0 ? maxHeight : null
          }

          // Restringir máximo considerando la altura del mueble
          // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
          if (maxWallHeight !== null && item.height) {
            const maxYAllowed = maxWallHeight - item.height
            height = Math.min(height, maxYAllowed)
          }
        }

        const pos = toVector3()
        const rotation = toQuaternion()
        const scale = toVector3()
        item.matrix.decompose(pos, rotation, scale)

        // CORRECCIÓN FINAL: asegurar que height nunca sea negativo (última línea de defensa)
        const finalHeight = Math.max(0, height)

        // Para luces, NO reconstruir la matriz - solo cambiar la posición Y
        // Esto preserva la rotación exacta que ya está en la matriz
        if (item.type === 'lights') {
          const rotationAntes = item.rotation

          console.log('🔧 editItemHeight - LUZ (solo cambiando Y):', {
            id: item.id,
            catalogID: item.catalogID,
            rotationFromStore: item.rotation,
            heightAntes: pos.y,
            heightNuevo: finalHeight,
          })

          // Solo cambiar la posición Y sin reconstruir la matriz completa
          // Esto preserva la rotación exacta
          item.matrix.setPosition(pos.x, finalHeight, pos.z)
          item.y = finalHeight

          // Asegurar que la rotación NO cambie
          if (rotationAntes !== item.rotation) {
            console.error('🔧 editItemHeight - ERROR: La rotación cambió!', {
              rotationAntes,
              rotationDespues: item.rotation,
            })
            // Restaurar la rotación original
            item.rotation = rotationAntes
          }

          console.log('🔧 editItemHeight - DESPUÉS:', {
            id: item.id,
            rotationEnStore: item.rotation,
            y: item.y,
          })
        } else {
          // Para otros items, reconstruir la matriz normalmente
          item.matrix.makeRotationFromQuaternion(rotation)
          item.matrix.setPosition(pos.x, finalHeight, pos.z)
          item.matrix.scale(scale)
          item.y = finalHeight
        }
      } else {
        const selectedItems = store.layers[store.currentLayer].selected
        selectedItems.forEach((id) => {
          const item = store.items.get(id) as Item
          if (!item) return

          let finalHeight = height

          // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
          if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
            finalHeight = Math.max(0, finalHeight)

            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall && item.height) {
              const wall = store.items.get(item.snappedWall) as Walls
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else if (item.height) {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              store.items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
            if (maxWallHeight !== null && item.height) {
              const maxYAllowed = maxWallHeight - item.height
              finalHeight = Math.min(finalHeight, maxYAllowed)
            }
          }

          const pos = toVector3()
          item.matrix.decompose(pos, toQuaternion(), toVector3())
          // CORRECCIÓN FINAL: asegurar que finalHeight nunca sea negativo (última línea de defensa)
          const correctedHeight = Math.max(0, finalHeight)
          item.matrix.setPosition(pos.x, correctedHeight, pos.z)
          item.y = correctedHeight
        })
      }
    })
  },
  incrementItemZ: (id: AnyID, z: number) => {
    const axis = 'z'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      const rotation = toQuaternion()

      item.matrix = addItemOffset(item.matrix, z, axis)
      item.matrix.decompose(pos, rotation, toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.z = pos.z
      item.y = pos.y
      item.x = pos.x

      // Si es una luz, actualizar también el campo rotation desde la matriz
      if (item.type === 'lights') {
        const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
        let rotationDegrees = euler.y * (180 / Math.PI)
        rotationDegrees = ((rotationDegrees % 360) + 360) % 360
        rotationDegrees = Math.round(rotationDegrees)

        // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
        // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
        if (
          rotationDegrees === 270 &&
          (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
        ) {
          rotationDegrees = 90
        }

        item.rotation = rotationDegrees
      }
    })
  },
  incrementItemY: (selectedItems: Set<string>, y: number) => {
    const axis = 'y'
    set((store) => {
      const mode = store.mode

      selectedItems.forEach((id) => {
        const item = store.items.get(id) as Item
        if (!item) return

        const pos = toVector3()

        item.matrix = addItemOffset(item.matrix, y, axis)
        item.matrix.decompose(pos, toQuaternion(), toVector3())

        let newY = pos.y

        // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
        if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
          // Restringir mínimo a 0 siempre
          newY = Math.max(0, newY)

          // Si el item tiene altura, aplicar restricción máxima
          if (item.height) {
            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall) {
              const wall = store.items.get(item.snappedWall) as Walls
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              store.items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
            if (maxWallHeight !== null) {
              const maxYAllowed = maxWallHeight - item.height
              newY = Math.min(newY, maxYAllowed)
            }
          }
        }

        // CORRECCIÓN FINAL: asegurar que newY nunca sea negativo (última línea de defensa)
        const finalY = Math.max(0, newY)

        item.matrix.setPosition(pos.x, finalY, pos.z)
        item.z = pos.z
        item.y = finalY
        item.x = pos.x

        // Si es una luz, NO recalcular la rotación desde la matriz
        // La rotación debe mantenerse igual cuando solo se cambia la posición Y
        // Solo actualizar si la rotación no está definida en la store
        if (item.type === 'lights') {
          if (item.rotation === undefined || item.rotation === null) {
            // Solo calcular desde la matriz si no está definida
            const rotation = toQuaternion()
            const scale = toVector3()
            item.matrix.decompose(pos, rotation, scale)
            const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
            let rotationDegrees = euler.y * (180 / Math.PI)
            rotationDegrees = ((rotationDegrees % 360) + 360) % 360
            rotationDegrees = Math.round(rotationDegrees)

            // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
            // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
            if (
              rotationDegrees === 270 &&
              (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
            ) {
              rotationDegrees = 90
            }

            item.rotation = rotationDegrees
          }
          // Si la rotación ya está definida en la store, mantenerla (no recalcular)
        }
      })
    })
  },
  incrementItemX: (id: AnyID, x: number) => {
    const axis = 'x'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      const rotation = toQuaternion()

      item.matrix = addItemOffset(item.matrix, x, axis)
      item.matrix.decompose(pos, rotation, toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.z = pos.z
      item.y = pos.y
      item.x = pos.x

      // Si es una luz, actualizar también el campo rotation desde la matriz
      if (item.type === 'lights') {
        const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
        let rotationDegrees = euler.y * (180 / Math.PI)
        rotationDegrees = ((rotationDegrees % 360) + 360) % 360
        rotationDegrees = Math.round(rotationDegrees)

        // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
        // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
        if (
          rotationDegrees === 270 &&
          (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
        ) {
          rotationDegrees = 90
        }

        item.rotation = rotationDegrees
      }
    })
  },
  editItemRotation: (id: string, rotation: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      // Extraer la posición actual de la matriz
      const position = toVector3()
      const scale = toVector3()
      item.matrix.decompose(position, new Quaternion(), scale)
      // Crear nueva matriz con la rotación especificada y mantener posición y escala
      item.matrix.makeRotationY(radians(-rotation))
      item.matrix.setPosition(position)
      item.matrix.scale(scale)
      // Actualizar el campo rotation en el item
      item.rotation = rotation
    })
  },

  setItemVisible: (id: string) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      if (!item.visible) item.visible = true
    })
  },
  editItemZone: <T extends Item>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    set((store) => {
      if (!config) return
      const item = store.items.get(id) as Item

      if (!item) return

      const property = Object.keys(config)[0]

      if (config[property].value && config[property].id) {
        item.textures.forEach((prop: { zone: string }, propIndex: number) => {
          if (property === prop.zone) {
            //@ts-expect-error
            item.textures[propIndex].value = config[property].value
            //@ts-expect-error
            item.textures[propIndex].id = config[property].id
            item.textures[propIndex].idTexturas = config[property].idTexturas
            item.textures[propIndex].idGruposDeTexturas = config[property].idGruposDeTexturas
            item.textures[propIndex].nombre = config[property].nombre
          }
        })
      }

      Object.entries(item.zones).forEach(([zoneID, zone]: Zone, zoneIndex: number) => {
        console
        if (property === zone.material?.name) {
          const {
            color,
            roughness,
            mirror,
            repeatX,
            repeatY,
            metalness,
            opacity,
            envMapIntensity,
            displacement,
            displacementMap,
            bumpScale,
            map,
          } = config[property]

          const material = Object.values(item.zones)[zoneIndex].material

          const finalRepeatX = repeatX //>= 0.001 ? repeatX / 1000 : repeatX
          const finalRepeatY = repeatY //>= 0.001 ? repeatY / 1000 : repeatY

          item.zones[zoneID].material = {
            ...item.zones[zoneID].material,
            name: property,
            color: color ? (validateHex(color) ? color : '#FFFFFF') : material.color,
            roughness: roughness ?? material.roughness,
            mirror: mirror ?? material.mirror,
            repeatX: finalRepeatX ?? material.repeatX,
            repeatY: finalRepeatY ?? material.repeatY,
            map: map ?? material.map,
            metalness: metalness ?? material.metalness,
            opacity: opacity ?? material.opacity,
            envMapIntensity: envMapIntensity ?? material.envMapIntensity,
            displacement: displacement ?? material.displacement,
            displacementMap: displacementMap ?? material.displacementMap,
            bumpScale: bumpScale ?? material.bumpScale,
            transparent: opacity < 1 ? true : false,
            depthWrite: opacity < 1 ? false : true,
            scale: toVector3([1, 1, 1]),
          }
        }
      })
    })
  },
  editGLBSize: (id: string, sizeVector: Vector3) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      item.glbSize = sizeVector
    })
  },
  editSnappedWall: (id: string, wallID?: string) => {
    set((store) => {
      const item = store.items.get(id) as Item
      const wall = store.items.get(wallID) as Walls
      if (!(isItem(item) || !isLinkedItem(item)) || !isWall(wall) || !id || !wallID) return
      item.snappedWall = wallID
      if (item.tipoNormalizado === 'MAL' && wall.highItems) {
        wall.highItems.add(item.id)
      }
      if ((item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') && wall.lowItems) {
        wall.lowItems && wall.lowItems.add(item.id)
      }
      const area = wall.areas
      if (!item.tipoNormalizado || !dictionary[item.tipoNormalizado]) return
      area.forEach((areaID) => {
        const currArea = store.areas.get(areaID)
        if (isArea(currArea)) {
          currArea.sides.forEach((singleWall) => {
            if (singleWall === wallID) return console.log('Same wall found', { wallID, singleWall }) // Si es la misma wall que está snapeando pasamos
            const singleWallObject = store.items.get(singleWall) as Walls
            if (isWall(singleWall)) {
              // Sino, buscamos si cualquier otra pared tiene este objeto pegado
              singleWallObject[dictionary[item.tipoNormalizado]].delete(id)
            }
          })
        }
      })
    })
  },
  removeSnappedWall: (id: string) => {
    set((store) => {
      const selectedIds = store.layers[store.currentLayer].selected as Set<AnyID>
      const targets = new Set<AnyID>()
      if (id) targets.add(id as AnyID)
      selectedIds?.forEach((selectedId) => targets.add(selectedId))

      targets.forEach((targetId) => {
        const item = store.items.get(targetId) as Item | LinkedItem
        const wall = store.items.get(item?.snappedWall ?? '') as Walls
        if (!item || (!isItem(item) && !isLinkedItem(item))) return
        if (!isWall(wall)) return

        if (item.tipoNormalizado === 'MAL') {
          wall.highItems.delete(item.id)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          wall.lowItems.delete(item.id)
        }
        item.snappedWall = ''
      })
    })
  },
  removeSnappedPartition: (id: string) => {
    set((store) => {
      const selectedIds = store.layers[store.currentLayer].selected as Set<AnyID>
      const targets = new Set<AnyID>()
      if (id) targets.add(id as AnyID)
      selectedIds?.forEach((selectedId) => targets.add(selectedId))

      targets.forEach((targetId) => {
        const item = store.items.get(targetId) as Item | LinkedItem
        const partition = store.items.get(item?.snappedWall ?? '') as Partition
        if (!item || (!isItem(item) && !isLinkedItem(item))) return
        if (!isPartition(partition)) return

        console.log('[removeSnappedPartition]', {
          targetId,
          snappedWall: item.snappedWall,
          tipo: item.tipoNormalizado,
        })

        if (item.tipoNormalizado === 'MAL') {
          partition.highItems.delete(item.id)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          partition.lowItems.delete(item.id)
        }
        item.snappedWall = ''
      })
    })
  },
  editItemAngle: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id)
      if (!item) return

      // Manejar ItemGroups
      if (item.type === 'itemGroup') {
        const position = toVector3()
        item.matrix.decompose(position, new Quaternion(), toVector3())
        item.matrix.makeRotationY(radians(-angle))
        item.matrix.setPosition(position)
        const newMatrix = item.matrix.clone()
        const updatedItem = {
          ...item,
          matrix: newMatrix,
        }
        store.items.set(id, updatedItem)
        // Actualizar la OBB del grupo después de cambiar su rotación
        get().setItemGroupOBB(id)
        return
      }

      // Manejar Items normales y LinkedItems
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      item.matrix.decompose(position, new Quaternion(), toVector3())
      item.matrix.makeRotationY(radians(-angle))
      item.matrix.setPosition(position)
      const newMatrix = item.matrix
      const updatedItem = {
        ...item,
        matrix: newMatrix,
      }
      store.items.set(id, updatedItem)
    })
  },
  editItemAngleX: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      item.matrix.decompose(position, new Quaternion(), toVector3())
      item.matrix.makeRotationX(radians(-angle))
      item.matrix.setPosition(position)
      const newMatrix = item.matrix
      const updatedItem = {
        ...item,
        matrix: newMatrix,
      }
      store.items.set(id, updatedItem)
    })
  },
  editItemAngleZ: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      item.matrix.decompose(position, new Quaternion(), toVector3())
      item.matrix.makeRotationZ(radians(-angle))
      item.matrix.setPosition(position)
      const newMatrix = item.matrix
      const updatedItem = {
        ...item,
        matrix: newMatrix,
      }
      store.items.set(id, updatedItem)
    })
  },
  setIsBlocked: (blocked: boolean, id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      item.isBlocked = blocked
    })
  },
  setCollision: (collision: boolean, id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item
      const collisionableItems = store.collisionableItems
      if (!item) return
      item.canCollide = collision
      if (collisionableItems.has(id)) {
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
      }
    })
  },
  removeCollision: (collision: boolean, id: string, isShiftKey: boolean) => {
    set((store) => {
      const item = store.items.get(id)
      const collisionableItems = store.collisionableItems
      if (isShiftKey) {
        if (!item) return
        item.canCollide = collision
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
        item.canCollide = !collision
      }
    })
  },
  collideItems: (itemId: AnyID, itemId1: AnyID) => {
    set((store) => {
      const item = store.items.get(itemId) as LinkedItem
      const item1 = store.items.get(itemId1) as LinkedItem
      if (!item || !item1) return
      function getRegletaInvertedValue(value: number, morph: Morphs) {
        if (!morph) return value
        if (value >= morph.max) return morph.min
        if (value <= morph.min) return morph.max
        return morph.max + morph.min - value
      }
      // Detectar si el mueble al que nos pegamos es un rinconero
      const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
      const isRinconeroDerecha =
        item1.catalogID &&
        rinconeroPatterns.some((pattern) =>
          item1.catalogID.toString().toLowerCase().includes(pattern),
        ) &&
        !item1.catalogID.toString().toString().toLowerCase().includes('i')
      const isRinconeroIzquierda =
        item1.catalogID &&
        rinconeroPatterns.some((pattern) =>
          item1.catalogID.toString().toLowerCase().includes(pattern),
        ) &&
        item1.catalogID.toString().toLowerCase().includes('i')
      const isRinconero = isRinconeroDerecha || isRinconeroIzquierda
      // Buscar el morph de ancho de manera más robusta
      const itemWidthMorph = item.morphs?.find(
        (morph) => morph.name === 'Ancho' || morph.name === 'width',
      )
      const itemWidth = item.long?.toLowerCase().includes('encimera b3200')
        ? (itemWidthMorph?._default ?? item.width ?? 0)
        : item.long !== 'Frontal' || item.long?.toLowerCase().includes('zocalo')
          ? (itemWidthMorph?.default ?? item.width ?? 0)
          : (itemWidthMorph?._default ?? item.width ?? 0)
      const item1WidthMorph = item1.morphs?.find(
        (morph) => morph.name === 'Ancho' || morph.name === 'width',
      )
      const item1Width = item1.long?.toLowerCase().includes('encimera b3200')
        ? (item1WidthMorph?._default ?? item1.width ?? 0)
        : item1.long !== 'Frontal' || item1.long?.toLowerCase().includes('zocalo')
          ? (item1WidthMorph?.default ?? item1.width ?? 0)
          : (item1WidthMorph?._default ?? item1.width ?? 0)
      const item1Matrix = item1.matrix.clone()
      const item1Pos = new Vector3()
      const item1Quat = new Quaternion()
      item1Matrix.decompose(item1Pos, item1Quat, new Vector3())
      const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(item1Quat)
      const directionMultiplier =
        item1XAxis.dot(new Vector3(item.x - item1Pos.x, 0, item.z - item1Pos.z)) < 0 ? -1 : 1

      // Variables que pueden ser modificadas por la lógica especial cuando se pega a rinconeros
      let newPosition
      const newQuaternion = item1Quat.clone() // Por defecto usa la misma rotación

      // Obtener la posición actual del item para casos especiales (MAL sobre MBJ/MAL, MBJ bajo MAL)
      const itemCurrentPos = new Vector3()
      item.matrix.decompose(itemCurrentPos, new Quaternion(), new Vector3())

      // Detectar si item1 es KARB (arp, arb) - estos usan regleta directa, NO invertida
      const item1CatalogID = item1.catalogID?.toString() || ''
      const isKARB =
        item1CatalogID.toLowerCase().includes('arb') || item1CatalogID.toLowerCase().includes('arp')
      const item1RegletaMorph = item1.morphs?.find((m) => m.name === 'Regleta')
      const item1RegletaValue = item1.regleta || 0
      // Para KARB: usar regleta directa (NO invertir). Para otros: usar regleta invertida
      const item1RegletaInverted = getRegletaInvertedValue(item1RegletaValue, item1RegletaMorph)
      const item1RegletaRealValue = isKARB
        ? item1RegletaValue // KARB: regleta directa
        : item1RegletaInverted // Otros: regleta invertida

      // CASO ESPECIAL: MAL sobre MBJ o MBJ bajo MAL - preservar posición horizontal actual
      // Para todos los demás casos (MBJ con MBJ, MAL con MAL, COL con COL, MBJ con COL, MAL con COL):
      // usar la misma lógica simple de colisión lateral
      const isMALOverMBJ = item.tipoNormalizado === 'MAL' && item1.tipoNormalizado === 'MBJ'
      const isMBJUnderMAL = item.tipoNormalizado === 'MBJ' && item1.tipoNormalizado === 'MAL'

      if (isMALOverMBJ || isMBJUnderMAL) {
        // Para MAL sobre MBJ/MAL o MBJ bajo MAL: mantener la posición X y Z actual del item
        // Solo ajustaremos la Y después
        newPosition = itemCurrentPos.clone()
      } else if (isRinconeroDerecha && directionMultiplier > 0) {
        newPosition = item1Pos
          .clone()
          .add(item1XAxis.multiplyScalar(directionMultiplier * item1Width))
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          -Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        const KARB_OFFSET_ADJUSTMENT = isKARB ? -50 : 0
        const rinconeroOffset =
          (item1.thickness || 0) + item1RegletaRealValue + KARB_OFFSET_ADJUSTMENT
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(item1Quat)
        newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
      }
      // CASO ESPECIAL: Mueble normal pegado a la izquierda de un rinconero izquierdo
      else if (isRinconeroIzquierda && directionMultiplier < 0) {
        newPosition = item1Pos.clone()
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        // Para KARB: usar regleta directa. Para otros: usar regleta invertida
        // KARB parece necesitar un offset adicional
        const KARB_OFFSET_ADJUSTMENT = isKARB ? -50 : 0
        const rinconeroOffset =
          (item1.thickness || 0) +
          item1RegletaRealValue +
          (item.width || 0) +
          KARB_OFFSET_ADJUSTMENT
        const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
        newPosition.add(zAxis.multiplyScalar(-rinconeroOffset))
      } else {
        // IMPORTANTE: Usar el ancho correcto dependiendo de la dirección
        // - Derecha (directionMultiplier = 1): item1Pos + item1Width (el item se coloca después del item1)
        // - Izquierda (directionMultiplier = -1): item1Pos - itemWidth (el item se coloca antes del item1)
        // Esto asegura que el nuevo item quede pegado correctamente al item de referencia
        const widthToUse = directionMultiplier > 0 ? item1Width : itemWidth
        const offsetDistance = directionMultiplier * widthToUse

        newPosition = item1Pos.clone().add(item1XAxis.multiplyScalar(offsetDistance))
      }

      // Ajustar posición Y según tipoNormalizado
      // Solo casos de apilamiento vertical:
      // - MAL sobre MBJ: colocar encima del MBJ
      // - MBJ bajo MAL: colocar debajo del MAL (misma Y base)
      // Para todos los demás casos (MBJ con MBJ, MAL con MAL, COL con COL, MBJ con COL, MAL con COL):
      // mantener la posición Y del item (van al lado)
      // const originalY = newPosition.y
      if (isMALOverMBJ) {
        // MAL sobre MBJ: posición Y = base del MBJ + altura del MBJ
        newPosition.y = item1Pos.y + (item1.height || 0)
      } else if (isMBJUnderMAL) {
        // MBJ bajo MAL: misma posición Y base que el MAL
        newPosition.y = item1Pos.y
      } else {
        // Caso normal: mantener la posición Y del item (incluye MBJ con MBJ, MAL con MAL, COL con COL, MBJ con COL, MAL con COL)
        newPosition.y = item.y
      }

      const centerPosition = toVector3()
      const quaternion = toQuaternion()
      item.matrix.decompose(centerPosition, quaternion, new Vector3())
      const itemCenter = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
      itemCenter.applyQuaternion(quaternion).add(newPosition)
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(
        (isRinconeroDerecha && directionMultiplier > 0) ||
          (isRinconeroIzquierda && directionMultiplier < 0)
          ? newQuaternion
          : quaternion,
      )
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
      item.matrix.makeRotationFromQuaternion(
        (isRinconeroDerecha && directionMultiplier > 0) ||
          (isRinconeroIzquierda && directionMultiplier < 0)
          ? newQuaternion
          : item1Quat,
      )
      // Usar newPosition.y para MAL sobre MBJ y MBJ bajo MAL
      item.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)

      item.x = newPosition.x
      item.y = newPosition.y // Actualizar item.y para mantener consistencia
      item.z = newPosition.z

      const eulerRotation = new Euler().setFromQuaternion(
        (isRinconeroDerecha && directionMultiplier > 0) ||
          (isRinconeroIzquierda && directionMultiplier < 0)
          ? newQuaternion
          : item1Quat,
      )
      item.rotation = radToDeg(eulerRotation.y)
      store.sceneBoundingBoxes.set(itemId, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })
      // ItemGroups no deben heredar snappedWall ni agregarse a las paredes
      if (item1.snappedWall && !isRinconero && item.type !== 'itemGroup') {
        const wall = store.items.get(item1.snappedWall) as Walls
        item.snappedWall = item1.snappedWall
        if (item.tipoNormalizado === 'MAL') {
          wall.highItems.add(itemId)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          wall.lowItems.add(itemId)
        }
      }
    })
  },
  // collideItems: (itemId: AnyID, itemId1: AnyID) => {
  //   set((store) => {
  //     const item = store.items.get(itemId) as LinkedItem
  //     const item1 = store.items.get(itemId1) as LinkedItem
  //     if (!item || !item1) return
  //     function getRegletaInvertedValue(value: number, morph: Morphs) {
  //       if (!morph) return value
  //       if (value >= morph.max) return morph.min
  //       if (value <= morph.min) return morph.max
  //       return morph.max + morph.min - value
  //     }
  //     // Detectar si el mueble al que nos pegamos es un rinconero
  //     const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
  //     const isRinconeroDerecha =
  //       item1.catalogID &&
  //       rinconeroPatterns.some((pattern) =>
  //         item1.catalogID.toString().toLowerCase().includes(pattern),
  //       ) &&
  //       !item1.catalogID.toString().toString().toLowerCase().includes('i')
  //     const isRinconeroIzquierda =
  //       item1.catalogID &&
  //       rinconeroPatterns.some((pattern) =>
  //         item1.catalogID.toString().toLowerCase().includes(pattern),
  //       ) &&
  //       item1.catalogID.toString().toLowerCase().includes('i')
  //     const isRinconero = isRinconeroDerecha || isRinconeroIzquierda

  //     // Buscar el morph de ancho de manera más robusta
  //     const itemWidthMorph = item.morphs?.find(
  //       (morph) => morph.name === 'Ancho' || morph.name === 'width',
  //     )

  //     const itemWidth = item.long?.toLowerCase().includes('encimera b3200')
  //       ? (itemWidthMorph?._default ?? item.width ?? 0)
  //       : item.long !== 'Frontal' || item.long?.toLowerCase().includes('zocalo')
  //         ? (itemWidthMorph?.default ?? item.width ?? 0)
  //         : (itemWidthMorph?._default ?? item.width ?? 0)

  //     const item1WidthMorph = item1.morphs?.find(
  //       (morph) => morph.name === 'Ancho' || morph.name === 'width',
  //     )
  //     const item1Width = item1.long?.toLowerCase().includes('encimera b3200')
  //       ? (item1WidthMorph?._default ?? item1.width ?? 0)
  //       : item1.long !== 'Frontal' || item1.long?.toLowerCase().includes('zocalo')
  //         ? (item1WidthMorph?.default ?? item1.width ?? 0)
  //         : (item1WidthMorph?._default ?? item1.width ?? 0)

  //     const item1Matrix = item1.matrix.clone()

  //     const item1Pos = new Vector3()
  //     const item1Quat = new Quaternion()
  //     item1Matrix.decompose(item1Pos, item1Quat, new Vector3())

  //     const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(item1Quat)

  //     const directionMultiplier =
  //       item1XAxis.dot(new Vector3(item.x - item1Pos.x, 0, item.z - item1Pos.z)) < 0 ? -1 : 1

  //     // Variables que pueden ser modificadas por la lógica especial cuando se pega a rinconeros
  //     let newPosition
  //     const newQuaternion = item1Quat.clone() // Por defecto usa la misma rotación

  //     // Obtener la posición actual del item para casos especiales (MAL sobre MBJ, MBJ bajo MAL)
  //     const itemCurrentPos = new Vector3()
  //     item.matrix.decompose(itemCurrentPos, new Quaternion(), new Vector3())

  //     // Detectar si item1 es KARB (arp, arb) - estos usan regleta directa, NO invertida
  //     const item1CatalogID = item1.catalogID?.toString() || ''
  //     const isKARB =
  //       item1CatalogID.toLowerCase().includes('arb') || item1CatalogID.toLowerCase().includes('arp')

  //     const item1RegletaMorph = item1.morphs?.find((m) => m.name === 'Regleta')
  //     const item1RegletaValue = item1.regleta || 0
  //     // Para KARB: usar regleta directa (NO invertir). Para otros: usar regleta invertida
  //     const item1RegletaInverted = getRegletaInvertedValue(item1RegletaValue, item1RegletaMorph)
  //     const item1RegletaRealValue = isKARB
  //       ? item1RegletaValue // KARB: regleta directa
  //       : item1RegletaInverted // Otros: regleta invertida

  //     // CASO ESPECIAL: MAL sobre MBJ/COL o MBJ/COL bajo MAL - preservar posición horizontal actual
  //     // COL se comporta igual que MBJ (forman parte del mismo grupo de muebles bajos)
  //     const isMALOverMBJ = item.tipoNormalizado === 'MAL' && item1.tipoNormalizado === 'MBJ'
  //     const isMALOverCOL = item.tipoNormalizado === 'MAL' && item1.tipoNormalizado === 'COL'
  //     const isMBJUnderMAL = item.tipoNormalizado === 'MBJ' && item1.tipoNormalizado === 'MAL'
  //     const isCOLUnderMAL = item.tipoNormalizado === 'COL' && item1.tipoNormalizado === 'MAL'

  //     if (isMALOverMBJ || isMALOverCOL || isMBJUnderMAL || isCOLUnderMAL) {
  //       // Para MAL sobre MBJ o MBJ bajo MAL: mantener la posición X y Z actual del item
  //       // Solo ajustaremos la Y después
  //       newPosition = itemCurrentPos.clone()
  //       console.log('[collideItems] Preservando posición horizontal para apilamiento vertical:', {
  //         itemID: itemId,
  //         item1ID: itemId1,
  //         itemTipo: item.tipoNormalizado,
  //         item1Tipo: item1.tipoNormalizado,
  //         currentX: itemCurrentPos.x,
  //         currentZ: itemCurrentPos.z,
  //         item1X: item1Pos.x,
  //         item1Z: item1Pos.z,
  //       })
  //     } else if (isRinconeroDerecha && directionMultiplier > 0) {
  //       newPosition = item1Pos
  //         .clone()
  //         .add(item1XAxis.multiplyScalar(directionMultiplier * item1Width))

  //       const rotationAdjustment = new Quaternion().setFromAxisAngle(
  //         new Vector3(0, 1, 0),
  //         -Math.PI / 2,
  //       )

  //       newQuaternion.multiply(rotationAdjustment)

  //       const KARB_OFFSET_ADJUSTMENT = isKARB ? -50 : 0
  //       const rinconeroOffset =
  //         (item1.thickness || 0) + item1RegletaRealValue + KARB_OFFSET_ADJUSTMENT

  //       const zAxis = new Vector3(0, 0, 1).applyQuaternion(item1Quat)
  //       newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
  //     }
  //     // CASO ESPECIAL: Mueble normal pegado a la izquierda de un rinconero izquierdo
  //     else if (isRinconeroIzquierda && directionMultiplier < 0) {
  //       newPosition = item1Pos.clone()

  //       const rotationAdjustment = new Quaternion().setFromAxisAngle(
  //         new Vector3(0, 1, 0),
  //         Math.PI / 2,
  //       )
  //       newQuaternion.multiply(rotationAdjustment)

  //       // Para KARB: usar regleta directa. Para otros: usar regleta invertida
  //       // KARB parece necesitar un offset adicional
  //       const KARB_OFFSET_ADJUSTMENT = isKARB ? -50 : 0
  //       const rinconeroOffset =
  //         (item1.thickness || 0) +
  //         item1RegletaRealValue +
  //         (item.width || 0) +
  //         KARB_OFFSET_ADJUSTMENT

  //       const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
  //       newPosition.add(zAxis.multiplyScalar(-rinconeroOffset))
  //     } else {
  //       newPosition = item1Pos
  //         .clone()
  //         .add(
  //           item1XAxis.multiplyScalar(
  //             directionMultiplier * (directionMultiplier < 0 ? itemWidth : item1Width),
  //           ),
  //         )
  //     }

  //     // Ajustar posición Y según tipoNormalizado
  //     // Si es MAL sobre MBJ/COL: colocar encima del MBJ/COL
  //     // Si es MBJ/COL bajo MAL: colocar debajo del MAL (misma Y base)
  //     // COL se comporta igual que MBJ (forman parte del mismo grupo de muebles bajos)
  //     const originalY = newPosition.y
  //     if (isMALOverMBJ || isMALOverCOL) {
  //       // MAL sobre MBJ/COL: posición Y = base del MBJ/COL + altura del MBJ/COL
  //       newPosition.y = item1Pos.y + (item1.height || 0)
  //       console.log('[collideItems] MAL sobre MBJ/COL - Ajustando Y:', {
  //         itemID: itemId,
  //         item1ID: itemId1,
  //         itemTipo: item.tipoNormalizado,
  //         item1Tipo: item1.tipoNormalizado,
  //         item1PosY: item1Pos.y,
  //         item1Height: item1.height,
  //         originalY,
  //         newY: newPosition.y,
  //         preservedX: newPosition.x,
  //         preservedZ: newPosition.z,
  //       })
  //     } else if (isMBJUnderMAL || isCOLUnderMAL) {
  //       // MBJ/COL bajo MAL: misma posición Y base que el MAL
  //       newPosition.y = item1Pos.y
  //       console.log('[collideItems] MBJ/COL bajo MAL - Ajustando Y:', {
  //         itemID: itemId,
  //         item1ID: itemId1,
  //         itemTipo: item.tipoNormalizado,
  //         item1Tipo: item1.tipoNormalizado,
  //         item1PosY: item1Pos.y,
  //         originalY,
  //         newY: newPosition.y,
  //         preservedX: newPosition.x,
  //         preservedZ: newPosition.z,
  //       })
  //     } else {
  //       // Caso normal: mantener la posición Y del item
  //       newPosition.y = item.y
  //     }

  //     const centerPosition = toVector3()
  //     const quaternion = toQuaternion()
  //     item.matrix.decompose(centerPosition, quaternion, new Vector3())

  //     const itemCenter = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
  //     itemCenter.applyQuaternion(quaternion).add(newPosition)

  //     const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(
  //       (isRinconeroDerecha && directionMultiplier > 0) ||
  //         (isRinconeroIzquierda && directionMultiplier < 0)
  //         ? newQuaternion
  //         : quaternion,
  //     )
  //     const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

  //     item.matrix.makeRotationFromQuaternion(
  //       (isRinconeroDerecha && directionMultiplier > 0) ||
  //         (isRinconeroIzquierda && directionMultiplier < 0)
  //         ? newQuaternion
  //         : item1Quat,
  //     )
  //     // Usar newPosition.y para MAL sobre MBJ y MBJ bajo MAL
  //     item.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)

  //     item.x = newPosition.x
  //     item.y = newPosition.y // Actualizar item.y para mantener consistencia
  //     item.z = newPosition.z

  //     const eulerRotation = new Euler().setFromQuaternion(
  //       (isRinconeroDerecha && directionMultiplier > 0) ||
  //         (isRinconeroIzquierda && directionMultiplier < 0)
  //         ? newQuaternion
  //         : item1Quat,
  //     )
  //     item.rotation = radToDeg(eulerRotation.y)

  //     store.sceneBoundingBoxes.set(itemId, {
  //       boundingBox: new OBB(
  //         itemCenter,
  //         toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
  //         rotationMatrix,
  //       ),
  //       type: 'items',
  //     })

  //     if (item1.snappedWall && !isRinconero) {
  //       const wall = store.items.get(item1.snappedWall) as Walls
  //       item.snappedWall = item1.snappedWall
  //       if (item.tipoNormalizado === 'MAL') {
  //         wall.highItems.add(itemId)
  //       }
  //       if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
  //         wall.lowItems.add(itemId)
  //       }
  //     }
  //   })
  // },
  applyToAllItems: (originalID: string) => {
    set((store) => {
      const items = store.items
      const itemOriginal = items.get(originalID)

      if (!itemOriginal || !originalID) {
        console.error('No se encontró el item original o el ID original es inválido.')
        return
      }

      items.forEach((value, key) => {
        if (value?.idUnidades === itemOriginal.idUnidades && key !== originalID) {
          const originalZones = itemOriginal.zones
          const targetZones = value.zones

          const originalTextures: Textures = itemOriginal.textures
          const targetTextures: Textures = value.textures

          if (itemOriginal.type === 'walls' && value.type === 'partition') {
            Object.keys(originalZones).forEach((zoneName) => {
              if (targetZones[zoneName]) {
                targetZones[zoneName] = { ...originalZones[zoneName] }
              }
            })

            const updatedTextures: Textures = targetTextures.map((targetTexture) => {
              const originalTexture = originalTextures.find(
                (t) => t.zoneName === targetTexture.zoneName,
              )
              return originalTexture ? { ...targetTexture, ...originalTexture } : targetTexture
            })

            value.textures = updatedTextures
          } else {
            value.zones = { ...originalZones }

            const updatedTextures: Textures = targetTextures.map((targetTexture) => {
              const originalTexture = originalTextures.find(
                (t) => t.zoneName === targetTexture.zoneName,
              )
              return originalTexture ? { ...targetTexture, ...originalTexture } : targetTexture
            })

            value.textures = updatedTextures
          }
        }
      })
    })
  },
  getInitialAngleDegrees: (itemId: AnyID) => {
    const items = get().items
    const item = items.get(itemId)
    if (!item) {
      throw new Error(`Item with ID ${itemId} not found`)
    }

    const x = toVector3()
    const y = toVector3()
    const z = toVector3()

    // Extraer las bases de la matriz del item
    item.matrix.extractBasis(x, y, z)

    // Calcular el ángulo de rotación en el eje X
    const xRot = toVector3([1, 0, 0]).angleTo(x)

    // Calcular el producto cruzado
    const crossProduct = toVector3([1, 0, 0]).cross(x)

    // Calcular el ángulo inicial en grados
    const initialAngleDegrees = (degrees(xRot * -Math.sign(crossProduct.y)) + 360) % 360

    return initialAngleDegrees
  },
  getContiguousItems: () => {
    const items = get().items
    // Filtrar solo items y linkedItems que sean visibles, excluyendo frontales
    const validItems = Array.from(items.values()).filter((item) => {
      if (!isLinkedItem(item) || !item.visible) {
        return false
      }
      const catalog = item.catalogID?.toString() ?? ''
      const catalogLower = catalog.toLowerCase()
      return (
        catalog !== 'Frontal' && catalogLower !== 'frontal' && !catalogLower.includes('frontal')
      )
    }) as LinkedItem[]

    const isKBRItem = (item: LinkedItem) => item.catalogID?.toString().toUpperCase().includes('KBR')

    const kbrItems = validItems.filter(isKBRItem)
    const otherItems = validItems.filter((item) => !isKBRItem(item))

    // Separar ítems por tipoNormalizado para grupos homogéneos
    const malItems = otherItems.filter((item) => item.tipoNormalizado !== 'MBJ')
    const mbjItems = otherItems.filter((item) => item.tipoNormalizado !== 'MAL')

    const createGroups = (itemList: LinkedItem[]) => {
      const groups: (Item | LinkedItem)[][] = []
      const processed = new Set<string>()

      itemList.forEach((item) => {
        if (processed.has(item.id)) return

        const group = [item]
        processed.add(item.id)

        // Buscar todos los muebles contiguos a este grupo
        let foundNew = true
        while (foundNew) {
          foundNew = false

          itemList.forEach((candidateItem) => {
            if (processed.has(candidateItem.id)) return

            // Verificar si el candidato es contiguo a algún mueble del grupo
            const isContiguousToGroup = group.some((groupItem) =>
              areContiguous(groupItem, candidateItem),
            )

            if (isContiguousToGroup) {
              group.push(candidateItem)
              processed.add(candidateItem.id)
              foundNew = true
            }
          })
        }

        // Incluir todos los grupos, incluso los de un solo elemento
        groups.push(group)
      })

      return groups
    }

    let totalDistancesBetweenGroups = 0
    // Función para verificar si dos muebles son contiguos
    const areContiguous = (item1: LinkedItem, item2: LinkedItem) => {
      // Código existente para la detección de contiguidad...
      // Extraer posiciones de la matriz para mayor precisión
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      const height1 = item1.height
      const height2 = item2.height

      // Verificar si están a la misma altura (tolerancia pequeña para flotantes)
      const verticalDistance = Math.abs(pos1.y - pos2.y)

      // Si la distancia vertical es mayor que la altura de cualquiera de los dos items, no son contiguos
      const maxHeight = Math.max(height1, height2)
      if (verticalDistance > maxHeight) {
        return false
      }

      const getVertices = (item: LinkedItem, pos: Vector3) => {
        const width = item.width
        const thickness = item.thickness + item.regleta || 0
        const angle = radians(get().getInitialAngleDegrees(item.id))
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const p = { x: pos.x, z: pos.z }

        // Vértices relativos al punto de pivote (asumido en p)
        const v = [
          { x: 0, z: 0 },
          { x: width, z: 0 },
          { x: width, z: thickness },
          { x: 0, z: thickness },
        ]

        // Rotar y trasladar vértices
        return v.map((vertex) => ({
          x: p.x + vertex.x * cos - vertex.z * sin,
          z: p.z + vertex.x * sin + vertex.z * cos,
        }))
      }

      // Resto del código de detección de contiguidad...
      const vertices1 = getVertices(item1, pos1)
      const vertices2 = getVertices(item2, pos2)

      const segments1 = [
        { p1: vertices1[0], p2: vertices1[1] },
        { p1: vertices1[1], p2: vertices1[2] },
        { p1: vertices1[2], p2: vertices1[3] },
        { p1: vertices1[3], p2: vertices1[0] },
      ]

      const segments2 = [
        { p1: vertices2[0], p2: vertices2[1] },
        { p1: vertices2[1], p2: vertices2[2] },
        { p1: vertices2[2], p2: vertices2[3] },
        { p1: vertices2[3], p2: vertices2[0] },
      ]

      const distSq = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2

      const distToSegmentSq = (p, v, w) => {
        const l2 = distSq(v, w)
        if (l2 === 0) return distSq(p, v)
        let t = ((p.x - v.x) * (w.x - v.x) + (p.z - v.z) * (w.z - v.z)) / l2
        t = Math.max(0, Math.min(1, t))
        return distSq(p, { x: v.x + t * (w.x - v.x), z: v.z + t * (w.z - v.z) })
      }

      const segmentDistance = (seg1, seg2) => {
        const d1 = distToSegmentSq(seg1.p1, seg2.p1, seg2.p2)
        const d2 = distToSegmentSq(seg1.p2, seg2.p1, seg2.p2)
        const d3 = distToSegmentSq(seg2.p1, seg1.p1, seg1.p2)
        const d4 = distToSegmentSq(seg2.p2, seg1.p1, seg1.p2)
        return Math.sqrt(Math.min(d1, d2, d3, d4))
      }

      const maxDistance = item1.regleta ? (item1.regleta || 0) + 10 : 30

      for (const seg1 of segments1) {
        for (const seg2 of segments2) {
          if (segmentDistance(seg1, seg2) < maxDistance) {
            // console.log(segmentDistance(seg1, seg2))
            totalDistancesBetweenGroups += segmentDistance(seg1, seg2)
            return true
          }
        }
      }

      return false
    }

    // Crear grupos para MAL y otros por separado
    const malGroups = createGroups(malItems)
    const mbjGroups = createGroups(mbjItems)

    const kbrGroups = kbrItems.map((item) => [item])
    const otherGroups = malGroups.concat(mbjGroups)

    // Combinar grupos de ambas listas
    const allGroupsRaw = [...kbrGroups, ...otherGroups]

    // Ordenar cada grupo según contenga o no rinconeros
    const sortedGroups = allGroupsRaw.map((group) => {
      // Para grupos de un solo elemento, no hay necesidad de ordenar
      if (group.length <= 1) {
        return group.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
          isKBR: isKBRItem(item),
        }))
      }

      // Verificar si hay un rinconero en el grupo
      const hasRinconero = group.some((item) => {
        const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
        return (
          item.catalogID &&
          rinconeroPatterns.some((pattern) =>
            item.catalogID.toString().toLowerCase().includes(pattern),
          )
        )
      })

      // Si hay rinconeros, usar la lógica de ordenamiento actual (antihorario alrededor del centro)
      if (hasRinconero) {
        // Extraer posiciones y rotaciones para análisis
        const itemPositions = group.map((item) => {
          const pos = new Vector3()
          const quat = new Quaternion()
          const scale = new Vector3()
          item.matrix.decompose(pos, quat, scale)
          return {
            id: item.id,
            position: pos,
            rotation: get().getInitialAngleDegrees(item.id),
          }
        })

        // Calcular el centro del grupo
        const center = new Vector3()
        itemPositions.forEach((item) => {
          center.add(item.position)
        })
        center.divideScalar(itemPositions.length)

        // Determinar la orientación principal del grupo
        const avgRotation =
          group.reduce((sum, item) => {
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees
          }, 0) / group.length

        const normalizedAngle = ((avgRotation % 360) + 360) % 360

        // Ordenar elementos considerando la orientación de la pared
        const sortedItems = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          const quatA = new Quaternion()
          const quatB = new Quaternion()
          const scaleA = new Vector3()
          const scaleB = new Vector3()

          a.matrix.decompose(posA, quatA, scaleA)
          b.matrix.decompose(posB, quatB, scaleB)

          // Calcular ángulos polares respecto al centro
          const angleA = Math.atan2(posA.z - center.z, posA.x - center.x)
          const angleB = Math.atan2(posB.z - center.z, posB.x - center.x)

          // Invertir el orden de comparación según la pared
          if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Pared norte (90°)
            return angleA - angleB // Invertir comparación para ordenar correctamente
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Pared sur (270°)
            return angleB - angleA // Invertir comparación para ordenar correctamente
          } else {
            return angleA - angleB // Mantener ordenamiento para paredes este/oeste
          }
        })
        // Mapear a formato de resultado
        return sortedItems.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
          isKBR: isKBRItem(item),
        }))
      } else {
        // Calcular el "ángulo" promedio del grupo para determinar la dirección de ordenamiento
        const avgRotation =
          group.reduce((sum, item) => {
            // Usamos la función que ya calcula el ángulo de forma consistente
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees // Acumulamos en grados
          }, 0) / group.length

        // Ordenar por proyección en el vector derecha según la orientación promedio
        const sortedGroup = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          a.matrix.decompose(posA, new Quaternion(), new Vector3())
          b.matrix.decompose(posB, new Quaternion(), new Vector3())

          const normalizedAngle = ((avgRotation % 360) + 360) % 360

          // Usar rangos de ángulos para determinar el eje de ordenación
          if (normalizedAngle > 315 || normalizedAngle <= 45) {
            // Cerca de 0°: Muebles alineados con el eje X. Ordenar por X ascendente.
            return posA.x - posB.x
          } else if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Cerca de 90°: Muebles alineados con el eje Z. Ordenar por Z ascendente.
            return posA.z - posB.z
          } else if (normalizedAngle > 135 && normalizedAngle <= 225) {
            // Cerca de 180°: Muebles alineados con el eje X. Ordenar por X descendente.
            return posB.x - posA.x
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Cerca de 270°: Muebles alineados con el eje Z. Ordenar por Z descendente.
            return posB.z - posA.z
          }

          // Fallback para cualquier caso no cubierto
          const fallbackRightVector = new Vector3(0, 0, 1)
          fallbackRightVector.applyAxisAngle(new Vector3(0, 1, 0), avgRotation * (Math.PI / 180))
          const projectionA = posA.dot(fallbackRightVector)
          const projectionB = posB.dot(fallbackRightVector)
          return projectionA - projectionB
        })

        return sortedGroup.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
          isKBR: isKBRItem(item),
        }))
      }
    })

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
      totalDistancesBetweenGroups,
    }
  },
  getContiguousItemsForLaterals: () => {
    const items = get().items
    // Filtrar solo items y linkedItems que sean visibles, excluyendo frontales
    const validItems = Array.from(items.values()).filter(
      (item) =>
        isLinkedItem(item) &&
        item.visible &&
        item.catalogID !== 'Frontal' &&
        item.catalogID?.toString() !== 'Frontal',
    ) as LinkedItem[]

    // Separar ítems por tipoNormalizado para grupos homogéneos
    const malItems = validItems.filter((item) => item.tipoNormalizado !== 'MBJ')
    const mbjItems = validItems.filter((item) => item.tipoNormalizado !== 'MAL')

    const createGroups = (itemList: LinkedItem[]) => {
      const groups: (Item | LinkedItem)[][] = []
      const processed = new Set<string>()

      itemList.forEach((item) => {
        if (processed.has(item.id)) return

        const group = [item]
        processed.add(item.id)

        // Buscar todos los muebles contiguos a este grupo
        let foundNew = true
        while (foundNew) {
          foundNew = false

          itemList.forEach((candidateItem) => {
            if (processed.has(candidateItem.id)) return

            // Verificar si el candidato es contiguo a algún mueble del grupo
            const isContiguousToGroup = group.some((groupItem) =>
              areContiguous(groupItem, candidateItem),
            )

            if (isContiguousToGroup) {
              group.push(candidateItem)
              processed.add(candidateItem.id)
              foundNew = true
            }
          })
        }

        // Incluir todos los grupos, incluso los de un solo elemento
        groups.push(group)
      })

      return groups
    }

    // Función para verificar si dos muebles son contiguos
    const areContiguous = (item1: LinkedItem, item2: LinkedItem) => {
      // Código existente para la detección de contiguidad...
      // Extraer posiciones de la matriz para mayor precisión
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      const height1 = item1.height
      const height2 = item2.height

      // Verificar si están a la misma altura (tolerancia pequeña para flotantes)
      const verticalDistance = Math.abs(pos1.y - pos2.y)

      // Si la distancia vertical es mayor que la altura de cualquiera de los dos items, no son contiguos
      const maxHeight = Math.max(height1, height2)
      if (verticalDistance > maxHeight) {
        return false
      }

      const getVertices = (item: LinkedItem, pos: Vector3) => {
        const width = item.width
        const thickness = item.thickness + item.regleta || 0
        const angle = radians(get().getInitialAngleDegrees(item.id))
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const p = { x: pos.x, z: pos.z }

        // Vértices relativos al punto de pivote (asumido en p)
        const v = [
          { x: 0, z: 0 },
          { x: width, z: 0 },
          { x: width, z: thickness },
          { x: 0, z: thickness },
        ]

        // Rotar y trasladar vértices
        return v.map((vertex) => ({
          x: p.x + vertex.x * cos - vertex.z * sin,
          z: p.z + vertex.x * sin + vertex.z * cos,
        }))
      }

      // Resto del código de detección de contiguidad...
      const vertices1 = getVertices(item1, pos1)
      const vertices2 = getVertices(item2, pos2)

      const segments1 = [
        { p1: vertices1[0], p2: vertices1[1] },
        { p1: vertices1[1], p2: vertices1[2] },
        { p1: vertices1[2], p2: vertices1[3] },
        { p1: vertices1[3], p2: vertices1[0] },
      ]

      const segments2 = [
        { p1: vertices2[0], p2: vertices2[1] },
        { p1: vertices2[1], p2: vertices2[2] },
        { p1: vertices2[2], p2: vertices2[3] },
        { p1: vertices2[3], p2: vertices2[0] },
      ]

      const distSq = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2

      const distToSegmentSq = (p, v, w) => {
        const l2 = distSq(v, w)
        if (l2 === 0) return distSq(p, v)
        let t = ((p.x - v.x) * (w.x - v.x) + (p.z - v.z) * (w.z - v.z)) / l2
        t = Math.max(0, Math.min(1, t))
        return distSq(p, { x: v.x + t * (w.x - v.x), z: v.z + t * (w.z - v.z) })
      }

      const segmentDistance = (seg1, seg2) => {
        const d1 = distToSegmentSq(seg1.p1, seg2.p1, seg2.p2)
        const d2 = distToSegmentSq(seg1.p2, seg2.p1, seg2.p2)
        const d3 = distToSegmentSq(seg2.p1, seg1.p1, seg1.p2)
        const d4 = distToSegmentSq(seg2.p2, seg1.p1, seg1.p2)
        return Math.sqrt(Math.min(d1, d2, d3, d4))
      }

      for (const seg1 of segments1) {
        for (const seg2 of segments2) {
          if (segmentDistance(seg1, seg2) < 10) {
            // console.log(segmentDistance(seg1, seg2))
            return true
          }
        }
      }

      return false
    }

    // Crear grupos para MAL y otros por separado
    const malGroups = createGroups(malItems)
    const mbjGroups = createGroups(mbjItems)

    // Combinar grupos de ambas listas
    const allGroupsRaw = malGroups.concat(mbjGroups)

    // Ordenar cada grupo según contenga o no rinconeros
    const sortedGroups = allGroupsRaw.map((group) => {
      // Para grupos de un solo elemento, no hay necesidad de ordenar
      if (group.length <= 1) {
        return group.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }

      // Verificar si hay un rinconero en el grupo
      const hasRinconero = group.some((item) => {
        const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
        return (
          item.catalogID &&
          rinconeroPatterns.some((pattern) =>
            item.catalogID.toString().toLowerCase().includes(pattern),
          )
        )
      })

      // Si hay rinconeros, usar la lógica de ordenamiento actual (antihorario alrededor del centro)
      if (hasRinconero) {
        // Extraer posiciones y rotaciones para análisis
        const itemPositions = group.map((item) => {
          const pos = new Vector3()
          const quat = new Quaternion()
          const scale = new Vector3()
          item.matrix.decompose(pos, quat, scale)
          return {
            id: item.id,
            position: pos,
            rotation: get().getInitialAngleDegrees(item.id),
          }
        })

        // Calcular el centro del grupo
        const center = new Vector3()
        itemPositions.forEach((item) => {
          center.add(item.position)
        })
        center.divideScalar(itemPositions.length)

        // Determinar la orientación principal del grupo
        const avgRotation =
          group.reduce((sum, item) => {
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees
          }, 0) / group.length

        const normalizedAngle = ((avgRotation % 360) + 360) % 360

        // Ordenar elementos considerando la orientación de la pared
        const sortedItems = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          const quatA = new Quaternion()
          const quatB = new Quaternion()
          const scaleA = new Vector3()
          const scaleB = new Vector3()

          a.matrix.decompose(posA, quatA, scaleA)
          b.matrix.decompose(posB, quatB, scaleB)

          // Calcular ángulos polares respecto al centro
          const angleA = Math.atan2(posA.z - center.z, posA.x - center.x)
          const angleB = Math.atan2(posB.z - center.z, posB.x - center.x)

          // Invertir el orden de comparación según la pared
          if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Pared norte (90°)
            return angleA - angleB // Invertir comparación para ordenar correctamente
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Pared sur (270°)
            return angleB - angleA // Invertir comparación para ordenar correctamente
          } else {
            return angleA - angleB // Mantener ordenamiento para paredes este/oeste
          }
        })
        // Mapear a formato de resultado
        return sortedItems.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      } else {
        // Calcular el "ángulo" promedio del grupo para determinar la dirección de ordenamiento
        const avgRotation =
          group.reduce((sum, item) => {
            // Usamos la función que ya calcula el ángulo de forma consistente
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees // Acumulamos en grados
          }, 0) / group.length

        // Ordenar por proyección en el vector derecha según la orientación promedio
        const sortedGroup = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          a.matrix.decompose(posA, new Quaternion(), new Vector3())
          b.matrix.decompose(posB, new Quaternion(), new Vector3())

          const normalizedAngle = ((avgRotation % 360) + 360) % 360

          // Usar rangos de ángulos para determinar el eje de ordenación
          if (normalizedAngle > 315 || normalizedAngle <= 45) {
            // Cerca de 0°: Muebles alineados con el eje X. Ordenar por X ascendente.
            return posA.x - posB.x
          } else if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Cerca de 90°: Muebles alineados con el eje Z. Ordenar por Z ascendente.
            return posA.z - posB.z
          } else if (normalizedAngle > 135 && normalizedAngle <= 225) {
            // Cerca de 180°: Muebles alineados con el eje X. Ordenar por X descendente.
            return posB.x - posA.x
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Cerca de 270°: Muebles alineados con el eje Z. Ordenar por Z descendente.
            return posB.z - posA.z
          }

          // Fallback para cualquier caso no cubierto
          const fallbackRightVector = new Vector3(0, 0, 1)
          fallbackRightVector.applyAxisAngle(new Vector3(0, 1, 0), avgRotation * (Math.PI / 180))
          const projectionA = posA.dot(fallbackRightVector)
          const projectionB = posB.dot(fallbackRightVector)
          return projectionA - projectionB
        })

        return sortedGroup.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }
    })

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getContItemsForEncimeras: () => {
    const items = get().items
    // Filtrar solo items y linkedItems que sean visibles
    const validItems = Array.from(items.values()).filter(
      (item) => isLinkedItem(item) && item.visible && item.tipoNormalizado !== 'MAL',
    ) as LinkedItem[]

    // Función para verificar si dos muebles son contiguos
    const areContiguous = (item1: LinkedItem, item2: LinkedItem) => {
      // IMPORTANTE: Solo considerar contiguos items del mismo tipoNormalizado
      // MBJ solo con MBJ, MAL solo con MAL, COL solo con COL
      // Esto evita que se agrupen items de diferentes alturas (bajos con altos)
      if (item1.tipoNormalizado !== item2.tipoNormalizado) {
        return false
      }

      // Extraer posiciones de la matriz para mayor precisión
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      // console.log(`Verificando contiguidad entre ${item1.id} y ${item2.id}`)
      // console.log(
      //   `Posición item1: x=${pos1.x}, z=${pos1.z}, rotación=${get().getInitialAngleDegrees(item1.id)}`,
      // )
      // console.log(
      //   `Posición item2: x=${pos2.x}, z=${pos2.z}, rotación=${get().getInitialAngleDegrees(item2.id)}`,
      // )

      const height1 = item1.height
      const height2 = item2.height

      // Verificar si están a la misma altura (tolerancia pequeña para flotantes)
      const verticalDistance = Math.abs(pos1.y - pos2.y)

      // Si la distancia vertical es mayor que la altura de cualquiera de los dos items, no son contiguos
      const maxHeight = Math.max(height1, height2)
      if (verticalDistance > maxHeight) {
        // console.log(`No contiguos: distancia vertical ${verticalDistance} > maxHeight ${maxHeight}`)
        return false
      }

      const getVertices = (item: LinkedItem, pos: Vector3) => {
        const width = item.width
        const thickness = item.thickness + (item.regleta || 0)
        const angle = radians(get().getInitialAngleDegrees(item.id))
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const p = { x: pos.x, z: pos.z }

        // Vértices relativos al punto de pivote (asumido en p)
        const v = [
          { x: 0, z: 0 },
          { x: width, z: 0 },
          { x: width, z: thickness },
          { x: 0, z: thickness },
        ]

        // Rotar y trasladar vértices
        return v.map((vertex) => ({
          x: p.x + vertex.x * cos - vertex.z * sin,
          z: p.z + vertex.x * sin + vertex.z * cos,
        }))
      }

      // Resto del código de detección de contiguidad...
      const vertices1 = getVertices(item1, pos1)
      const vertices2 = getVertices(item2, pos2)

      // console.log('Vértices item1:', vertices1)
      // console.log('Vértices item2:', vertices2)

      const segments1 = [
        { p1: vertices1[0], p2: vertices1[1] },
        { p1: vertices1[1], p2: vertices1[2] },
        { p1: vertices1[2], p2: vertices1[3] },
        { p1: vertices1[3], p2: vertices1[0] },
      ]

      const segments2 = [
        { p1: vertices2[0], p2: vertices2[1] },
        { p1: vertices2[1], p2: vertices2[2] },
        { p1: vertices2[2], p2: vertices2[3] },
        { p1: vertices2[3], p2: vertices2[0] },
      ]

      const distSq = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2

      const distToSegmentSq = (p, v, w) => {
        const l2 = distSq(v, w)
        if (l2 === 0) return distSq(p, v)
        let t = ((p.x - v.x) * (w.x - v.x) + (p.z - v.z) * (w.z - v.z)) / l2
        t = Math.max(0, Math.min(1, t))
        return distSq(p, { x: v.x + t * (w.x - v.x), z: v.z + t * (w.z - v.z) })
      }

      const segmentDistance = (seg1, seg2) => {
        const d1 = distToSegmentSq(seg1.p1, seg2.p1, seg2.p2)
        const d2 = distToSegmentSq(seg1.p2, seg2.p1, seg2.p2)
        const d3 = distToSegmentSq(seg2.p1, seg1.p1, seg1.p2)
        const d4 = distToSegmentSq(seg2.p2, seg1.p1, seg1.p2)
        return Math.sqrt(Math.min(d1, d2, d3, d4))
      }

      let minDistance = Infinity
      for (const seg1 of segments1) {
        for (const seg2 of segments2) {
          const dist = segmentDistance(seg1, seg2)
          minDistance = Math.min(minDistance, dist)
        }
      }

      // Usar un umbral más preciso (5mm) para detectar contigüidad real
      // Esto evita falsos positivos cuando hay espacios pequeños
      const TOLERANCE = 5 // Margen de error para considerar contiguos
      const isContiguous = minDistance <= TOLERANCE
      return isContiguous
    }

    // Crear grupos de muebles contiguos
    const groups: (Item | LinkedItem)[][] = []
    const processed = new Set<string>()

    validItems.forEach((item) => {
      if (processed.has(item.id)) return

      const group = [item]
      processed.add(item.id)

      // Buscar todos los muebles contiguos a este grupo
      let foundNew = true
      while (foundNew) {
        foundNew = false

        validItems.forEach((candidateItem) => {
          if (processed.has(candidateItem.id)) return

          // Verificar si el candidato es contiguo a algún mueble del grupo
          const isContiguousToGroup = group.some((groupItem) =>
            areContiguous(groupItem, candidateItem),
          )

          if (isContiguousToGroup) {
            group.push(candidateItem)
            processed.add(candidateItem.id)
            foundNew = true
          }
        })
      }

      // Incluir todos los grupos, incluso los de un solo elemento
      groups.push(group)
    })

    // Ordenar cada grupo según contenga o no rinconeros
    const sortedGroups = groups.map((group) => {
      // Para grupos de un solo elemento, no hay necesidad de ordenar
      if (group.length <= 1) {
        return group.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }

      // Verificar si hay un rinconero en el grupo
      const hasRinconero = group.some((item) => {
        const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
        return (
          item.catalogID &&
          rinconeroPatterns.some((pattern) =>
            item.catalogID.toString().toLowerCase().includes(pattern),
          )
        )
      })

      // Si hay rinconeros, usar la lógica de ordenamiento actual (antihorario alrededor del centro)
      if (hasRinconero) {
        // Extraer posiciones y rotaciones para análisis
        const itemPositions = group.map((item) => {
          const pos = new Vector3()
          const quat = new Quaternion()
          const scale = new Vector3()
          item.matrix.decompose(pos, quat, scale)
          return {
            id: item.id,
            position: pos,
            rotation: get().getInitialAngleDegrees(item.id),
          }
        })

        // Calcular el centro del grupo
        const center = new Vector3()
        itemPositions.forEach((item) => {
          center.add(item.position)
        })
        center.divideScalar(itemPositions.length)

        // Determinar la orientación principal del grupo
        const avgRotation =
          group.reduce((sum, item) => {
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees
          }, 0) / group.length

        const normalizedAngle = ((avgRotation % 360) + 360) % 360

        // Ordenar elementos considerando la orientación de la pared
        const sortedItems = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          const quatA = new Quaternion()
          const quatB = new Quaternion()
          const scaleA = new Vector3()
          const scaleB = new Vector3()

          a.matrix.decompose(posA, quatA, scaleA)
          b.matrix.decompose(posB, quatB, scaleB)

          // Calcular ángulos polares respecto al centro
          const angleA = Math.atan2(posA.z - center.z, posA.x - center.x)
          const angleB = Math.atan2(posB.z - center.z, posB.x - center.x)

          // Invertir el orden de comparación según la pared
          if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Pared norte (90°)
            return angleA - angleB // Invertir comparación para ordenar correctamente
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Pared sur (270°)
            return angleB - angleA // Invertir comparación para ordenar correctamente
          } else {
            return angleA - angleB // Mantener ordenamiento para paredes este/oeste
          }
        })
        // Mapear a formato de resultado
        return sortedItems.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      } else {
        // Calcular el "ángulo" promedio del grupo para determinar la dirección de ordenamiento
        const avgRotation =
          group.reduce((sum, item) => {
            // Usamos la función que ya calcula el ángulo de forma consistente
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees // Acumulamos en grados
          }, 0) / group.length

        // Verificar si todos los muebles están en la misma pared
        const items = get().items
        const firstItem = items.get(group[0].id) as Item | LinkedItem
        const commonWall = firstItem?.snappedWall

        let sortedGroup: typeof group

        // Si todos están en la misma pared, ordenar según posición a lo largo de la pared
        if (
          commonWall &&
          group.every((item) => {
            const itemData = items.get(item.id) as Item | LinkedItem
            return itemData?.snappedWall === commonWall
          })
        ) {
          const wall = items.get(commonWall) as Walls
          if (wall) {
            const start = toVector3(wall.start)
            const end = toVector3(wall.end)
            const wallDirection = end.clone().sub(start)
            const wallDirNormalized = wallDirection.clone().normalize()

            sortedGroup = group.sort((a, b) => {
              const posA = new Vector3()
              const posB = new Vector3()
              a.matrix.decompose(posA, new Quaternion(), new Vector3())
              b.matrix.decompose(posB, new Quaternion(), new Vector3())

              // Proyectar posiciones en la dirección de la pared
              const itemAToStart = posA.clone().sub(start)
              const itemBToStart = posB.clone().sub(start)
              const alongWallA = Math.max(0, itemAToStart.dot(wallDirNormalized))
              const alongWallB = Math.max(0, itemBToStart.dot(wallDirNormalized))

              return alongWallA - alongWallB
            })
          } else {
            // Si no se encuentra la pared, usar ordenamiento por proyección en la dirección de la pared
            sortedGroup = group.sort((a, b) => {
              const posA = new Vector3()
              const posB = new Vector3()
              const quatA = new Quaternion()
              const quatB = new Quaternion()

              a.matrix.decompose(posA, quatA, new Vector3())
              b.matrix.decompose(posB, quatB, new Vector3())

              // Obtener la dirección de la pared del primer item
              const angleA = get().getInitialAngleDegrees(a.id)
              const radA = radians(angleA)

              // Calcular vector director de la pared (normalizado)
              const wallDir = new Vector3(Math.cos(radA), 0, Math.sin(radA))

              // Proyectar posiciones en la dirección de la pared
              const projA = posA.dot(wallDir)
              const projB = posB.dot(wallDir)

              // Ordenar por proyección a lo largo de la pared
              return projA - projB
            })
          }
        } else {
          // Si no están todos en la misma pared, usar ordenamiento por coordenadas absolutas
          sortedGroup = group.sort((a, b) => {
            const posA = new Vector3()
            const posB = new Vector3()
            a.matrix.decompose(posA, new Quaternion(), new Vector3())
            b.matrix.decompose(posB, new Quaternion(), new Vector3())

            const normalizedAngle = ((avgRotation % 360) + 360) % 360

            // Usar rangos de ángulos para determinar el eje de ordenación
            if (normalizedAngle > 315 || normalizedAngle <= 45) {
              // Cerca de 0°: Muebles alineados con el eje X. Ordenar por X ascendente.
              return posA.x - posB.x
            } else if (normalizedAngle > 45 && normalizedAngle <= 135) {
              // Cerca de 90°: Muebles alineados con el eje Z. Ordenar por Z ascendente.
              return posA.z - posB.z
            } else if (normalizedAngle > 135 && normalizedAngle <= 225) {
              // Cerca de 180°: Muebles alineados con el eje X. Ordenar por X descendente.
              return posB.x - posA.x
            } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
              // Cerca de 270°: Muebles alineados con el eje Z. Ordenar por Z descendente.
              return posB.z - posA.z
            }

            // Fallback para cualquier caso no cubierto
            const fallbackRightVector = new Vector3(0, 0, 1)
            fallbackRightVector.applyAxisAngle(new Vector3(0, 1, 0), avgRotation * (Math.PI / 180))
            const projectionA = posA.dot(fallbackRightVector)
            const projectionB = posB.dot(fallbackRightVector)
            return projectionA - projectionB
          })
        }

        return sortedGroup.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }
    })

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getFirstItemPositionsOfSnappedGroups: () => {
    const contiguousResult = get().getContiguousItems()
    const items = get().items

    // Array para almacenar las posiciones de los primeros items de grupos (con o sin pared)
    const firstItemPositions: Array<{
      groupIndex: number
      itemId: string
      position: { x: number; y: number; z: number }
      snappedWall: string
      catalogID: string
      groupTotalWidth: number
      matrix: Matrix4
      rotation: number
    }> = []

    contiguousResult.groups.forEach((group, groupIndex) => {
      if (group.length === 0) {
        return
      }

      // Calcular el ancho total del grupo sumando todos los anchos
      const groupTotalWidth = group.reduce((totalWidth, groupItem) => {
        return totalWidth + groupItem.width
      }, 0)

      // Obtener el primer item del grupo (ya están ordenados)
      const firstGroupItem = group[0]
      const firstItem = items.get(firstGroupItem.id)

      if (!firstItem) {
        return
      }

      if (isItem(firstItem) || isLinkedItem(firstItem)) {
        // Procesar tanto grupos con pared como sin pared
        // Si tiene snappedWall, usarlo; si no, usar cadena vacía
        const snappedWall =
          firstItem.snappedWall && firstItem.snappedWall !== '' ? firstItem.snappedWall : ''

        firstItemPositions.push({
          groupIndex,
          itemId: firstGroupItem.id,
          position: firstGroupItem.position,
          snappedWall: snappedWall,
          catalogID: String(firstGroupItem.catalogID),
          groupTotalWidth,
          matrix: firstItem.matrix.clone(),
          rotation: firstGroupItem.rotation,
        })
      }
    })
    return firstItemPositions
  },
  deleteFrontals: () => {
    const itemsToDelete: AnyID[] = []
    get().items.forEach((item, id) => {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal') {
        itemsToDelete.push(id)
      }
    })

    itemsToDelete.forEach((id) => {
      set((store) => {
        store.items.delete(id)
        store.layers[store.currentLayer].items.delete(id)
        store.layers[store.currentLayer].selected.delete(id)
        store.sceneBoundingBoxes.delete(id)
        store.collisionableItems.delete(id)
      })
    })

    // Actualizar el estado frontalActive cuando se eliminan todos los frontales
    if (itemsToDelete.length > 0) {
      const remainingFrontals = Array.from(get().items.values()).filter(
        (item) => (isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal',
      )
      if (remainingFrontals.length === 0) {
        set((store) => {
          store.frontalActive = false
        })
      }
    }

    return itemsToDelete.length
  },
  drawFrontalsFromSnappedGroups: async (height: number, options?: { force?: boolean }) => {
    const autoEnabled = options?.force ? true : (get().frontalsAutoEnabled ?? true)
    if (!autoEnabled) {
      return
    }
    const isDrawingFrontals = get().isDrawingFrontals
    if (isDrawingFrontals) {
      return
    }

    set((store) => {
      store.isDrawingFrontals = true
    })

    try {
      get().deleteFrontals()

      await new Promise((resolve) => setTimeout(resolve, 100))

      const unitsWithSuffixes = mapUnitsWithSuffixes(get().items)

      const contiguousResult = get().getContiguousItems()
      const items = get().items as Map<AnyID, LinkedItem>
      const frontalPositions = get().getFirstItemPositionsOfSnappedGroups()

      // Función helper para obtener el grosor de la encimera basado en el acabado
      const getEncimeraThickness = (encimera: any): number => {
        if (!encimera) {
          return 0
        }

        const acabado = encimera.textures?.[0]?.acabado
        if (acabado) {
          const acabadoLower = acabado.toLowerCase()
          if (acabadoLower.includes('porcelanicas 12')) {
            return 12
          } else if (acabadoLower.includes('porcelanicas 20')) {
            return 20
          } else if (acabadoLower.includes('postformadas')) {
            return 30
          } else if (acabadoLower.includes('compacto')) {
            return 10
          }
        }

        // Si no hay acabado específico, usar el height de la encimera
        return encimera.height ?? 0
      }

      // Función helper para encontrar la encimera asociada a un grupo de unidades
      const findEncimeraForGroup = (group: any[]): any => {
        for (const groupItem of group) {
          const linkedItem = items.get(groupItem.id) as LinkedItem
          if (!linkedItem || linkedItem.type !== 'linkedItem') continue

          // Buscar encimera en los hijos de esta unidad
          if (linkedItem.childs) {
            for (const childId of linkedItem.childs) {
              const child = items.get(childId)
              if (
                child &&
                child.type === 'linkedSon' &&
                ((child as any).typeOfSon === 'Encimera' ||
                  child.description?.toLowerCase().includes('encimera'))
              ) {
                return child
              }
            }
          }
        }
        return null
      }

      for (const group of contiguousResult.groups) {
        const hasMBJ = group.some((item) => {
          const linkedItem = items.get(item.id) as LinkedItem
          return linkedItem && linkedItem.tipoNormalizado === 'MBJ'
        })

        if (!hasMBJ) {
          continue
        }

        const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
        const rinconeroIndex = group.findIndex((item) => {
          return (
            item.catalogID &&
            rinconeroPatterns.some((pattern) =>
              item.catalogID.toString().toLowerCase().includes(pattern),
            )
          )
        })

        if (rinconeroIndex !== -1) {
          const rinconero = group[rinconeroIndex]
          const rinconeroItem = items.get(rinconero.id)

          // Buscar encimera para este grupo (se reutiliza para ambos frontales)
          const encimera = findEncimeraForGroup(group)

          // Detectar si es rinconero derecho o izquierdo usando la misma lógica que collideItems
          const isRinconeroDerecha =
            rinconero.catalogID &&
            rinconeroPatterns.some((pattern) =>
              rinconero.catalogID.toString().toLowerCase().includes(pattern),
            ) &&
            !rinconero.catalogID.toString().toLowerCase().includes('i')
          const isRinconeroIzquierda =
            rinconero.catalogID &&
            rinconeroPatterns.some((pattern) =>
              rinconero.catalogID.toString().toLowerCase().includes(pattern),
            ) &&
            rinconero.catalogID.toString().toLowerCase().includes('i')

          // Obtener la orientación del rinconero
          const rinconeroRotation = get().getInitialAngleDegrees(rinconero.id)
          const normalizedRinconeroRotation = (Math.round(rinconeroRotation / 90) * 90) % 360

          // Dividir los elementos del grupo según su orientación
          const horizontalItems = []
          const verticalItems = []

          // Clasificar los elementos según estén alineados con el rinconero o perpendiculares
          group.forEach((item) => {
            const itemRotation = get().getInitialAngleDegrees(item.id)
            const normalizedItemRotation = (Math.round(itemRotation / 90) * 90) % 360

            // Si la rotación coincide con el rinconero o está 180° rotada (misma línea)
            if (
              normalizedItemRotation === normalizedRinconeroRotation ||
              normalizedItemRotation === (normalizedRinconeroRotation + 180) % 360
            ) {
              horizontalItems.push(item)
            }
            // Si está a 90° o 270° del rinconero (perpendicular)
            else if (
              normalizedItemRotation === (normalizedRinconeroRotation + 90) % 360 ||
              normalizedItemRotation === (normalizedRinconeroRotation + 270) % 360
            ) {
              verticalItems.push(item)
            }
          })

          // console.log(
          //   '[DEBUG] drawFrontalsFromSnappedGroupsNEW: Ítems horizontales:',
          //   horizontalItems.map((i) => i.id),
          //   'verticales:',
          //   verticalItems.map((i) => i.id),
          // )

          // Función helper para obtener regleta invertida (misma lógica que collideItems)
          const getRegletaInvertedValue = (
            value: number,
            morph: { max: number; min: number } | undefined,
          ) => {
            if (!morph) return value
            if (value >= morph.max) return morph.min
            if (value <= morph.min) return morph.max
            return morph.max + morph.min - value
          }

          // PRIMER FRONTAL: todos los elementos alineados con la primera dirección del rinconero
          // Calcular ancho total sumando SOLO anchos de elementos MBJ
          const widthFirstFrontal = horizontalItems.reduce((sum, item) => {
            const linkedItem = items.get(item.id) as LinkedItem
            if (linkedItem && linkedItem.tipoNormalizado === 'MBJ') {
              // Usar el valor por defecto del morph "Ancho" si existe, sino usar width
              const widthMorph = linkedItem.morphs?.find(
                (morph) => morph.name === 'Ancho' || morph.name === 'width',
              )
              const itemWidth = widthMorph?.default ?? widthMorph?._default ?? item.width
              return sum + itemWidth
            }
            return sum
          }, 0)

          // Encontrar el elemento más a la izquierda/derecha según la orientación para posicionar el frontal
          const rinconeroPos1 = new Vector3()
          const rinconeroQuat1 = new Quaternion()
          rinconeroItem.matrix.decompose(rinconeroPos1, rinconeroQuat1, new Vector3())

          // Ordenar elementos horizontales por posición para encontrar el primero de la línea
          horizontalItems.sort((a, b) => {
            const posA = new Vector3()
            const posB = new Vector3()
            items.get(a.id).matrix.decompose(posA, new Quaternion(), new Vector3())
            items.get(b.id).matrix.decompose(posB, new Quaternion(), new Vector3())

            // Proyectar sobre el eje X del rinconero para ordenarlos correctamente
            const directionVector = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat1)
            const projectionA = posA.clone().sub(rinconeroPos1).dot(directionVector)
            const projectionB = posB.clone().sub(rinconeroPos1).dot(directionVector)

            return projectionA - projectionB
          })

          // Usar la matriz del primer elemento ordenado para el frontal
          const firstAlignedItem = items.get(horizontalItems[0].id)
          const firstFrontalMatrix = firstAlignedItem.matrix.clone()

          // Ajustar posición Y del primer frontal basado en encimera
          if (encimera) {
            // Buscar la altura máxima de los items MBJ del grupo (excluyendo regletas)
            let maxUnitHeight = 0
            for (const groupItem of group) {
              const item = items.get(groupItem.id) as LinkedItem
              if (item && item.tipoNormalizado === 'MBJ') {
                const catalogIDStr = item.catalogID?.toString().toUpperCase() || ''
                const isRegleta =
                  catalogIDStr.startsWith('RB') ||
                  item.description?.toLowerCase().includes('regleta')

                if (!isRegleta && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            // Si no encontramos altura de unidad no-regleta, usar la máxima altura del grupo
            if (maxUnitHeight === 0) {
              for (const groupItem of group) {
                const item = items.get(groupItem.id) as LinkedItem
                if (item && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            const unitHeight = maxUnitHeight || (firstAlignedItem.height ?? 0)
            const encimeraThickness = getEncimeraThickness(encimera)
            const newY = unitHeight + encimeraThickness

            const currentPos = new Vector3()
            const quaternion = new Quaternion()
            const scale = new Vector3()
            firstFrontalMatrix.decompose(currentPos, quaternion, scale)
            firstFrontalMatrix.setPosition(currentPos.x, newY, currentPos.z)
          }

          const firstFrontalId = await get().drawItem(
            getId('linkedItem'),
            'Frontal',
            '',
            true,
            firstFrontalMatrix,
            'items',
          )

          // Configurar dimensiones del primer frontal
          get().editMorph(firstFrontalId, 'Ancho', widthFirstFrontal)
          get().editMorph(firstFrontalId, 'Alto', height)
          get().editItem(firstFrontalId, { width: widthFirstFrontal, height })
          get().setOBB(firstFrontalId)

          // SEGUNDO FRONTAL: perpendicular, desde la esquina del rinconero
          // Calcular ancho total sumando SOLO anchos de elementos MBJ
          const widthSecondFrontalNoRinconero = verticalItems.reduce((sum, item) => {
            const linkedItem = items.get(item.id) as LinkedItem
            if (linkedItem && linkedItem.tipoNormalizado === 'MBJ') {
              // Usar el valor por defecto del morph "Ancho" si existe, sino usar width
              const widthMorph = linkedItem.morphs?.find(
                (morph) => morph.name === 'Ancho' || morph.name === 'width',
              )
              const itemWidth = widthMorph?.default ?? widthMorph?._default ?? item.width
              return sum + itemWidth
            }
            return sum
          }, 0)

          // Ajustar ancho y offset según el tipo de rinconero usando la misma lógica que collideItems
          let widthSecondFrontal: number
          let secondFrontalMatrix: Matrix4
          let newPosition: Vector3
          let newQuaternion: Quaternion

          const rinconeroPos = new Vector3()
          const rinconeroQuat = new Quaternion()
          const rinconeroScale = new Vector3()
          rinconeroItem.matrix.decompose(rinconeroPos, rinconeroQuat, rinconeroScale)

          if (isRinconeroDerecha) {
            // RINCONERO DERECHO: Calcular posición manual desde el borde derecho del rinconero

            // Ancho del frontal = items verticales + thickness + regleta del rinconero
            widthSecondFrontal =
              widthSecondFrontalNoRinconero +
              rinconero.thickness +
              (getRegletaInvertedValue(
                rinconeroItem.regleta || 0,
                rinconeroItem.morphs?.find((m) => m.name === 'Regleta'),
              ) || 0)

            // 1. Posición base: desde el rinconero, desplazar por su ancho en el eje X local
            const rinconeroXAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
            newPosition = rinconeroPos.clone().add(rinconeroXAxis.multiplyScalar(rinconero.width))

            // 2. Rotación: -90° en Y (perpendicular hacia la derecha)
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              -Math.PI / 2,
            )
            newQuaternion = rinconeroQuat.clone().multiply(rotationAdjustment)

            // Reconstruir matriz
            secondFrontalMatrix = new Matrix4().compose(newPosition, newQuaternion, rinconeroScale)
          } else if (isRinconeroIzquierda) {
            // RINCONERO IZQUIERDO: Ancho del frontal = items verticales + thickness (NO width)

            // CORREGIDO: Usar thickness en lugar de width del rinconero
            widthSecondFrontal =
              widthSecondFrontalNoRinconero +
              rinconero.thickness +
              (getRegletaInvertedValue(
                rinconeroItem.regleta || 0,
                rinconeroItem.morphs?.find((m) => m.name === 'Regleta'),
              ) || 0)

            // 1. Posición: exactamente en la posición del rinconero
            newPosition = rinconeroPos.clone()

            // 2. Rotación: +90° en Y (perpendicular hacia la izquierda)
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              Math.PI / 2,
            )
            newQuaternion = rinconeroQuat.clone().multiply(rotationAdjustment)

            // 3. Desplazar en el eje X local del nuevo quaternion (hacia atrás en la nueva orientación)
            const rinconeroOffset =
              rinconero.thickness +
              (getRegletaInvertedValue(
                rinconeroItem.regleta || 0,
                rinconeroItem.morphs?.find((m) => m.name === 'Regleta'),
              ) || 0) +
              widthSecondFrontalNoRinconero
            const xAxisAdjusted = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
            newPosition.add(xAxisAdjusted.multiplyScalar(-rinconeroOffset))

            // Reconstruir matriz
            secondFrontalMatrix = new Matrix4().compose(newPosition, newQuaternion, rinconeroScale)
          } else {
            // Fallback (no debería ocurrir)
            widthSecondFrontal =
              widthSecondFrontalNoRinconero + rinconero.thickness + rinconeroItem.regleta
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              -Math.PI / 2,
            )
            newQuaternion = rinconeroQuat.clone().multiply(rotationAdjustment)
            const rightVector = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
            newPosition = rinconeroPos
              .clone()
              .add(rightVector.clone().multiplyScalar(rinconero.width))
            secondFrontalMatrix = new Matrix4().compose(newPosition, newQuaternion, rinconeroScale)
          }

          // Ajustar posición Y del segundo frontal basado en encimera
          if (encimera) {
            // Buscar la altura máxima de los items MBJ del grupo (excluyendo regletas)
            let maxUnitHeight = 0
            for (const groupItem of group) {
              const item = items.get(groupItem.id) as LinkedItem
              if (item && item.tipoNormalizado === 'MBJ') {
                const catalogIDStr = item.catalogID?.toString().toUpperCase() || ''
                const isRegleta =
                  catalogIDStr.startsWith('RB') ||
                  item.description?.toLowerCase().includes('regleta')

                if (!isRegleta && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            // Si no encontramos altura de unidad no-regleta, usar la máxima altura del grupo
            if (maxUnitHeight === 0) {
              for (const groupItem of group) {
                const item = items.get(groupItem.id) as LinkedItem
                if (item && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            const unitHeight = maxUnitHeight || (rinconeroItem.height ?? 0)
            const encimeraThickness = getEncimeraThickness(encimera)
            const newY = unitHeight + encimeraThickness

            const currentPos = new Vector3()
            const quaternion = new Quaternion()
            const scale = new Vector3()
            secondFrontalMatrix.decompose(currentPos, quaternion, scale)
            secondFrontalMatrix.setPosition(currentPos.x, newY, currentPos.z)
          }

          const secondFrontalId = await get().drawItem(
            getId('linkedItem'),
            'Frontal',
            '',
            true,
            secondFrontalMatrix,
            'items',
          )

          // Configurar dimensiones del segundo frontal
          get().editMorph(secondFrontalId, 'Ancho', widthSecondFrontal)
          get().editMorph(secondFrontalId, 'Alto', height)
          get().editItem(secondFrontalId, { width: widthSecondFrontal, height })
          get().setOBB(secondFrontalId)
        } else {
          // Caso normal: un solo frontal lineal
          // Verificar si al menos un item del grupo tiene snappedWall
          const hasAnySnappedWall = group.some((item) => {
            const linkedItem = items.get(item.id) as LinkedItem
            return linkedItem && linkedItem.snappedWall && linkedItem.snappedWall !== ''
          })

          // Filtrar el grupo según si hay algún snappedWall
          const filteredGroup = group.filter((item) => {
            const linkedItem = items.get(item.id) as LinkedItem

            // Si al menos un item tiene snappedWall, solo filtrar por MBJ
            if (hasAnySnappedWall) {
              return linkedItem && linkedItem.tipoNormalizado === 'MBJ'
            }

            // Si NINGUNO tiene snappedWall, no dibujar frontal (filtrar todo)
            return false
          })

          // Si no quedan ítems válidos en el grupo, saltar al siguiente
          if (filteredGroup.length === 0) {
            continue
          }

          // Buscar el frontalInfo correspondiente al primer ítem del grupo filtrado
          const groupFirstItemId = filteredGroup[0]?.id
          const frontalInfo = frontalPositions.find((info) => info.itemId === groupFirstItemId)
          const extraWidth = contiguousResult.totalDistancesBetweenGroups
          if (frontalInfo) {
            try {
              const groupTotalWidth = filteredGroup.reduce((totalWidth, groupItem) => {
                const item = items.get(groupItem.id) as LinkedItem
                if (!item) {
                  return totalWidth + groupItem.width
                }

                // Obtener el ancho correcto usando el morph "Ancho" con su valor por defecto
                // Esto es especialmente importante para regletas que pueden tener width = max pero default = valor real
                const widthMorph = item.morphs?.find(
                  (morph) => morph.name === 'Ancho' || morph.name === 'width',
                )

                let itemWidth: number
                if (widthMorph) {
                  // Usar el valor por defecto del morph en lugar del width actual
                  // Esto asegura que regletas y otros items usen su valor por defecto, no el máximo
                  itemWidth =
                    widthMorph.default ?? widthMorph._default ?? item.width ?? groupItem.width
                } else {
                  itemWidth = item.width ?? groupItem.width
                }

                return totalWidth + itemWidth
              }, 0)

              frontalInfo.groupTotalWidth = groupTotalWidth
              const frontalMatrix = frontalInfo.matrix.clone()

              const offset = new Vector3(0, 0, 0)
              const currentPosition = new Vector3()
              const quaternion = new Quaternion()
              const scale = new Vector3()

              frontalMatrix.decompose(currentPosition, quaternion, scale)
              offset.applyQuaternion(quaternion)
              currentPosition.add(offset)

              const encimera = findEncimeraForGroup(filteredGroup)
              if (encimera) {
                let maxUnitHeight = 0
                for (const groupItem of filteredGroup) {
                  const item = items.get(groupItem.id) as LinkedItem
                  if (item && item.tipoNormalizado === 'MBJ') {
                    // Excluir regletas (tienen catalogID que empieza con RB)
                    const catalogIDStr = item.catalogID?.toString().toUpperCase() || ''
                    const isRegleta =
                      catalogIDStr.startsWith('RB') ||
                      item.description?.toLowerCase().includes('regleta')

                    if (!isRegleta && item.height && item.height > maxUnitHeight) {
                      maxUnitHeight = item.height
                    }
                  }
                }

                // Si no encontramos altura de unidad no-regleta, usar la máxima altura del grupo
                if (maxUnitHeight === 0) {
                  for (const groupItem of filteredGroup) {
                    const item = items.get(groupItem.id) as LinkedItem
                    if (item && item.height && item.height > maxUnitHeight) {
                      maxUnitHeight = item.height
                    }
                  }
                }

                const unitHeight = maxUnitHeight
                const encimeraThickness = getEncimeraThickness(encimera)
                const newY = unitHeight + encimeraThickness
                currentPosition.y = newY
              }

              frontalMatrix.setPosition(currentPosition)
              // Generar un ID único para el frontal
              const frontalId = getId('linkedItem')

              const drawnFrontalId = await get().drawItem(
                frontalId,
                'Frontal',
                '',
                true,
                frontalMatrix,
                'items',
                undefined,
                false,
              )

              await new Promise((resolve) => setTimeout(resolve, 500))

              const frontalAfterTimeout = get().items.get(drawnFrontalId)
              if (!frontalAfterTimeout) {
                continue
              }

              get().editMorph(drawnFrontalId, 'Ancho', frontalInfo.groupTotalWidth + extraWidth)
              get().editMorph(drawnFrontalId, 'Alto', height)
              get().editItem(drawnFrontalId, {
                width: frontalInfo.groupTotalWidth,
                height: height,
              })
              set((store) => {
                store.collisionableItems.delete(drawnFrontalId)
              })
              get().setOBB(drawnFrontalId)
            } catch (error) {
              console.error(
                `[DEBUG error] drawFrontalsFromSnappedGroupsNEW: Error dibujando frontal para grupo ${frontalInfo.groupIndex}:`,
                error,
              )
            }
          }
        }
      }

      // Restaurar sufijos (si es necesario) después de procesar todos los grupos
      if (unitsWithSuffixes.size > 0) {
        setTimeout(async () => {
          for (const [unitId, suffix] of unitsWithSuffixes) {
            const unit = get().items.get(unitId)
            if (unit && unit.type === 'linkedItem') {
              const currentCatalogID = unit.catalogID.toString()
              const baseCatalogID = getBaseCatalogID(currentCatalogID)
              const expectedCatalogID = buildCatalogIDWithSuffix(baseCatalogID, suffix)

              if (currentCatalogID !== expectedCatalogID) {
                try {
                  set((s) => {
                    const newItems = new Map(s.items)
                    const item = { ...newItems.get(unitId) }

                    if (item && item.type === 'linkedItem') {
                      item.catalogID = expectedCatalogID

                      if (item.glbPath) {
                        item.glbPath = updateGlbPathWithSuffix(item.glbPath, suffix)
                      }

                      if (item.referencia) {
                        item.referencia = updateReferenciaWithSuffix(item.referencia, suffix)
                      }

                      newItems.set(unitId, item)
                    }

                    return {
                      items: newItems,
                    }
                  })
                } catch (error) {
                  console.error(`Error restaurando sufijo para unidad ${unitId}:`, error)
                }
              }
            }
          }
        }, 1000)
      }
    } finally {
      // Resetear el flag de ejecución
      set((store) => {
        store.isDrawingFrontals = false
      })
    }
  },
  deleteLaterales: () => {
    const itemsToDelete: AnyID[] = []
    const lateralIds = new Set<AnyID>()

    get().items.forEach((item, id) => {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID) {
        const catalogID = item.catalogID.toString()
        if (
          catalogID.startsWith('LA') ||
          catalogID.startsWith('LB') ||
          catalogID.startsWith('LC') ||
          catalogID.startsWith('CC') ||
          catalogID.startsWith('CB')
        ) {
          itemsToDelete.push(id)
          lateralIds.add(id)
        }
      }
    })

    if (lateralIds.size > 0) {
      get().items.forEach((item, id) => {
        if (!item || (item as any).type !== 'linkedSon') return
        const firstParentID = (item as any).firstParentID as AnyID | undefined
        if (!firstParentID) return
        if (!lateralIds.has(firstParentID)) return

        itemsToDelete.push(id)
      })
    }

    itemsToDelete.forEach((id) => {
      set((store) => {
        store.items.delete(id)
        store.layers[store.currentLayer].items.delete(id)
        store.layers[store.currentLayer].selected.delete(id)
        store.sceneBoundingBoxes.delete(id)
        store.collisionableItems.delete(id)
      })
    })

    return itemsToDelete.length
  },
  updateFrontalsForUnit: (unitId: string) => {
    const items = get().items
    const unit = items.get(unitId)

    // Verificar que la unidad existe y no es un frontal
    if (!unit) {
      return
    }
    if ((isItem(unit) || isLinkedItem(unit)) && unit.catalogID === 'Frontal') {
      return
    }

    const contiguousResult = get().getContiguousItems()

    const unitGroup = contiguousResult.groups.find((group) =>
      group.some((item) => item.id === unitId),
    )

    if (!unitGroup || unitGroup.length === 0) {
      return
    }

    // Calcular el ancho total del grupo usando los anchos actualizados de los items
    const groupTotalWidth = unitGroup.reduce((totalWidth, groupItem) => {
      const item = items.get(groupItem.id)
      if (!item || (!isItem(item) && !isLinkedItem(item))) {
        return totalWidth + groupItem.width
      }
      // Usar el ancho actualizado del item (priorizar width property que es el más actualizado)
      // Si width no está disponible, usar el morph default (valor actualizado), y si no, usar el groupItem.width
      // NO usar _default porque es el valor por defecto original, no el actualizado
      const itemWidth =
        item.width ??
        item.morphs?.find((morph) => morph.name === 'Ancho')?.default ??
        groupItem.width
      return totalWidth + itemWidth
    }, 0)

    const firstGroupItemId = unitGroup[0]?.id
    if (!firstGroupItemId) {
      return
    }

    const allFrontals = Array.from(items.values()).filter(
      (item) => (isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal',
    )

    if (allFrontals.length === 0) {
      return
    }

    const firstGroupItem = items.get(firstGroupItemId)
    if (!firstGroupItem || (!isItem(firstGroupItem) && !isLinkedItem(firstGroupItem))) {
      return
    }

    const firstGroupItemSnappedWall = firstGroupItem.snappedWall || ''

    let matchingFrontals = allFrontals.filter((frontal) => {
      if (!isItem(frontal) && !isLinkedItem(frontal)) return false
      const frontalSnappedWall = frontal.snappedWall || ''
      return frontalSnappedWall === firstGroupItemSnappedWall
    })

    if (matchingFrontals.length > 1) {
      const firstGroupItemPos = new Vector3()
      const firstGroupItemQuat = new Quaternion()
      firstGroupItem.matrix.decompose(firstGroupItemPos, firstGroupItemQuat, new Vector3())

      // Encontrar el frontal más cercano al primer item del grupo
      let closestFrontal = matchingFrontals[0]
      let minDistance = Infinity

      matchingFrontals.forEach((frontal) => {
        if (!isItem(frontal) && !isLinkedItem(frontal)) return
        const frontalPos = new Vector3()
        frontal.matrix.decompose(frontalPos, new Quaternion(), new Vector3())
        const distance = firstGroupItemPos.distanceTo(frontalPos)
        if (distance < minDistance) {
          minDistance = distance
          closestFrontal = frontal
        }
      })

      matchingFrontals = [closestFrontal]
    } else if (matchingFrontals.length === 0) {
      // Intentar buscar por posición si no hay coincidencia por snappedWall
      const firstGroupItemPos = new Vector3()
      firstGroupItem.matrix.decompose(firstGroupItemPos, new Quaternion(), new Vector3())

      let closestFrontal = allFrontals[0]
      let minDistance = Infinity

      allFrontals.forEach((frontal) => {
        if (!isItem(frontal) && !isLinkedItem(frontal)) return
        const frontalPos = new Vector3()
        frontal.matrix.decompose(frontalPos, new Quaternion(), new Vector3())
        const distance = firstGroupItemPos.distanceTo(frontalPos)
        if (distance < minDistance) {
          minDistance = distance
          closestFrontal = frontal
        }
      })

      matchingFrontals = [closestFrontal]
    }

    // Actualizar el/los frontal(es) encontrado(s)
    // Guardar la selección actual para restaurarla después
    const currentLayer = get().currentLayer
    const originalSelected = new Set(get().layers[currentLayer].selected)

    matchingFrontals.forEach((frontal) => {
      if (!isItem(frontal) && !isLinkedItem(frontal)) return

      // Temporalmente seleccionar solo el frontal para que editMorph solo lo afecte
      set((store) => {
        store.layers[store.currentLayer].selected.clear()
        store.layers[store.currentLayer].selected.add(frontal.id)
      })

      // Actualizar el ancho del frontal
      get().editMorph(frontal.id, 'Ancho', groupTotalWidth)
      get().editItem(frontal.id, { width: groupTotalWidth })
      get().setOBB(frontal.id)
    })

    set((store) => {
      store.layers[store.currentLayer].selected = originalSelected
    })
  },
  drawLateralesForGroups: async (options?: { force?: boolean }) => {
    const autoEnabled = options?.force ? true : (get().lateralsAutoEnabled ?? true)
    if (!autoEnabled) {
      return
    }

    get().deleteLaterales()
    const { groups } = get().getContiguousItemsForLaterals()
    const items = get().items

    // Listar los items COL y MBJ en la escena
    const colItems: Array<{ id: string; catalogID: string; height: number }> = []
    const mbjItems: Array<{ id: string; catalogID: string; height: number }> = []
    for (const [id, item] of items) {
      if (!isLinkedItem(item)) continue
      if (!item.visible) continue
      if (item.catalogID === 'Frontal') continue
      if (item.tipoNormalizado === 'COL') {
        colItems.push({
          id,
          catalogID: item.catalogID?.toString() || 'unknown',
          height: item.height || 0,
        })
      } else if (item.tipoNormalizado === 'MBJ') {
        mbjItems.push({
          id,
          catalogID: item.catalogID?.toString() || 'unknown',
          height: item.height || 0,
        })
      }
    }

    // Función helper para obtener el valor REAL (invertido) de regleta para cálculos geométricos
    const getRegletaRealValue = (item: LinkedItem): number => {
      if (!item.regleta) return 0
      const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
      if (!regletaMorph) return item.regleta
      // El valor está invertido: si regleta = 20, el valor real es 120; si regleta = 120, el valor real es 20
      const storedValue = item.regleta
      if (storedValue >= regletaMorph.max) return regletaMorph.min
      if (storedValue <= regletaMorph.min) return regletaMorph.max
      return regletaMorph.max + regletaMorph.min - storedValue
    }

    // Función helper para obtener el valor invertido de regleta (igual que en collideItems)
    const getRegletaInvertedValue = (value: number, morph: Morphs | undefined): number => {
      if (!morph) return value
      if (value >= morph.max) return morph.min
      if (value <= morph.min) return morph.max
      return morph.max + morph.min - value
    }

    // Función para encontrar la unidad pegada a la regleta de un KARB/KBR
    const findUnitSnappedToRegleta = (
      rinconero: LinkedItem,
      items: Map<string, SceneObjects>,
    ): LinkedItem | null => {
      const rinconeroPos = new Vector3()
      const rinconeroQuat = new Quaternion()
      rinconero.matrix.decompose(rinconeroPos, rinconeroQuat, new Vector3())

      const isKBR = rinconero.catalogID?.toString().toUpperCase().includes('KBR')
      const isKAR = rinconero.catalogID?.toString().toUpperCase().includes('KARB')
      if ((!isKBR && !isKAR) || !rinconero.regleta) return null

      const catalog = rinconero.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      const isRightRinconero = !isLeftRinconero

      for (const [id, otherItem] of items) {
        if (id === rinconero.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
        const directionMultiplier =
          item1XAxis.dot(new Vector3(otherPos.x - rinconeroPos.x, 0, otherPos.z - rinconeroPos.z)) <
          0
            ? -1
            : 1

        const regletaMorph = rinconero.morphs?.find((m) => m.name === 'Regleta')
        const regletaValue = isKAR
          ? rinconero.regleta || 0
          : getRegletaInvertedValue(rinconero.regleta || 0, regletaMorph)

        let expectedPosition: Vector3

        if (isRightRinconero && directionMultiplier > 0) {
          expectedPosition = rinconeroPos
            .clone()
            .add(item1XAxis.multiplyScalar(directionMultiplier * rinconero.width))
          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          const rinconeroOffset = (rinconero.thickness || 0) + regletaValue
          expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))
        } else if (isLeftRinconero && directionMultiplier < 0) {
          expectedPosition = rinconeroPos.clone()
          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const adjustedQuat = rinconeroQuat.clone().multiply(rotationAdjustment)
          const rinconeroOffset = (rinconero.thickness || 0) + regletaValue + (otherItem.width || 0)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
          expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))
        } else {
          continue
        }

        const distance = expectedPosition.distanceTo(otherPos)
        if (distance <= 60) {
          return otherItem as LinkedItem
        }
      }

      return null
    }

    // Función para verificar si hay una unidad contigua en el lado donde se va a dibujar el lateral
    // Usa la misma lógica que collideItems para detectar contiguidad con regletas
    const hasContiguousUnit = (item: LinkedItem, isFirstItem: boolean): boolean => {
      const items = get().items
      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      // Variable para rastrear si el item está pegado a alguna regleta (para evitar verificar contiguidad normal con KBR con regleta)
      let isPegadoARegleta = false

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue // Ignorar frontales
        if (!otherItem.visible) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
        const isKAR = item.catalogID?.toString().toUpperCase().includes('KARB')
        const hasRegleta = (isKBR || isKAR) && item.regleta
        const catalog = item.catalogID?.toString().toLowerCase() ?? ''
        const isLeftRinconero = catalog.endsWith('i')
        const isRightRinconero =
          item.catalogID &&
          ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb', 'kbr', 'karb'].some((pattern) =>
            item.catalogID.toString().toLowerCase().includes(pattern),
          ) &&
          !catalog.endsWith('i')

        if (hasRegleta) {
          // Determinar en qué lado está la regleta
          // KBR/KAR izquierdo: regleta en lado izquierdo (isFirstItem = true)
          // KBR/KAR derecho: regleta en lado derecho (isFirstItem = false)
          const isRegletaSide =
            (isLeftRinconero && isFirstItem) || (isRightRinconero && !isFirstItem)

          if (isRegletaSide) {
            // Calcular la posición donde collideItems colocaría otherItem pegado a la regleta
            const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
            const directionMultiplier =
              item1XAxis.dot(new Vector3(otherPos.x - itemPos.x, 0, otherPos.z - itemPos.z)) < 0
                ? -1
                : 1

            let expectedPosition: Vector3
            const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
            // KAR usa regleta directa (no invertida), KBR usa regleta invertida
            const regletaValue = isKAR
              ? item.regleta || 0 // KAR: regleta directa
              : getRegletaInvertedValue(item.regleta || 0, regletaMorph) // KBR: regleta invertida

            if (isRightRinconero && directionMultiplier > 0) {
              // KBR/KAR derecho: mueble normal pegado a la derecha
              expectedPosition = itemPos
                .clone()
                .add(item1XAxis.multiplyScalar(directionMultiplier * item.width))
              const zAxis = new Vector3(0, 0, 1).applyQuaternion(itemQuat)
              const rinconeroOffset = (item.thickness || 0) + regletaValue
              expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))
            } else if (isLeftRinconero && directionMultiplier < 0) {
              // KBR/KAR izquierdo: mueble normal pegado a la izquierda
              expectedPosition = itemPos.clone()
              const rotationAdjustment = new Quaternion().setFromAxisAngle(
                new Vector3(0, 1, 0),
                Math.PI / 2,
              )
              const adjustedQuat = itemQuat.clone().multiply(rotationAdjustment)
              const rinconeroOffset = (item.thickness || 0) + regletaValue + (otherItem.width || 0)
              const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
              expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))
            } else {
              continue // No es el caso de regleta
            }

            const distance = expectedPosition.distanceTo(otherPos)

            if (distance <= 60) {
              return true
            }
          }
        }

        // El item actual NO es KBR/KAR, verificar si está pegado a la regleta de otherItem (KBR/KAR)
        const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
        const otherIsKAR = otherItem.catalogID?.toString().toUpperCase().includes('KARB')
        const otherHasRegleta = (otherIsKBR || otherIsKAR) && otherItem.regleta

        if (!isKBR && !isKAR && otherHasRegleta) {
          const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
          const otherIsLeftRinconero = otherCatalog.endsWith('i')
          const otherIsRightRinconero =
            otherItem.catalogID &&
            ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb', 'kbr', 'karb'].some(
              (pattern) => otherItem.catalogID.toString().toLowerCase().includes(pattern),
            ) &&
            !otherCatalog.endsWith('i')

          const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
          const directionMultiplier =
            otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
              ? -1
              : 1

          let expectedPosition: Vector3
          const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
          // KAR usa regleta directa (no invertida), KBR usa regleta invertida
          const otherRegletaValue = otherIsKAR
            ? otherItem.regleta || 0 // KAR: regleta directa
            : getRegletaInvertedValue(otherItem.regleta || 0, otherRegletaMorph) // KBR: regleta invertida

          if (otherIsRightRinconero && directionMultiplier > 0) {
            // KBR/KAR derecho: Item normal pegado a la regleta
            // La regleta está en el lado IZQUIERDO del item (donde colisionó)
            // El lateral debe estar en el lado DERECHO (opuesto, exterior)
            expectedPosition = otherPos
              .clone()
              .add(otherItemXAxis.multiplyScalar(directionMultiplier * otherItem.width))
            const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
            const rinconeroOffset = (otherItem.thickness || 0) + otherRegletaValue
            expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)

            if (distance < 50) {
              isPegadoARegleta = true
              if (isFirstItem) {
                return true
              } else {
                return false // Permitir dibujar lateral en el lado opuesto
              }
            }
          } else if (otherIsLeftRinconero && directionMultiplier < 0) {
            expectedPosition = otherPos.clone()
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              Math.PI / 2,
            )
            const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
            const rinconeroOffset =
              (otherItem.thickness || 0) + otherRegletaValue + (item.width || 0)
            const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
            expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)
            if (distance < 50) {
              isPegadoARegleta = true

              if (!isFirstItem) {
                return true
              } else {
                return false // Permitir dibujar lateral en el lado opuesto
              }
            }
          }
        }

        if (!isKBR && !isKAR && isPegadoARegleta && (otherIsKBR || otherIsKAR) && otherHasRegleta) {
          continue
        }

        // Usar OBB reales si están disponibles para una detección más precisa
        const sceneBoundingBoxes = get().sceneBoundingBoxes
        const itemOBB = sceneBoundingBoxes.get(item.id)
        const otherItemOBB = sceneBoundingBoxes.get(id)

        // Calcular la posición donde iría el lateral usando la misma lógica que processLateral
        const lateralWidth = 19
        let lateralPosition: Vector3
        let lateralMatrix: Matrix4

        if (isFirstItem) {
          const offset = -19
          lateralMatrix = getRelativeMatrix(item.matrix, offset, item, 0)
        } else {
          const offset = item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, item, 0)
        }

        const lateralPos = new Vector3()
        const lateralQuat = new Quaternion()
        const lateralScale = new Vector3()
        lateralMatrix.decompose(lateralPos, lateralQuat, lateralScale)
        lateralPosition = lateralPos

        // Crear una OBB aproximada para el lateral (19mm de ancho, misma altura y profundidad que el item)
        const lateralHeight = item.height || 0
        const lateralDepth = item.thickness || 0
        const lateralOBB = new OBB(
          lateralPosition,
          new Vector3(lateralWidth / 2, lateralHeight / 2, lateralDepth / 2),
        )
        const lateralRotationMatrix = new Matrix3().setFromMatrix4(
          new Matrix4().makeRotationFromQuaternion(lateralQuat),
        )
        lateralOBB.rotation = lateralRotationMatrix

        if (itemOBB && otherItemOBB) {
          if (lateralOBB.intersectsOBB(otherItemOBB.boundingBox)) {
            return true
          }
        }

        const distance = lateralPosition.distanceTo(otherPos)

        // Si el otro item está muy cerca, verificar alineación usando límites más precisos
        const maxItemDimension = Math.max(otherItem.width || 0, otherItem.thickness || 0)
        // Reducir el umbral para ser más estricto cuando las unidades están cerca pero no snapadas
        const distanceThreshold = Math.min(maxItemDimension + 50, 200)
        if (distance < distanceThreshold) {
          // Verificar si están en la misma pared y alineados
          if (
            areItemsAlignedOnWall(
              item,
              otherItem,
              isFirstItem ? 'left' : 'right',
              getRegletaRealValue,
            )
          ) {
            return true
          }
        }
      }
      return false
    }

    // Función para verificar si dos items están alineados en la misma pared
    const areItemsAlignedOnWall = (
      item1: LinkedItem,
      item2: LinkedItem,
      side: 'left' | 'right',
      getRegletaRealValue: (item: LinkedItem) => number,
    ): boolean => {
      // Extraer posiciones y rotaciones
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      // Verificar si comparten la misma pared
      if (item1.snappedWall && item2.snappedWall && item1.snappedWall !== item2.snappedWall) {
        return false
      }

      const sceneBoundingBoxes = get().sceneBoundingBoxes
      const item1OBB = sceneBoundingBoxes.get(item1.id)
      const item2OBB = sceneBoundingBoxes.get(item2.id)

      if (item1OBB && item2OBB) {
        // Usar OBB reales para calcular límites más precisos
        const obb1 = item1OBB.boundingBox
        const obb2 = item2OBB.boundingBox

        // Obtener los puntos de las esquinas de las OBB proyectadas en el plano XZ
        const getOBBBoundsXZ = (obb: OBB) => {
          const corners = [
            new Vector3(-1, 0, -1),
            new Vector3(1, 0, -1),
            new Vector3(-1, 0, 1),
            new Vector3(1, 0, 1),
          ]
          const worldCorners = corners.map((corner) => {
            const worldCorner = corner.clone()
            // Multiplicar cada componente por el halfSize correspondiente
            worldCorner.x *= obb.halfSize.x
            worldCorner.y *= obb.halfSize.y
            worldCorner.z *= obb.halfSize.z
            worldCorner.applyMatrix3(obb.rotation)
            worldCorner.add(obb.center)
            return worldCorner
          })

          const xValues = worldCorners.map((c) => c.x)
          const zValues = worldCorners.map((c) => c.z)
          const minX = Math.min(...xValues)
          const maxX = Math.max(...xValues)
          const minZ = Math.min(...zValues)
          const maxZ = Math.max(...zValues)

          return { minX, maxX, minZ, maxZ, center: obb.center }
        }

        const bounds1 = getOBBBoundsXZ(obb1)
        const bounds2 = getOBBBoundsXZ(obb2)

        // Verificar superposición en el eje relevante usando límites OBB
        if (side === 'left') {
          // Para lateral izquierdo, verificar si el item2 está a la izquierda del item1
          // El lateral estaría en bounds1.minX - 19/2, verificar si bounds2.maxX está cerca
          const lateralX = bounds1.minX - 9.5 // 19/2
          const distanceLeft = Math.abs(bounds2.maxX - lateralX)
          // Verificar también si hay superposición en el eje Z (profundidad)
          const zOverlap = bounds1.minZ <= bounds2.maxZ + 20 && bounds1.maxZ >= bounds2.minZ - 20
          return distanceLeft < 30 && zOverlap // Tolerancia más estricta: 30 unidades
        } else {
          // Para lateral derecho, verificar si el item2 está a la derecha del item1
          // El lateral estaría en bounds1.maxX + 19/2, verificar si bounds2.minX está cerca
          const lateralX = bounds1.maxX + 9.5 // 19/2
          const distanceRight = Math.abs(bounds2.minX - lateralX)
          // Verificar también si hay superposición en el eje Z (profundidad)
          const zOverlap = bounds1.minZ <= bounds2.maxZ + 20 && bounds1.maxZ >= bounds2.minZ - 20
          return distanceRight < 30 && zOverlap // Tolerancia más estricta: 30 unidades
        }
      }

      // Fallback: Calcular los límites de cada item usando el valor REAL de regleta
      const getItemBounds = (item: LinkedItem, pos: Vector3, quat: Quaternion) => {
        const forward = new Vector3(0, 0, 1).applyQuaternion(quat)
        const right = new Vector3(1, 0, 0).applyQuaternion(quat)
        const center = pos.clone()
        const width = item.width
        // Usar el valor REAL (invertido) de regleta para cálculos geométricos
        const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
        const regletaRealValue = isKBR && item.regleta ? getRegletaRealValue(item) : 0
        const depth = item.thickness + regletaRealValue

        return {
          front: center.clone().add(forward.multiplyScalar(depth / 2)),
          back: center.clone().add(forward.multiplyScalar(-depth / 2)),
          left: center.clone().add(right.multiplyScalar(-width / 2)),
          right: center.clone().add(right.multiplyScalar(width / 2)),
          center: center,
        }
      }

      const bounds1 = getItemBounds(item1, pos1, quat1)
      const bounds2 = getItemBounds(item2, pos2, quat2)

      // Verificar superposición en el eje relevante con tolerancia más estricta
      if (side === 'left') {
        // Para lateral izquierdo, verificar si el item2 está a la izquierda del item1
        const distanceLeft = bounds1.left.distanceTo(bounds2.right)
        // Verificar también superposición en profundidad
        const depthOverlap =
          Math.abs(bounds1.front.z - bounds2.back.z) < 50 ||
          Math.abs(bounds1.back.z - bounds2.front.z) < 50 ||
          (bounds1.front.z >= bounds2.back.z - 20 && bounds1.back.z <= bounds2.front.z + 20)
        return distanceLeft < 30 && depthOverlap // Tolerancia más estricta: 30 unidades
      } else {
        // Para lateral derecho, verificar si el item2 está a la derecha del item1
        const distanceRight = bounds1.right.distanceTo(bounds2.left)
        // Verificar también superposición en profundidad
        const depthOverlap =
          Math.abs(bounds1.front.z - bounds2.back.z) < 50 ||
          Math.abs(bounds1.back.z - bounds2.front.z) < 50 ||
          (bounds1.front.z >= bounds2.back.z - 20 && bounds1.back.z <= bounds2.front.z + 20)
        return distanceRight < 30 && depthOverlap // Tolerancia más estricta: 30 unidades
      }
    }

    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const group = groups[groupIndex]
      // if (group.length === 0) continue

      // Obtener primer y último elemento del grupo
      const firstItemInfo = group[0]
      const lastItemInfo = group[group.length - 1]

      const firstItem = items.get(firstItemInfo.id) as LinkedItem
      const lastItem = items.get(lastItemInfo.id) as LinkedItem

      if (!firstItem || !lastItem) {
        continue
      }

      const catalogUpper = firstItem.catalogID?.toString().toUpperCase() ?? ''
      // Separar KALB (en L) de KARB (rectos en esquina, igual que KBR)
      const isKALB = catalogUpper.includes('KALB')
      const isKARB = catalogUpper.includes('KARB')
      const isKBRGroup = Boolean(firstItemInfo?.isKBR)

      if (isKBRGroup) {
        const hasLeftContiguous = hasContiguousUnit(firstItem, true)
        const hasRightContiguous = hasContiguousUnit(firstItem, false)

        if (!hasLeftContiguous) {
          await processLateralKBR(firstItem, true)
        }
        if (!hasRightContiguous) {
          await processLateralKBR(firstItem, false)
        }
        continue
      }

      if (isKARB) {
        const hasLeftContiguous = hasContiguousUnit(firstItem, true)
        const hasRightContiguous = hasContiguousUnit(firstItem, false)

        if (!hasLeftContiguous) {
          await processLateralKARB(firstItem, true)
        } else {
          if (firstItem.regleta) {
            const catalog = firstItem.catalogID?.toString().toLowerCase() ?? ''
            const isLeftRinconero = catalog.endsWith('i')
            if (isLeftRinconero) {
              const unitPegadaARegleta = findUnitSnappedToRegleta(firstItem, items)
              if (unitPegadaARegleta) {
                const lateralSide = true // true = izquierdo (extremo opuesto)
                await processLateralKARB(unitPegadaARegleta, lateralSide)
              }
            }
          }
        }
        if (!hasRightContiguous) {
          await processLateralKARB(firstItem, false)
        } else {
          if (firstItem.regleta) {
            const catalog = firstItem.catalogID?.toString().toLowerCase() ?? ''
            const isLeftRinconero = catalog.endsWith('i')
            const isRightRinconero = !isLeftRinconero

            if (isRightRinconero) {
              const unitPegadaARegleta = findUnitSnappedToRegleta(firstItem, items)
              if (unitPegadaARegleta) {
                const lateralSide = false // false = derecho (extremo opuesto)
                await processLateralKARB(unitPegadaARegleta, lateralSide)
              }
            }
          }
        }
        continue
      }

      // Para KALB (en L), usar siempre la lógica específica de rinconeros en L
      if (isKALB) {
        await processLateralKALB(firstItem, true)
        await processLateralKALB(firstItem, false)
        continue
      }

      // Caso general (no rinconero alto ni KBR)
      await processLateral(firstItem, true)
      if (group.length === 1) {
        await processLateral(firstItem, false)
      }
      if (group.length > 1) {
        await processLateral(lastItem, false)
      }
    }

    /**
     * Procesa un lateral para un mueble específico
     */
    async function processLateral(item: LinkedItem, isFirstItem: boolean) {
      // Verificar si hay una unidad contigua (especialmente importante para unidades pegadas a regletas)
      const hasContiguous = hasContiguousUnit(item, isFirstItem)
      if (hasContiguous) {
        return
      }

      // Verificar si el item está pegado a una regleta y determinar el lado correcto para el lateral
      // Para KBR derecho: lateral en lado opuesto al gizmo (lado izquierdo, isFirstItem = true)
      // Para KBR izquierdo: lateral en lado del gizmo (lado derecho, isFirstItem = false)
      const items = get().items
      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      let shouldDrawLateral = true
      // const correctSide = isFirstItem

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue

        const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
        const otherHasRegleta = otherIsKBR && otherItem.regleta

        if (!otherIsKBR || !otherHasRegleta) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
        const otherIsLeftRinconero = otherCatalog.endsWith('i')
        const otherIsRightRinconero =
          otherItem.catalogID &&
          ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb', 'kbr'].some((pattern) =>
            otherItem.catalogID.toString().toLowerCase().includes(pattern),
          ) &&
          !otherCatalog.endsWith('i')

        // Calcular la posición donde collideItems colocaría este item pegado a la regleta
        const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
        const directionMultiplier =
          otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
            ? -1
            : 1

        const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
        const otherRegletaInvertedValue = getRegletaInvertedValue(
          otherItem.regleta || 0,
          otherRegletaMorph,
        )

        let expectedPosition: Vector3

        if (otherIsRightRinconero && directionMultiplier > 0) {
          // KBR derecho: Item normal pegado a la regleta en su lado IZQUIERDO
          // El lateral debe estar en el lado DERECHO (opuesto, isFirstItem = false)
          expectedPosition = otherPos
            .clone()
            .add(otherItemXAxis.multiplyScalar(directionMultiplier * otherItem.width))
          const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
          const rinconeroOffset = (otherItem.thickness || 0) + otherRegletaInvertedValue
          expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))

          const distance = expectedPosition.distanceTo(itemPos)
          if (distance < 50) {
            // El lateral debe estar en el lado DERECHO (isFirstItem = false)
            if (isFirstItem) {
              shouldDrawLateral = false
              break
            }
            // isFirstItem = false es correcto, continuar
          }
        } else if (otherIsLeftRinconero && directionMultiplier < 0) {
          // KBR izquierdo: Item normal pegado a la regleta en su lado DERECHO
          // El lateral debe estar en el lado IZQUIERDO (opuesto, isFirstItem = true)
          expectedPosition = otherPos.clone()
          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
          const rinconeroOffset =
            (otherItem.thickness || 0) + otherRegletaInvertedValue + (item.width || 0)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
          expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))

          const distance = expectedPosition.distanceTo(itemPos)
          if (distance < 50) {
            // El lateral debe estar en el lado IZQUIERDO (isFirstItem = true)
            if (!isFirstItem) {
              // Estamos intentando dibujar en el lado derecho (donde está la regleta), pero debe estar en el izquierdo
              shouldDrawLateral = false
              break
            }
            // isFirstItem = true es correcto, continuar
          }
        }
      }

      if (!shouldDrawLateral) {
        return
      }

      // Para rinconeros (KBR, KALB), siempre permitir laterales incluso en esquinas
      // Para otros muebles, verificar si hay espacio disponible en la pared
      const isRinconero =
        item.catalogID?.toString().toUpperCase().includes('KBR') ||
        item.catalogID?.toString().toUpperCase().includes('KALB')
      if (!isRinconero && !hasAvailableSpace(item, isFirstItem)) return
      // if (item.catalogID?.toString().toLowerCase().includes('deco')) return
      const lateralConfig = buildLateralConfig(item, isFirstItem)
      if (!lateralConfig) return
      const { catalogID, heightOffset } = lateralConfig

      const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
      let lateralMatrix: Matrix4

      if (isKBR && item.regleta) {
        // Decidir cuál lateral es el de regleta:
        // - KBR...I (izquierdo): el PRIMER lateral (isFirstItem === true) es el de regleta
        // - KBR...  (derecho) : el SEGUNDO lateral (isFirstItem === false) es el de regleta
        const catalog = item.catalogID?.toString().toLowerCase() ?? ''
        const isLeftRinconero = catalog.endsWith('i')
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

        if (isRegletaLateral) {
          lateralMatrix = calculateKBRLateralMatrix(item, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
        }
      } else {
        // No es KBR: lógica estándar para muebles normales
        // El offset base: -19 para lado izquierdo, item.width para lado derecho
        let offset = isFirstItem ? -19 : item.width

        // Verificar si el item está pegado a una regleta
        // Si está pegado, el lateral debe estar en el lado OPUESTO y debe incluir el fondo (thickness) de la unidad
        const items = get().items
        const itemPos = new Vector3()
        const itemQuat = new Quaternion()
        item.matrix.decompose(itemPos, itemQuat, new Vector3())

        let isPegadoARegleta = false
        let ladoColision = '' // 'left' o 'right'

        for (const [id, otherItem] of items) {
          if (id === item.id) continue
          if (!isLinkedItem(otherItem)) continue
          if (otherItem.catalogID === 'Frontal') continue
          if (!otherItem.visible) continue

          const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
          const otherHasRegleta = otherIsKBR && otherItem.regleta

          if (!otherIsKBR || !otherHasRegleta) continue

          const otherPos = new Vector3()
          const otherQuat = new Quaternion()
          otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

          const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
          const otherIsLeftRinconero = otherCatalog.endsWith('i')
          const otherIsRightRinconero =
            otherItem.catalogID &&
            ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb', 'kbr'].some((pattern) =>
              otherItem.catalogID.toString().toLowerCase().includes(pattern),
            ) &&
            !otherCatalog.endsWith('i')

          const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
          const directionMultiplier =
            otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
              ? -1
              : 1

          const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
          const otherRegletaInvertedValue = getRegletaInvertedValue(
            otherItem.regleta || 0,
            otherRegletaMorph,
          )

          let expectedPosition: Vector3

          if (otherIsRightRinconero && directionMultiplier > 0) {
            // KBR derecho: Item normal pegado a la regleta en su lado IZQUIERDO
            expectedPosition = otherPos
              .clone()
              .add(otherItemXAxis.multiplyScalar(directionMultiplier * otherItem.width))
            const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
            const rinconeroOffset = (otherItem.thickness || 0) + otherRegletaInvertedValue
            expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)
            if (distance < 50) {
              isPegadoARegleta = true
              ladoColision = 'left' // Colisionó en el lado IZQUIERDO del item

              if (!isFirstItem) {
                offset = item.width || 0
                // Continuar para dibujar el lateral
              } else {
                return // NO dibujar lateral en el lado donde está la regleta
              }
            }
          } else if (otherIsLeftRinconero && directionMultiplier < 0) {
            // KBR izquierdo: Item normal pegado a la regleta en su lado DERECHO
            expectedPosition = otherPos.clone()
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              Math.PI / 2,
            )
            const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
            const rinconeroOffset =
              (otherItem.thickness || 0) + otherRegletaInvertedValue + (item.width || 0)
            const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
            expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)
            if (distance < 50) {
              isPegadoARegleta = true
              ladoColision = 'right' // Colisionó en el lado DERECHO del item
              // El lateral debe estar en el lado IZQUIERDO (opuesto)
              if (isFirstItem) {
                // Estamos en el lado izquierdo, que es correcto - SÍ dibujar
                // El offset debe ser negativo estándar: -19
                offset = -19
                // Continuar para dibujar el lateral
              } else {
                return // NO dibujar lateral en el lado donde está la regleta
              }
            }
          }
        }

        lateralMatrix = getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      // Formatear altura y fondo para el ID
      //       let height = Math.round(item.height || 0)
      //       let depth = Math.round(item.thickness || 0)
      //       if (depth < 350) {
      //         depth = 350
      //       }
      //       if (idPrefix === 'CB') {
      //         depth = 600
      //       }
      //       if (idPrefix === 'CC') {
      //         height = height + 100
      //       }
      //       let heightOffset = 0
      //       if (idPrefix === 'LA') {
      //         if (height === 820 || height === 920) {
      //           //no hacemos nada
      //         } else if (height > 900) {
      //           height = 900
      //         } else if (height < 900) {
      //           height = 800
      //         }
      //         heightOffset = 20
      //       }
      //       const catalogID = `${idPrefix}${height / 10}${depth / 10}`
      //       // Calcular la matriz para la posición del lateral
      //       // Offset negativo para el primer elemento, positivo para el último
      //       const offset = isFirstItem ? -19 : item.width
      //       const lateralMatrix = getRelativeMatrix(item.matrix, offset, item, heightOffset)

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        const originalCurrentObjectID = get().currentObjectID
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        // Actualizar x, z del lateral desde la matriz (como los elementos enlazados)
        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }

        if (item.snappedWall && !isKBR) {
          get().editSnappedWall(lateralId, item.snappedWall)
          // Proyectar el lateral en la pared para asegurar que esté correctamente alineado
          // Usar un pequeño delay para asegurar que el lateral se haya dibujado completamente
          setTimeout(() => {
            const lateralItem = get().items.get(lateralId)
            if (lateralItem && lateralItem.matrix) {
              get().projectItemOnWall(lateralId, item.snappedWall, lateralItem.matrix)
            }
          }, 100)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(
          `❌ Error al dibujar lateral ${catalogID} para mueble ${item.id} (${item.catalogID}):`,
          error,
        )
      }
    }

    async function processLateralKBR(item: LinkedItem, isFirstItem: boolean) {
      const lateralConfig = buildLateralConfig(item, isFirstItem)
      if (!lateralConfig) return
      const { catalogID, heightOffset } = lateralConfig
      let lateralMatrix: Matrix4

      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')

      if (item.regleta) {
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem
        if (isRegletaLateral) {
          lateralMatrix = calculateKBRLateralMatrix(item, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        lateralMatrix = getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        const originalCurrentObjectID = get().currentObjectID
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }

        if (item.snappedWall && !isInCorner(item, isFirstItem)) {
          get().editSnappedWall(lateralId, item.snappedWall)
          setTimeout(() => {
            const lateralItem = get().items.get(lateralId)
            if (lateralItem && lateralItem.matrix) {
              get().projectItemOnWall(lateralId, item.snappedWall, lateralItem.matrix)
            }
          }, 100)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(`❌ Error al dibujar lateral KBR ${catalogID}:`, error)
      }
    }

    /**
     * Procesa laterales para KARB (igual que KBR pero con regleta directa, no invertida)
     */
    async function processLateralKARB(item: LinkedItem, isFirstItem: boolean) {
      const lateralConfig = buildLateralConfig(item, isFirstItem)
      if (!lateralConfig) {
        return
      }
      const { catalogID, heightOffset } = lateralConfig
      let lateralMatrix: Matrix4

      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')

      if (item.regleta) {
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

        if (isRegletaLateral) {
          lateralMatrix = calculateKARBLateralMatrix(item, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      }

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        const originalCurrentObjectID = get().currentObjectID
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }

        // Para laterales de KARB con regleta, NO proyectar en la pared porque la posición
        // ya está calculada correctamente con calculateKARBLateralMatrix
        const isKARBWithRegletaLateral =
          item.regleta && (isLeftRinconero ? isFirstItem : !isFirstItem)

        if (item.snappedWall && !isInCorner(item, isFirstItem) && !isKARBWithRegletaLateral) {
          get().editSnappedWall(lateralId, item.snappedWall)
          setTimeout(() => {
            const lateralItem = get().items.get(lateralId)
            if (lateralItem && lateralItem.matrix) {
              get().projectItemOnWall(lateralId, item.snappedWall, lateralItem.matrix)
            }
          }, 100)
        } else if (item.snappedWall && isKARBWithRegletaLateral) {
          // Para KARB con regleta, solo asignar la pared pero NO proyectar (la posición ya está correcta)
          get().editSnappedWall(lateralId, item.snappedWall)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(`❌ Error al dibujar lateral KARB ${catalogID}:`, error)
      }
    }

    /**
     * Procesa laterales para rinconeros altos (KALB) en los lados cortos/alejados
     * Los rinconeros en L tienen dos lados cortos: uno en cada brazo del L (las esquinas)
     */
    async function processLateralKALB(item: LinkedItem, isFirstShortSide: boolean) {
      const lateralConfig = buildLateralConfig(item, isFirstShortSide)
      if (!lateralConfig) return
      const { catalogID, heightOffset } = lateralConfig

      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      item.matrix.decompose(basePosition, baseQuaternion, baseScale)

      let lateralMatrix: Matrix4

      if (isFirstShortSide) {
        const offset = -19
        lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      } else {
        // Segundo lado corto: en el extremo del segundo brazo (lado del segundo brazo, rotado 270°)
        // Este es el lateral que va apoyado al frente, sin desplazamiento extra de 19mm
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          (3 * Math.PI) / 2,
        )
        const newQuat = baseQuaternion.clone().multiply(rotationAdjustment)

        const xAxis = new Vector3(1, 0, 0).applyQuaternion(baseQuaternion)
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(baseQuaternion)

        const endOfSecondArm = basePosition
          .clone()
          .add(xAxis.multiplyScalar(item.width))
          .add(zAxis.multiplyScalar(item.thickness))

        // Crear una matriz temporal en el extremo del segundo brazo con la rotación correcta
        const tempMatrix = new Matrix4().compose(endOfSecondArm, newQuat, baseScale)

        // Aquí NO aplicamos offset extra en X: el frente hace de referencia
        // (offset = 0 → solo heightOffset)
        lateralMatrix = getRelativeMatrix(tempMatrix, 0, undefined, heightOffset)
      }

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        const originalCurrentObjectID = get().currentObjectID
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        // Actualizar x, z del lateral desde la matriz
        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        // Cambiar altura para normalizados
        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(
          `❌ Error al dibujar lateral KALB ${catalogID} para mueble ${item.id} (${item.catalogID}):`,
          error,
        )
      }
    }

    /**
     * Calcula la matriz del lateral para rinconeros KBR de manera relativa al rinconero
     */
    function calculateKBRLateralMatrix(
      item: LinkedItem,
      isFirstItem: boolean,
      heightOffset: number,
    ): Matrix4 {
      function getRegletaInvertedValue(value: number, morph: Morphs) {
        if (!morph) return value
        if (value >= morph.max) return morph.min
        if (value <= morph.min) return morph.max
        return morph.max + morph.min - value
      }

      const baseMatrix = item.matrix.clone()
      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

      // Obtener el valor de regleta usando la misma lógica que getRelativeMatrix
      // const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
      // const regletaRaw = typeof item.regleta === 'number' ? item.regleta : 60
      // const regletaValue = getRegletaInvertedValue(regletaRaw, regletaMorph)

      // const thicknessValue =
      //   typeof item.thickness === 'number' && item.thickness > 0 ? item.thickness : 600
      // const widthValue = typeof item.width === 'number' && item.width > 0 ? item.width : 1000

      const hand = (item.hand || item.mano || '').toString().toLowerCase()
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = hand.includes('cd') || catalog.endsWith('i')

      // Determinar si este lateral está más próximo a la regleta
      const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

      let lateralPosition: Vector3
      let lateralQuaternion: Quaternion

      if (isRegletaLateral) {
        const rinconeroQuat = baseQuaternion.clone()
        const rinconeroPos = basePosition.clone()

        if (!isLeftRinconero) {
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
          const newPos = rinconeroPos.clone().add(xAxis.multiplyScalar(item.width))

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            -Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset =
            (item.thickness || 0) +
            (getRegletaInvertedValue(
              item.regleta || 0,
              item.morphs?.find((m) => m.name === 'Regleta'),
            ) || 0)

          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          newPos.add(zAxis.multiplyScalar(rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        } else {
          const newPos = rinconeroPos.clone()

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset =
            (item.thickness || 0) +
            (getRegletaInvertedValue(
              item.regleta || 0,
              item.morphs?.find((m) => m.name === 'Regleta'),
            ) || 0)

          const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuat)
          newPos.add(zAxis.multiplyScalar(-rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        return getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      return new Matrix4().compose(lateralPosition, lateralQuaternion, baseScale)
    }

    /**
     * Calcula la matriz del lateral para rinconeros KARB (igual que KBR pero con regleta directa, no invertida, y ajuste -40mm)
     */
    function calculateKARBLateralMatrix(
      item: LinkedItem,
      isFirstItem: boolean,
      heightOffset: number,
    ): Matrix4 {
      const baseMatrix = item.matrix.clone()
      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

      const hand = (item.hand || item.mano || '').toString().toLowerCase()
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = hand.includes('cd') || catalog.endsWith('i')

      // Determinar si este lateral está más próximo a la regleta
      const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

      let lateralPosition: Vector3
      let lateralQuaternion: Quaternion

      if (isRegletaLateral) {
        const rinconeroQuat = baseQuaternion.clone()
        const rinconeroPos = basePosition.clone()

        // Para KARB: usar regleta directa (NO invertida) y ajuste de -40mm
        const regletaValue = item.regleta || 0
        const KARB_OFFSET_ADJUSTMENT = -50

        if (!isLeftRinconero) {
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
          const newPos = rinconeroPos.clone().add(xAxis.multiplyScalar(item.width))

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            -Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset = (item.thickness || 0) + regletaValue + KARB_OFFSET_ADJUSTMENT

          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          newPos.add(zAxis.multiplyScalar(rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        } else {
          const newPos = rinconeroPos.clone()

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset = (item.thickness || 0) + regletaValue + KARB_OFFSET_ADJUSTMENT

          const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuat)
          newPos.add(zAxis.multiplyScalar(-rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        }
      } else {
        // Lateral que NO es de regleta: usar lógica simple sin pasar item como referenceItem
        // para evitar que getRelativeMatrix aplique lógica especial de rinconero
        const offset = isFirstItem ? -19 : item.width
        return getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      }

      return new Matrix4().compose(lateralPosition, lateralQuaternion, baseScale)
    }

    function isInCorner(item: LinkedItem, isFirstItem: boolean) {
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      return (isLeftRinconero && !isFirstItem) || (!isLeftRinconero && isFirstItem)
    }

    /**
     * Verifica si hay espacio disponible para colocar un lateral
     */
    function hasAvailableSpace(item: LinkedItem, isFirstItem: boolean): boolean {
      // Si el mueble no está pegado a una pared, asumimos que hay espacio
      if (!item.snappedWall) return true

      const wall = items.get(item.snappedWall) as Walls
      if (!wall) return false

      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      const wallStart = new Vector3(wall.start[0], 0, wall.start[1])
      const wallEnd = new Vector3(wall.end[0], 0, wall.end[1])
      const wallDir = wallEnd.clone().sub(wallStart).normalize()

      const itemToWallStart = itemPos.clone().sub(wallStart)
      const projectedDist = itemToWallStart.dot(wallDir)

      const wallLength = wallStart.distanceTo(wallEnd)

      if (isFirstItem) {
        return projectedDist >= 20
      } else {
        const itemEndPos = projectedDist + item.width
        return wallLength - itemEndPos >= 20
      }
    }

    // Función helper para detectar si hay unidades MBJ adyacentes a una unidad COL en un lado específico
    function hasMBJAdjacent(item: LinkedItem, isFirstItem: boolean): boolean {
      if (item.tipoNormalizado !== 'COL') return false

      const items = get().items
      const sceneBoundingBoxes = get().sceneBoundingBoxes
      const itemOBB = sceneBoundingBoxes.get(item.id)

      if (!itemOBB) {
        return false
      }

      // Primero verificar si están en el mismo grupo contiguo (más confiable)
      const { groups } = get().getContiguousItemsForLaterals()
      for (const group of groups) {
        const colInGroup = group.find((g) => g.id === item.id)
        if (colInGroup) {
          // Buscar MBJ en el mismo grupo
          for (const groupItem of group) {
            if (groupItem.id === item.id) continue
            const groupItemObj = items.get(groupItem.id) as LinkedItem
            if (
              groupItemObj &&
              isLinkedItem(groupItemObj) &&
              groupItemObj.tipoNormalizado === 'MBJ' &&
              groupItemObj.visible
            ) {
              // Verificar si el MBJ está en el lado correcto
              // Si es el primer item del grupo y estamos verificando el lado izquierdo, el MBJ debe estar después
              // Si es el último item del grupo y estamos verificando el lado derecho, el MBJ debe estar antes
              const colIndex = group.findIndex((g) => g.id === item.id)
              const mbjIndex = group.findIndex((g) => g.id === groupItem.id)

              if (isFirstItem && mbjIndex < colIndex) {
                return true
              } else if (!isFirstItem && mbjIndex > colIndex) {
                return true
              }
            }
          }
        }
      }

      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      // Obtener los límites del item COL usando su OBB
      const colOBB = itemOBB.boundingBox
      const colRight = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
      const colLeft = new Vector3(-1, 0, 0).applyQuaternion(itemQuat)

      // Calcular los límites izquierdo y derecho del COL
      const colWidth = item.width || 0
      const colLeftEdge = itemPos.clone().add(colLeft.multiplyScalar(colWidth / 2))
      const colRightEdge = itemPos.clone().add(colRight.multiplyScalar(colWidth / 2))

      // Contar todas las unidades MBJ visibles
      const allMBJItems: Array<{ id: string; catalogID: string; tipoNormalizado: string }> = []
      for (const [id, otherItem] of items) {
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue
        if (otherItem.tipoNormalizado === 'MBJ') {
          allMBJItems.push({
            id,
            catalogID: otherItem.catalogID?.toString() || 'unknown',
            tipoNormalizado: otherItem.tipoNormalizado || 'unknown',
          })
        }
      }

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue
        if (otherItem.tipoNormalizado !== 'MBJ') continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        // Verificar si están en la misma pared
        if (
          item.snappedWall &&
          otherItem.snappedWall &&
          item.snappedWall !== otherItem.snappedWall
        ) {
          continue
        }

        const otherItemOBB = sceneBoundingBoxes.get(id)
        if (!otherItemOBB) {
          continue
        }

        const mbjOBB = otherItemOBB.boundingBox

        // PRIMERO verificar si están en el mismo grupo contiguo
        // Si no están en el mismo grupo, descartar inmediatamente (no son realmente contiguos)
        const { groups } = get().getContiguousItemsForLaterals()
        let isInSameGroup = false
        for (const group of groups) {
          const colInGroup = group.find((g) => g.id === item.id)
          const mbjInGroup = group.find((g) => g.id === id)
          if (colInGroup && mbjInGroup) {
            isInSameGroup = true
            break
          }
        }

        if (!isInSameGroup) {
          continue
        }

        // Verificar si las OBB se intersectan o están muy cerca
        if (colOBB.intersectsOBB(mbjOBB)) {
          return true
        }

        // Calcular la distancia entre los límites de las OBB
        // Obtener los límites del MBJ
        const mbjRight = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
        const mbjLeft = new Vector3(-1, 0, 0).applyQuaternion(otherQuat)
        const mbjWidth = otherItem.width || 0
        const mbjLeftEdge = otherPos.clone().add(mbjLeft.multiplyScalar(mbjWidth / 2))
        const mbjRightEdge = otherPos.clone().add(mbjRight.multiplyScalar(mbjWidth / 2))

        // Verificar si el MBJ está en el lado correcto de la COL
        // SOLO si están en el mismo grupo contiguo (ya verificado arriba)
        let distanceToEdge: number
        if (isFirstItem) {
          distanceToEdge = colLeftEdge.distanceTo(mbjRightEdge)
        } else {
          // Lado derecho: verificar distancia entre el borde derecho del COL y el borde izquierdo del MBJ
          distanceToEdge = colRightEdge.distanceTo(mbjLeftEdge)
        }

        // Solo considerar MBJs muy cercanos (<50mm) - isInSameGroup ya verificado arriba
        if (distanceToEdge < 50) {
          const depthDiff = Math.abs(itemPos.z - otherPos.z)
          const maxDepthDiff = distanceToEdge < 20 ? 400 : 100 // Más permisivo si están muy cerca

          if (depthDiff < maxDepthDiff) {
            const heightDiff = Math.abs(itemPos.y - otherPos.y)
            console.log(
              `  📊 MBJ ${id} (${otherItem.catalogID}): diferencia altura=${heightDiff.toFixed(2)}`,
            )

            if (heightDiff < 2000) {
              if (distanceToEdge < 20) {
                return true
              }

              const isAligned = areItemsAlignedOnWall(
                item,
                otherItem,
                isFirstItem ? 'left' : 'right',
                getRegletaRealValue,
              )

              if (isAligned || distanceToEdge < 30) {
                return true
              }
            }
          }
        }
      }

      return false
    }

    function buildLateralConfig(item: LinkedItem, isFirstItem: boolean) {
      const type = item.tipoNormalizado
      if (!type) {
        return null
      }

      if (type === 'MAL') {
        // Laterales válidos disponibles: 30, 35, 37, 40, 42, 45, 47, 50, 52, 80, 82, 90, 92
        const validHeights = [30, 35, 37, 40, 42, 45, 47, 50, 52, 80, 82, 90, 92]

        const itemHeight = Math.round(item.height || 0)
        const heightInCm = Math.round(itemHeight / 10)

        // Encontrar el lateral válido más cercano
        let closestHeight = validHeights[0]
        let minDiff = Math.abs(heightInCm - closestHeight)

        for (const validHeight of validHeights) {
          const diff = Math.abs(heightInCm - validHeight)
          if (diff < minDiff) {
            minDiff = diff
            closestHeight = validHeight
          }
        }

        // Si la diferencia es muy grande (más de 5cm), no hay lateral válido
        if (minDiff > 5) {
          console.warn(
            `No se encontró lateral válido para altura ${itemHeight}mm (${heightInCm}cm). Más cercano: ${closestHeight}cm (diferencia: ${minDiff}cm)`,
          )
          return null
        }

        const catalogID = `LA${closestHeight}35`

        return {
          catalogID,
          heightOffset: 20,
        }
      }

      let idPrefix = ''
      if (type === 'MBJ') {
        idPrefix = 'CB'
      } else if (type === 'COL') {
        // Para COL, verificar si hay MBJ adyacentes en este lado
        const hasMBJ = hasMBJAdjacent(item, isFirstItem)

        if (hasMBJ) {
          // Si hay MBJ adyacente, usar LC13860 o LC14860 según la altura de la columna
          // LC13860 es para columnas de 2200mm de altura
          // LC14860 es para columnas de 2300mm de altura
          const itemHeight = Math.round(item.height || 0)
          const heightInCm = Math.round(itemHeight / 10)

          // Determinar qué lateral usar según la altura
          // Si la altura es 2200mm (220cm) o cerca, usar LC13860
          // Si la altura es 2300mm (230cm) o cerca, usar LC14860
          let lateralCatalogID: string
          if (Math.abs(heightInCm - 220) <= 5) {
            // Altura cerca de 2200mm
            lateralCatalogID = 'LC13860'
          } else if (Math.abs(heightInCm - 230) <= 5) {
            // Altura cerca de 2300mm
            lateralCatalogID = 'LC14860'
          } else {
            // Por defecto, usar LC13860 si altura <= 2250mm, LC14860 si altura > 2250mm
            lateralCatalogID = heightInCm <= 225 ? 'LC13860' : 'LC14860'
          }

          return {
            catalogID: lateralCatalogID,
            heightOffset: 0,
          }
        }
        // Si no hay MBJ adyacente, usar el lateral normal CC
        idPrefix = 'CC'
      } else {
        return null
      }

      let height = Math.round(item.height || 0)
      let depth = Math.round(item.thickness || 0)
      if (depth < 350) {
        depth = 350
      }
      if (idPrefix === 'CB') {
        depth = 600
        const heightInCm = Math.round(height / 10)
        // Redondear hacia arriba a la decena más cercana (ceil a múltiplos de 10)
        const roundedHeightInCm = Math.ceil(heightInCm / 10) * 10
        // Convertir de vuelta a mm y asegurar mínimo de 90cm (900mm)
        height = Math.max(roundedHeightInCm * 10, 900)
      }
      const catalogID = `${idPrefix}${height / 10}${depth / 10}`

      return {
        catalogID,
        heightOffset: 0,
      }
    }
  },
  createItemGroup: (selectedItems: Set<AnyID>) => {
    const itemIds = Array.from(selectedItems)

    if (itemIds.length < 2) return

    // IMPORTANTE: Actualizar todas las OBBs antes de calcular posición del grupo
    // Esto asegura que las dimensiones sean correctas tanto en 2D como en 3D
    get().setOBBScene()

    // Obtener referencias actualizadas después de setOBBScene
    const { items, currentLayer, sceneBoundingBoxes } = get()

    // Find min boundary (bottom-left-back)
    const minPoint = new Vector3(Infinity, Infinity, Infinity)
    const maxPoint = new Vector3(-Infinity, -Infinity, -Infinity)
    const itemsData: { id: AnyID; worldMatrix: Matrix4 }[] = []

    let hasBounds = false

    itemIds.forEach((id) => {
      const item = items.get(id)
      if (item) {
        let worldMatrix: Matrix4
        if (item.matrix) {
          worldMatrix = item.matrix.clone()
        } else {
          // Fallback if no matrix
          worldMatrix = new Matrix4().setPosition(item.x ?? 0, item.y ?? 0, item.z ?? 0)
        }

        const obbData = sceneBoundingBoxes.get(id)
        if (obbData && obbData.boundingBox) {
          const obb = obbData.boundingBox
          // Calculate AABB points from OBB
          const center = new Vector3().copy(obb.center)

          // We must calculate all corner vertices
          // OBB is defined by center, rotation3x3, halfSize
          const result = new Vector3()
          const corners = [
            new Vector3(-1, -1, -1),
            new Vector3(1, -1, -1),
            new Vector3(1, 1, -1),
            new Vector3(-1, 1, -1),
            new Vector3(-1, -1, 1),
            new Vector3(1, -1, 1),
            new Vector3(1, 1, 1),
            new Vector3(-1, 1, 1),
          ]

          corners.forEach((p) => {
            p.x *= obb.halfSize.x
            p.y *= obb.halfSize.y
            p.z *= obb.halfSize.z
            p.applyMatrix3(obb.rotation).add(obb.center)
            minPoint.min(p)
            maxPoint.max(p)
          })
          hasBounds = true
        } else {
          // Fallback to position if no OBB
          const pos = new Vector3()
          pos.setFromMatrixPosition(worldMatrix)
          minPoint.min(pos)
          maxPoint.max(pos)
        }

        itemsData.push({ id, worldMatrix })
      }
    })

    if (!hasBounds && minPoint.x === Infinity) return

    // If no bounds were found but we have items, use the min position found
    if (minPoint.x === Infinity) {
      return
    }

    // Create Group Matrix: gizmo en esquina trasera inferior izquierda
    const groupPosition = new Vector3(maxPoint.x, minPoint.y, minPoint.z)

    // El gizmo del grupo siempre debe tener una rotación de 90 grados en el eje Y
    const groupRotation = new Quaternion().setFromEuler(new Euler(0, -Math.PI / 2, 0))

    const groupMatrix = new Matrix4().compose(groupPosition, groupRotation, new Vector3(1, 1, 1))
    const groupInverse = groupMatrix.clone().invert()

    // Create group ID
    const groupID = getId('itemGroup')

    // Perform state mutations inside set()
    set((state) => {
      // 1. Create new group object
      const newGroup = {
        id: groupID,
        type: 'itemGroup',
        children: new Set(itemIds),
        matrix: groupMatrix,
        x: groupPosition.x,
        y: groupPosition.y,
        z: groupPosition.z,

        visible: true,
        selected: true,
        isBlocked: false,
        canCollide: true,
        name: 'Nuevo Grupo',
        rotation: 0,
      } as any

      // Add group to items map
      state.items.set(groupID, newGroup)

      // 2. Add group to current layer items
      const layer = state.layers[state.currentLayer]
      if (layer) {
        layer.items.add(groupID)

        // 4. Update selection (select only the group)
        layer.selected.clear()
        layer.selected.add(groupID)
      }

      state.currentObjectID = groupID

      // 3. Guardar matrices LOCALES en los hijos (relativas al grupo) y bloquearlos.
      // El componente ItemGroup2D/3D usa localMatrices.current (de su propio ref) para render.
      itemsData.forEach(({ id, worldMatrix }) => {
        const item = state.items.get(id)
        if (item) {
          const localMatrix = groupInverse.clone().multiply(worldMatrix)

          const pos = new Vector3()
          const rot = new Quaternion()
          const scale = new Vector3()
          localMatrix.decompose(pos, rot, scale)

          // Si el item estaba pegado a una pared, removerlo de highItems/lowItems
          if (item.snappedWall) {
            const wall = state.items.get(item.snappedWall)
            if (wall && (isWall(wall) || isPartition(wall) || isHiddenWall(wall))) {
              if (item.tipoNormalizado === 'MAL') {
                wall.highItems?.delete(id as AnyID)
              }
              if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
                wall.lowItems?.delete(id as AnyID)
              }
            }
            // Limpiar snappedWall del item hijo
            ;(item as any).snappedWall = ''
          }

          // Mutate the draft item (matriz LOCAL respecto al grupo)
          ;(item as any).matrix = localMatrix
          ;(item as any).x = pos.x
          ;(item as any).y = pos.y
          ;(item as any).z = pos.z
          ;(item as any).isBlocked = true
          // Quitar hijo de colisionables (ya no actúa individualmente)
          state.collisionableItems.delete(id as AnyID)
        }
      })

      // Añair el grupo como unidad colisionable
      state.collisionableItems.add(groupID)
    })

    // Calular la OBB del grupo recién creado
    get().setItemGroupOBB(groupID)
  },
  getFrontalHeight: () => {
    const items = get().items
    const prefs = get().prefs
    for (const [, item] of items) {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal') {
        return item.height
      }
    }

    return prefs.ALTURAFRONTALES
  },
  disbandItemGroup: (groupId: AnyID) => {
    set((store) => {
      const group = store.items.get(groupId) as ItemGroup | undefined
      if (!group || group.type !== 'itemGroup') return

      const childrenIds = Array.from(group.children) as AnyID[]
      const groupMatrix = group.matrix.clone()

      // Eliminar el grupo del store y de la capa
      store.items.delete(groupId)
      store.layers[store.currentLayer].items.delete(groupId)
      store.layers[store.currentLayer].selected.delete(groupId)
      store.sceneBoundingBoxes.delete(groupId)
      store.collisionableItems.delete(groupId)

      // Devolver los hijos a la capa actual como items independientes y desbloquearlos
      childrenIds.forEach((childId) => {
        store.layers[store.currentLayer].items.add(childId)
        const child = store.items.get(childId)
        if (child) {
          // Aplicar la transformación del grupo a la transformación del hijo
          // La nueva matriz del hijo es: groupMatrix * childMatrix (transformación mundo)
          const childMatrix = child.matrix.clone()
          child.matrix = groupMatrix.clone().multiply(childMatrix)
          ;(child as any).isBlocked = false
          // Restaurar colisión individual si el item la tenía habilitada
          if ((child as any).canCollide !== false) {
            store.collisionableItems.add(childId)
          }
        }
      })

      // Seleccionar los hijos tras desagrupar
      store.layers[store.currentLayer].selected = new Set(childrenIds)
      if (childrenIds.length > 0) {
        store.currentObjectID = childrenIds[0]
      }
    })
  },

  /**
   * Renumera todos los items de forma jerárquica
   * Los items padres obtienen números 1, 2, 3...
   * Los hijos obtienen números como 1.1, 1.2, 2.1, 2.2...
   * Los nietos obtienen números como 1.1.1, 1.1.2...
   * Esta función procesa la store y asigna los números increment
   */
  renumberItemsHierarchically: () => {
    set((store) => {
      const items = store.items
      const areas = store.areas

      // Identificar items padres (aquellos sin firstParentID o con depth === 0)
      const parentItems: AnyID[] = []
      const allItemsArray: any[] = []

      // Recopilar todos los items y áreas
      items.forEach((item, id) => {
        allItemsArray.push({ id, item, isArea: false })

        // Es padre si no tiene firstParentID (items universales o linkedItems raíz)
        const isParent = !('firstParentID' in item) || item.depth === 0
        if (isParent) {
          parentItems.push(id)
        }
      })

      areas.forEach((area, id) => {
        allItemsArray.push({ id, item: area, isArea: true })
        parentItems.push(id) // Las áreas siempre son padres
      })

      // Ordenar padres por tipo: primero áreas, luego paredes, después el resto
      parentItems.sort((a, b) => {
        const itemA = items.get(a) || areas.get(a)
        const itemB = items.get(b) || areas.get(b)

        if (!itemA || !itemB) return 0

        const typeA = itemA.type
        const typeB = itemB.type

        // Definir prioridades: areas=1, walls=2, resto=3
        const getPriority = (type: string) => {
          if (type === 'areas') return 1
          if (type === 'walls') return 2
          return 3
        }

        const priorityA = getPriority(typeA)
        const priorityB = getPriority(typeB)

        // Ordenar por prioridad primero
        if (priorityA !== priorityB) {
          return priorityA - priorityB
        }

        // Si tienen la misma prioridad, ordenar por ID (orden de creación)
        const aStr = String(a)
        const bStr = String(b)
        return aStr.localeCompare(bStr)
      })

      // Función recursiva para numerar un item y sus hijos
      const numberItemAndChildren = (itemId: AnyID, parentNumber: string): void => {
        const item = items.get(itemId)
        if (!item) return

        // Asignar el número a este item
        item.increment = parentNumber

        // Si tiene hijos, numerarlos
        if ('childs' in item && item.childs && item.childs.size > 0) {
          const childrenArray = Array.from(item.childs)

          // Ordenar los hijos según childrenOrder si existe
          if ('childrenOrder' in item && item.childrenOrder && item.childrenOrder.length > 0) {
            const orderMap = new Map<string, number>()
            item.childrenOrder.forEach((orderStr, index) => {
              const childId = orderStr.split('!')[1]
              orderMap.set(childId, index)
            })

            childrenArray.sort((a, b) => {
              const orderA = orderMap.get(a) ?? Infinity
              const orderB = orderMap.get(b) ?? Infinity
              return orderA - orderB
            })
          }

          // Numerar cada hijo
          childrenArray.forEach((childId, index) => {
            const childNumber = `${parentNumber}.${index + 1}`
            numberItemAndChildren(childId as AnyID, childNumber)
          })
        }
      }

      // Numerar los padres y sus descendientes
      parentItems.forEach((parentId, index) => {
        const parentNumber = String(index + 1)
        const item = items.get(parentId) || areas.get(parentId)

        if (item) {
          item.increment = parentNumber

          // Si es un item con hijos, numerarlos recursivamente
          if ('childs' in item && item.childs && item.childs.size > 0) {
            const childrenArray = Array.from(item.childs)

            // Ordenar los hijos según childrenOrder si existe
            if ('childrenOrder' in item && item.childrenOrder && item.childrenOrder.length > 0) {
              const orderMap = new Map<string, number>()
              item.childrenOrder.forEach((orderStr, index) => {
                const childId = orderStr.split('!')[1]
                orderMap.set(childId, index)
              })

              childrenArray.sort((a, b) => {
                const orderA = orderMap.get(a) ?? Infinity
                const orderB = orderMap.get(b) ?? Infinity
                return orderA - orderB
              })
            }

            // Numerar cada hijo
            childrenArray.forEach((childId, index) => {
              const childNumber = `${parentNumber}.${index + 1}`
              numberItemAndChildren(childId as AnyID, childNumber)
            })
          }
        }
      })
    })
  },
})
